/* ============================================================
   Educational peptide profiles.

   This content is original, written from established, publicly documented
   pharmacology as general educational reference. It is NOT medical advice and
   NOT a protocol to follow. Many of these compounds are research chemicals that
   are not approved for human use in most jurisdictions. Dose ranges shown are
   figures commonly cited in the research literature for context only.

   `calcPreset` (when present) pre-fills the reconstitution calculator with a
   common vial size so readers can explore the math — not a recommendation.
   ============================================================ */

export const DISCLAIMER =
  'Educational reference only — not medical advice. These compounds are largely research chemicals not approved for human use in most countries. Nothing here is a recommendation to obtain or administer any substance. Consult a qualified clinician.'

const RAW = [
  {
    slug: 'bpc-157',
    name: 'BPC-157',
    aka: ['Body Protection Compound-157', 'Bepecin', 'PL 14736'],
    category: 'Recovery & tissue repair',
    tags: ['healing', 'gut', 'tendon', 'systemic'],
    summary:
      'A synthetic 15-amino-acid peptide based on a fragment of a protective protein found in gastric juice, studied mainly for soft-tissue repair and gut protection.',
    sequence: 'Gly-Glu-Pro-Pro-Pro-Gly-Lys-Pro-Ala-Asp-Asp-Ala-Gly-Leu-Val',
    overview:
      'BPC-157 is a "stable gastric pentadecapeptide" — a lab-made chain of fifteen amino acids derived from a naturally occurring body-protection compound isolated from the stomach lining. In animal research it has drawn attention for an unusually broad cytoprotective profile, appearing to help repair tendon, ligament, muscle and intestinal tissue while remaining stable in gastric acid. Human clinical data remain limited, so most of what is written about it comes from rodent studies.',
    mechanism: [
      'Promotes new blood-vessel growth (angiogenesis), partly by up-regulating the VEGFR2 receptor pathway.',
      'Interacts with the nitric-oxide (NO) system, which influences blood flow and tissue protection.',
      'Appears to increase growth-hormone receptor expression in tendon fibroblasts, supporting collagen repair.',
    ],
    researchedUses: [
      'Tendon, ligament and muscle recovery in animal models',
      'Protection and healing of the gut lining (studied in colitis/ulcer models)',
      'General wound healing and cytoprotection',
    ],
    protocol: {
      route: 'Subcutaneous injection (research); sometimes oral for gut-local effects',
      dose: '~200–500 mcg per day',
      frequency: 'Once or split twice daily',
      notes: 'Often administered near the target area in injury-focused research.',
    },
    halfLife: 'Short — estimated on the order of hours; not firmly established in humans.',
    reconstitution:
      'Commonly supplied as a 5 mg lyophilized vial. Reconstituting 5 mg with 2 mL of bacteriostatic water gives 2.5 mg/mL, so a 250 mcg dose ≈ 10 units on a U-100 syringe.',
    cautions: [
      'Not approved by the FDA; banned by WADA for competitive athletes.',
      'Human safety and long-term data are minimal.',
      'Quality and purity of research-grade material vary widely by source.',
    ],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'ghk-cu',
    name: 'GHK-Cu',
    aka: ['Copper Tripeptide-1', 'Gly-His-Lys copper complex'],
    category: 'Skin, hair & regeneration',
    tags: ['skin', 'collagen', 'anti-aging', 'topical'],
    summary:
      'A naturally occurring copper-binding tripeptide widely used in cosmetics and studied for skin remodeling, wound healing and hair support.',
    sequence: 'Gly-His-Lys (bound to copper(II))',
    overview:
      'GHK is a tiny three-amino-acid peptide that circulates naturally in human plasma and drops sharply with age. Bound to a copper ion it becomes GHK-Cu, a complex with a long history in skincare. Research describes it as a signal that tells skin to "remodel" — clearing damaged matrix and rebuilding fresh collagen and elastin. It is most established as a topical cosmetic ingredient, with injectable use confined to research settings.',
    mechanism: [
      'Stimulates production of collagen, elastin and glycosaminoglycans that give skin structure.',
      'Acts as an antioxidant and modulates genes involved in tissue remodeling and repair.',
      'Delivers copper, a cofactor for enzymes central to wound healing.',
    ],
    researchedUses: [
      'Skin firmness, elasticity and fine-line appearance (topical)',
      'Wound healing and scar remodeling',
      'Hair-follicle support in some studies',
    ],
    protocol: {
      route: 'Topical (cosmetic) most commonly; subcutaneous in research',
      dose: 'Topical: low cosmetic concentrations. Research injectable figures cited around 1–2 mg/day.',
      frequency: 'Varies by format',
      notes: 'Topical serums are the mainstream, best-supported use.',
    },
    halfLife: 'Short in circulation; topical effects are local and gradual.',
    reconstitution:
      'Injectable research vials are often 50 mg. Because copper can accumulate, injectable use is approached conservatively in the literature; topical formats avoid the systemic-copper question entirely.',
    cautions: [
      'Excess copper exposure is a concern with high or frequent injectable dosing.',
      'Most human evidence is for topical, cosmetic use.',
      'Can cause local irritation in sensitive skin.',
    ],
    calcPreset: { vialMg: 50, waterMl: 5, dose: 2, doseUnit: 'mg' },
  },

  {
    slug: 'tesamorelin',
    name: 'Tesamorelin',
    aka: ['TH9507'],
    category: 'Growth-hormone axis',
    tags: ['GHRH', 'visceral fat', 'FDA-approved'],
    summary:
      'A stabilized growth-hormone-releasing hormone (GHRH) analog and one of the few peptides here with FDA approval — indicated for reducing excess visceral fat in HIV-associated lipodystrophy.',
    sequence: 'Stabilized analog of human GRF(1-44)',
    overview:
      'Tesamorelin is a synthetic version of the natural hormone that tells the pituitary to release growth hormone. A small chemical modification makes it more resistant to breakdown than native GHRH. Unlike most compounds in this list, it has been through large clinical trials and carries an approved indication for trimming visceral abdominal fat in people with HIV-related fat redistribution. It raises the body\'s own GH and IGF-1 rather than supplying GH directly.',
    mechanism: [
      'Binds GHRH receptors on the pituitary, prompting release of the body\'s own growth hormone.',
      'The resulting GH pulse raises IGF-1, the downstream mediator of many GH effects.',
      'Preferentially reduces visceral (deep abdominal) adipose tissue in trials.',
    ],
    researchedUses: [
      'Reduction of visceral fat in HIV-associated lipodystrophy (approved use)',
      'Investigational interest in metabolic and cognitive endpoints',
    ],
    protocol: {
      route: 'Subcutaneous injection',
      dose: '2 mg once daily (clinical dosing)',
      frequency: 'Daily, typically evening',
      notes: 'Effects on visceral fat build over weeks to months in studies.',
    },
    halfLife: 'Short — roughly 25–40 minutes, consistent with a pulsatile GH release.',
    reconstitution:
      'Research vials are often 10 mg. 10 mg with 2 mL of bacteriostatic water gives 5 mg/mL, so a 2 mg dose = 0.4 mL (40 units on a U-100 syringe).',
    cautions: [
      'Contraindicated in pregnancy and in active malignancy.',
      'Can raise blood glucose / affect insulin sensitivity — monitored in clinical use.',
      'Prescription drug where approved; other uses are off-label or research.',
    ],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2, doseUnit: 'mg' },
  },

  {
    slug: 'cjc-1295-no-dac',
    name: 'CJC-1295 without DAC',
    aka: ['Mod GRF 1-29', 'Modified GRF(1-29)', 'CJC-1295 DAC-free'],
    category: 'Growth-hormone axis',
    tags: ['GHRH', 'short-acting', 'GH pulse'],
    summary:
      'A short-acting GHRH fragment that stimulates a natural growth-hormone pulse — the "DAC-free" version clears quickly and is frequently paired with a GHRP.',
    sequence: 'Modified GRF(1-29) — a 29-amino-acid GHRH fragment with stabilizing substitutions',
    overview:
      'CJC-1295 comes in two forms. With DAC (Drug Affinity Complex) it binds albumin and lingers for days; without DAC — the version described here, also called Mod GRF 1-29 — it acts fast and washes out within about half an hour. That short window is the point: it mimics the body\'s own brief GHRH bursts, so researchers often combine it with a growth-hormone-releasing peptide (such as ipamorelin) that works through a complementary pathway for a larger, more natural pulse.',
    mechanism: [
      'Activates pituitary GHRH receptors to trigger a short, natural-shaped GH pulse.',
      'Stabilizing amino-acid swaps resist the enzyme (DPP-IV) that degrades native GHRH.',
      'Synergizes with GHRP/ghrelin-mimetic peptides that release GH by a separate route.',
    ],
    researchedUses: [
      'Raising endogenous GH/IGF-1 in research contexts',
      'Commonly studied stacked with ipamorelin or GHRP-2/6',
    ],
    protocol: {
      route: 'Subcutaneous injection',
      dose: '~100 mcg per dose',
      frequency: 'One to three times daily, often before bed and/or post-workout',
      notes: 'Timed away from food/fat, which can blunt the GH response in research.',
    },
    halfLife: 'Short — about 30 minutes (the key difference from the DAC version).',
    reconstitution:
      'Often a 5 mg vial. Reconstituting 5 mg with 2.5 mL of bacteriostatic water gives 2 mg/mL, so 100 mcg = 5 units on a U-100 syringe.',
    cautions: [
      'Not approved for human use; research chemical status.',
      'Water retention, tingling or head-rush are commonly reported in anecdotal use.',
      'Do not confuse with the long-acting "with DAC" version — dosing logic differs.',
    ],
    calcPreset: { vialMg: 5, waterMl: 2.5, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'semax',
    name: 'Semax',
    aka: ['ACTH(4-10) analog', 'Russian nootropic peptide'],
    category: 'Cognitive & neuroprotective',
    tags: ['nootropic', 'BDNF', 'intranasal'],
    summary:
      'A short heptapeptide developed in Russia from a fragment of ACTH, used there clinically for cognition and stroke recovery and popular as a nootropic.',
    sequence: 'Met-Glu-His-Phe-Pro-Gly-Pro',
    overview:
      'Semax is a synthetic peptide built from a seven-amino-acid piece of the hormone ACTH, modified so it no longer affects stress hormones but keeps effects on the brain. It has been used in Russia for decades — appearing on their essential-medicines list — for cognitive complaints, circulatory brain conditions and stroke rehabilitation. Outside Russia it is unapproved and studied mostly as a nootropic. It is typically delivered as nasal drops rather than injected.',
    mechanism: [
      'Raises BDNF (brain-derived neurotrophic factor), supporting neuron growth and plasticity.',
      'Modulates dopamine and serotonin signaling linked to focus and mood.',
      'Shows neuroprotective and antioxidant effects in ischemia models.',
    ],
    researchedUses: [
      'Attention, memory and mental clarity',
      'Stroke and cerebrovascular recovery (clinical use in Russia)',
      'Neuroprotection under stress or hypoxia',
    ],
    protocol: {
      route: 'Intranasal (drops/spray) most commonly',
      dose: '~200–600 mcg per day (0.1% solutions)',
      frequency: 'Split through the day',
      notes: 'Higher-strength (1%) formulations exist for clinical indications.',
    },
    halfLife: 'Very short in blood, but central effects outlast plasma levels.',
    reconstitution:
      'Usually dissolved as a nasal solution rather than drawn into a syringe, so insulin-unit math generally does not apply.',
    cautions: [
      'Not FDA approved; Western clinical evidence is limited.',
      'Intranasal irritation is possible.',
      'Nootropic marketing often outpaces the strength of the evidence.',
    ],
    calcPreset: null,
  },

  {
    slug: 'selank',
    name: 'Selank',
    aka: ['Tuftsin analog', 'TP-7'],
    category: 'Cognitive & neuroprotective',
    tags: ['anxiolytic', 'GABA', 'intranasal'],
    summary:
      'A Russian-developed heptapeptide based on the immune peptide tuftsin, studied as a non-sedating anti-anxiety agent with cognitive and immune effects.',
    sequence: 'Thr-Lys-Pro-Arg-Pro-Gly-Pro',
    overview:
      'Selank is a stabilized synthetic analog of tuftsin, a naturally occurring immune-modulating peptide. Developed in Russia as an anxiolytic, it is designed to calm anxiety without the sedation, dependence or withdrawal associated with benzodiazepines. Research describes a mix of anti-anxiety, mild cognitive and immune-modulating effects. Like its cousin Semax, it is usually taken intranasally and is unapproved outside its region of origin.',
    mechanism: [
      'Modulates GABA and serotonin systems tied to anxiety regulation.',
      'Influences BDNF and the balance of pro-/anti-inflammatory cytokines (tuftsin-like immune activity).',
      'May slow breakdown of enkephalins, extending calming signals.',
    ],
    researchedUses: [
      'Generalized anxiety and stress without sedation',
      'Cognitive support and stress resilience',
      'Immune modulation in some studies',
    ],
    protocol: {
      route: 'Intranasal (drops/spray) most commonly',
      dose: '~250–500 mcg per day (0.15% solutions)',
      frequency: 'Split through the day',
      notes: 'Sometimes studied alongside Semax for combined mood/focus effects.',
    },
    halfLife: 'Short, but stabilized to last longer than native tuftsin.',
    reconstitution:
      'Typically prepared as a nasal solution, so syringe-unit dosing generally does not apply.',
    cautions: [
      'Not FDA approved; limited large-scale human trials in the West.',
      'Possible nasal irritation.',
      'Interactions with other serotonergic/anxiolytic agents are under-studied.',
    ],
    calcPreset: null,
  },

  {
    slug: 'dsip',
    name: 'DSIP',
    aka: ['Delta Sleep-Inducing Peptide'],
    category: 'Sleep & stress',
    tags: ['sleep', 'stress', 'neuromodulator'],
    summary:
      'A naturally occurring nine-amino-acid neuropeptide named for its association with deep (delta-wave) sleep, studied for sleep, stress and pain modulation.',
    sequence: 'Trp-Ala-Gly-Gly-Asp-Ala-Ser-Gly-Glu',
    overview:
      'DSIP was first identified for its apparent ability to promote delta-wave (deep) sleep. It is a small, naturally occurring peptide found in the brain, and beyond sleep it has been linked in research to stress resilience, pain modulation and effects on several hormones. Its exact mechanism is still not well understood, and study results have been inconsistent — which keeps it firmly in the experimental category.',
    mechanism: [
      'Appears to modulate sleep architecture, favoring slow-wave (delta) sleep.',
      'Interacts with stress systems and may buffer the response to stressors.',
      'Reported influences on hormones (e.g. LH, GH) and pain thresholds, though findings vary.',
    ],
    researchedUses: [
      'Sleep quality and normalization of disturbed sleep',
      'Stress and anxiety modulation',
      'Pain and withdrawal-related research',
    ],
    protocol: {
      route: 'Subcutaneous injection (research)',
      dose: '~100–300 mcg per dose',
      frequency: 'Typically before sleep',
      notes: 'Response is notably variable between individuals in the literature.',
    },
    halfLife: 'Very short — on the order of minutes in circulation.',
    reconstitution:
      'Often a 5 mg vial. Reconstituting 5 mg with 2 mL of bacteriostatic water gives 2.5 mg/mL, so 250 mcg = 10 units on a U-100 syringe.',
    cautions: [
      'Not FDA approved; evidence is mixed and mostly older/small studies.',
      'Effects and reliability differ widely between users in reports.',
      'Purity of research-grade peptide varies by source.',
    ],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'vitamin-b12',
    name: 'Vitamin B12',
    type: 'amino',
    aka: ['Cobalamin', 'Methylcobalamin', 'Cyanocobalamin', 'Hydroxocobalamin'],
    category: 'Energy & metabolism',
    tags: ['vitamin', 'energy', 'nerve', 'injectable'],
    summary:
      'An essential water-soluble vitamin often given by injection to correct deficiency and support energy, nerve function, and healthy red blood cells.',
    sequence: 'Cobalamin — a cobalt-containing vitamin (forms: methyl-, cyano-, hydroxo-, adenosyl-)',
    overview:
      'Vitamin B12 (cobalamin) is an essential nutrient the body cannot make on its own. It is central to red-blood-cell formation, nerve-cell health, and DNA synthesis. Because absorbing B12 from food depends on healthy gut and stomach function, injections are commonly used to bypass absorption problems and rapidly restore levels — which is why B12 shots are a fixture of wellness and energy clinics. Methylcobalamin and hydroxocobalamin are the forms most often used in injections.',
    mechanism: [
      'Acts as a cofactor for methionine synthase, supporting methylation and homocysteine metabolism.',
      'Required by methylmalonyl-CoA mutase for energy metabolism from fats and proteins.',
      'Supports myelin (the protective sheath around nerves) and red-blood-cell production.',
    ],
    researchedUses: [
      'Correcting B12 deficiency and related anemia',
      'Fatigue and low energy where deficiency is present',
      'Nerve health; commonly included in wellness injection blends',
    ],
    protocol: {
      route: 'Intramuscular or subcutaneous injection',
      dose: '~1000 mcg',
      frequency: 'Weekly to monthly for maintenance (deficiency protocols vary)',
      notes: 'Water-soluble — excess is largely excreted rather than stored to excess.',
    },
    halfLife: 'Variable; the body stores B12 in the liver, so levels decline slowly over months.',
    reconstitution:
      'B12 is usually supplied as a ready-to-inject solution (commonly 1000 mcg/mL), so lyophilized-vial reconstitution is typically not needed.',
    cautions: [
      'Generally very safe and water-soluble, but confirm dosing and deficiency with a clinician and bloodwork.',
      'Rare allergic reactions to injectable forms (often the preservative).',
      'Not a stimulant — “energy” benefits are mainly seen when correcting a true deficiency.',
    ],
    calcPreset: null,
  },

  {
    slug: 'mots-c',
    name: 'MOTS-c',
    aka: ['Mitochondrial ORF of the 12S rRNA type-c'],
    category: 'Metabolic & mitochondrial',
    tags: ['metabolic', 'mitochondrial', 'energy', 'insulin sensitivity'],
    summary: 'A mitochondrial-derived peptide studied for metabolism, insulin sensitivity, and exercise capacity.',
    sequence: '16-amino-acid peptide encoded within mitochondrial 12S rRNA',
    overview:
      'MOTS-c is one of a small family of “mitochondrial-derived peptides” encoded within mitochondrial DNA. Research interest centers on its role as a metabolic regulator — it appears to influence how cells use glucose and respond to exercise, which is why it is studied in the context of metabolic health and aging.',
    mechanism: [
      'Activates the AMPK pathway, a master switch for cellular energy balance.',
      'Improves insulin sensitivity and glucose handling in animal studies.',
      'Modulates the metabolic response to exercise and stress.',
    ],
    researchedUses: ['Metabolic health and insulin sensitivity', 'Exercise capacity and endurance', 'Healthy-aging research'],
    protocol: { route: 'Subcutaneous injection', dose: '~5–10 mg per week (often split)', frequency: 'A few times weekly', notes: 'Often cycled in research settings.' },
    halfLife: 'Short in circulation; effects are downstream and metabolic.',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so a 2.5 mg starting dose = 0.5 mL (50 units on a U-100 syringe).',
    cautions: ['Not approved for human use; research chemical.', 'Human data are early and limited.', 'May affect blood glucose.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2.5, doseUnit: 'mg' },
  },

  {
    slug: 'nad-plus',
    name: 'NAD+',
    type: 'amino',
    aka: ['Nicotinamide adenine dinucleotide'],
    category: 'Longevity & cellular energy',
    tags: ['longevity', 'energy', 'anti-aging', 'injectable'],
    summary: 'A coenzyme central to cellular energy and repair, given by injection or IV for energy and longevity research.',
    sequence: 'A dinucleotide coenzyme (not a peptide)',
    overview:
      'NAD+ (nicotinamide adenine dinucleotide) is a coenzyme found in every cell, essential for turning food into energy and for DNA-repair and sirtuin pathways tied to aging. Levels decline with age, which has driven interest in restoring it through injections and IV drips in wellness and longevity settings.',
    mechanism: [
      'Serves as an essential coenzyme for mitochondrial energy (ATP) production.',
      'Fuels sirtuins and PARPs involved in DNA repair and cellular aging.',
      'Supports redox balance across metabolism.',
    ],
    researchedUses: ['Energy and fatigue', 'Longevity and cellular-repair research', 'Recovery and cognitive support'],
    protocol: { route: 'Slow subcutaneous injection or IV infusion', dose: 'Highly variable (tens to hundreds of mg)', frequency: 'Varies', notes: 'Injected too quickly it can cause flushing/discomfort — given slowly.' },
    halfLife: 'Short; often dosed as infusions.',
    reconstitution: 'Usually supplied as a solution or large-mg vial for slow injection/IV, so simple insulin-unit dosing often does not apply.',
    cautions: ['Not an approved therapy for anti-aging.', 'Rapid administration causes flushing, nausea, chest tightness.', 'Evidence for many claimed benefits is limited.'],
    calcPreset: null,
  },

  {
    slug: 'aod-9604',
    name: 'AOD-9604',
    aka: ['Anti-Obesity Drug 9604', 'hGH fragment 176-191'],
    category: 'Fat loss & metabolism',
    tags: ['fat loss', 'metabolism', 'hgh fragment'],
    summary: 'A modified fragment of growth hormone studied for fat metabolism without the blood-sugar effects of full hGH.',
    sequence: 'Modified hGH fragment (residues 176–191) plus a stabilizing tyrosine',
    overview:
      'AOD-9604 is a small piece of the growth-hormone molecule (the 176–191 fragment) that was investigated specifically for fat loss. The idea is to capture hGH’s fat-burning signal without its effects on growth or blood sugar. Clinical trials for obesity were mixed, and it is not an approved weight-loss drug.',
    mechanism: [
      'Stimulates lipolysis (fat breakdown) and inhibits lipogenesis (fat storage).',
      'Acts on fat metabolism without meaningfully raising IGF-1 or blood glucose.',
    ],
    researchedUses: ['Fat loss research', 'Metabolic and cartilage-repair investigation'],
    protocol: { route: 'Subcutaneous injection', dose: '~300 mcg per day', frequency: 'Daily, often fasted', notes: 'Sometimes timed in the morning or before cardio.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 300 mcg = 12 units on a U-100 syringe.',
    cautions: ['Not FDA approved; obesity trials were inconclusive.', 'Research chemical status.', 'Long-term human safety unclear.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 300, doseUnit: 'mcg' },
  },

  {
    slug: 'melanotan-1',
    name: 'Melanotan I',
    aka: ['Melanotan-1', 'Afamelanotide', 'MT-1'],
    category: 'Skin & pigmentation',
    tags: ['tanning', 'skin', 'photoprotection'],
    summary: 'An α-MSH analog that stimulates melanin (tanning) and is approved in some regions for a rare light-sensitivity disorder.',
    sequence: 'Analog of α-melanocyte-stimulating hormone (α-MSH)',
    overview:
      'Melanotan I is a synthetic version of α-MSH, the hormone that triggers melanin production. It promotes skin pigmentation and is approved in some regions (as afamelanotide) for erythropoietic protoporphyria, a rare light-sensitivity condition. Compared with Melanotan II it is more selective for pigmentation and less associated with libido effects.',
    mechanism: [
      'Activates melanocortin-1 receptors (MC1R) to increase melanin (eumelanin).',
      'Provides photoprotection by darkening skin.',
    ],
    researchedUses: ['Tanning / photoprotection', 'Erythropoietic protoporphyria (approved as afamelanotide)'],
    protocol: { route: 'Subcutaneous injection', dose: '~500 mcg – 1 mg', frequency: 'Loading then maintenance', notes: 'Sun exposure still drives tanning response.' },
    halfLife: 'Short (hours); the approved form uses a slow-release implant.',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so 500 mcg = 10 units on a U-100 syringe.',
    cautions: ['Injectable research forms are unregulated and unapproved.', 'Can darken moles/freckles — skin checks advised.', 'Nausea and flushing are common.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 500, doseUnit: 'mcg' },
  },

  {
    slug: 'melanotan-2',
    name: 'Melanotan II',
    aka: ['Melanotan-2', 'MT-2'],
    category: 'Skin & pigmentation',
    tags: ['tanning', 'skin', 'libido'],
    summary: 'A potent α-MSH analog used for tanning that also affects appetite and libido.',
    sequence: 'Cyclic analog of α-MSH',
    overview:
      'Melanotan II is a more potent, less selective α-MSH analog. Beyond tanning it activates several melanocortin receptors, which is why users also report appetite suppression and increased libido. It is unapproved and widely sold as a research chemical; its non-selectivity is the source of most of its side effects.',
    mechanism: [
      'Activates multiple melanocortin receptors (MC1R for pigment, MC4R for appetite/libido).',
      'Stimulates melanin production for tanning.',
    ],
    researchedUses: ['Tanning', 'Appetite and libido research'],
    protocol: { route: 'Subcutaneous injection', dose: '~250–500 mcg', frequency: 'Loading then maintenance', notes: 'Lower starting doses reduce nausea.' },
    halfLife: 'Short (hours).',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so 250 mcg = 5 units on a U-100 syringe.',
    cautions: ['Not approved; unregulated research chemical.', 'Nausea, flushing, spontaneous erections, and darkening moles reported.', 'Monitor skin/moles for changes.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'pt-141',
    name: 'PT-141',
    aka: ['Bremelanotide'],
    category: 'Sexual health',
    tags: ['libido', 'sexual health', 'fda-approved'],
    summary: 'A melanocortin agonist FDA-approved (as bremelanotide) for low sexual desire, acting through the brain rather than blood flow.',
    sequence: 'Cyclic melanocortin-receptor agonist (a metabolite of Melanotan II)',
    overview:
      'PT-141 (bremelanotide) came out of Melanotan II research and targets sexual function specifically. Unlike PDE5 drugs that work on blood flow, it acts centrally on melanocortin receptors in the brain to increase sexual desire and arousal. It is FDA approved (as bremelanotide) for premenopausal women with hypoactive sexual desire disorder.',
    mechanism: [
      'Activates central melanocortin receptors (mainly MC4R) involved in sexual arousal.',
      'Works through the nervous system, not the vascular system.',
    ],
    researchedUses: ['Low sexual desire (approved in women)', 'Erectile and arousal research'],
    protocol: { route: 'Subcutaneous injection', dose: '~1–2 mg before activity', frequency: 'As needed', notes: 'Taken ahead of anticipated activity, not daily.' },
    halfLife: 'Short (a few hours).',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so 1 mg = 20 units on a U-100 syringe.',
    cautions: ['Can transiently raise blood pressure; avoid with uncontrolled hypertension.', 'Nausea and flushing are common.', 'Prescription drug where approved.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 1, doseUnit: 'mg' },
  },

  {
    slug: 'ipamorelin',
    name: 'Ipamorelin',
    aka: [],
    category: 'Growth-hormone axis',
    tags: ['ghrp', 'gh pulse', 'recovery'],
    summary: 'A selective growth-hormone-releasing peptide that raises GH with minimal effect on hunger or cortisol.',
    sequence: 'Pentapeptide GH secretagogue',
    overview:
      'Ipamorelin is a GHRP (growth-hormone-releasing peptide) valued for being “clean” — it prompts a natural GH pulse without the strong hunger, cortisol, or prolactin bumps seen with older GHRPs. It is frequently paired with a GHRH like CJC-1295 (no DAC) because the two work through complementary pathways for a larger, natural pulse.',
    mechanism: [
      'Activates the ghrelin/GH-secretagogue receptor to release the body’s own GH.',
      'Selective — minimal effect on cortisol, prolactin, or appetite.',
      'Synergizes with GHRH analogs.',
    ],
    researchedUses: ['Raising endogenous GH/IGF-1', 'Recovery and body-composition research', 'Commonly stacked with CJC-1295 (no DAC)'],
    protocol: { route: 'Subcutaneous injection', dose: '~200–300 mcg per dose', frequency: '1–3× daily, often before bed', notes: 'Timed away from food/fat for a cleaner GH response.' },
    halfLife: 'Short (~2 hours).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 300 mcg = 12 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Water retention or tingling reported.', 'Raises GH/IGF-1 — caution with any active cancer.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 300, doseUnit: 'mcg' },
  },

  {
    slug: 'tb-500',
    name: 'TB-500',
    aka: ['Thymosin Beta-4 fragment', 'TB4'],
    category: 'Recovery & tissue repair',
    tags: ['healing', 'recovery', 'flexibility', 'systemic'],
    summary: 'A synthetic version of a thymosin beta-4 region studied for systemic healing, flexibility, and recovery.',
    sequence: 'Synthetic fragment of thymosin beta-4',
    overview:
      'TB-500 is based on thymosin beta-4, a naturally occurring protein involved in cell migration and tissue repair. In animal research it is associated with faster healing of muscle, tendon, and other tissues, and improved flexibility. It is popular alongside BPC-157 as a “recovery stack,” though human clinical data are limited.',
    mechanism: [
      'Regulates actin, helping cells migrate to sites of injury.',
      'Promotes new blood-vessel growth (angiogenesis) and tissue remodeling.',
      'Has a systemic, whole-body distribution.',
    ],
    researchedUses: ['Soft-tissue and muscle recovery', 'Tendon/ligament healing', 'Flexibility and injury research'],
    protocol: { route: 'Subcutaneous injection', dose: '~2–2.5 mg', frequency: 'A couple times weekly (loading), then weekly', notes: 'Often cycled and stacked with BPC-157.' },
    halfLife: 'Longer-acting than many peptides; dosed a few times weekly.',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so a 2 mg dose = 0.4 mL (40 units on a U-100 syringe).',
    cautions: ['Not approved for human use; WADA-banned in sport.', 'Limited human safety data.', 'Theoretical caution with active cancer given pro-angiogenic effects.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2, doseUnit: 'mg' },
  },

  {
    slug: 'sermorelin',
    name: 'Sermorelin',
    aka: ['GRF 1-29'],
    category: 'Growth-hormone axis',
    tags: ['ghrh', 'gh pulse', 'anti-aging'],
    summary: 'A GHRH analog (the first 29 amino acids) that prompts the pituitary to release growth hormone.',
    sequence: 'GHRH(1-29) — the active fragment of growth-hormone-releasing hormone',
    overview:
      'Sermorelin is the 1–29 fragment of GHRH and one of the longest-studied GH secretagogues, historically used to test pituitary function and in anti-aging clinics. It nudges the body’s own GH production in a natural, pulsatile way rather than supplying GH directly.',
    mechanism: [
      'Binds pituitary GHRH receptors to release endogenous GH.',
      'Preserves the body’s natural GH pulse and feedback.',
    ],
    researchedUses: ['Raising endogenous GH/IGF-1', 'Age-related GH decline research', 'Sleep and recovery'],
    protocol: { route: 'Subcutaneous injection', dose: '~200–300 mcg', frequency: 'Nightly', notes: 'Bedtime dosing aligns with the natural GH pulse.' },
    halfLife: 'Short (~10–20 minutes).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 300 mcg = 12 units on a U-100 syringe.',
    cautions: ['Prescription/compounded where used; otherwise research status.', 'Injection-site reactions possible.', 'Caution with active malignancy.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 300, doseUnit: 'mcg' },
  },

  {
    slug: 'ghrp-2',
    name: 'GHRP-2',
    aka: ['Pralmorelin'],
    category: 'Growth-hormone axis',
    tags: ['ghrp', 'gh pulse'],
    summary: 'A growth-hormone-releasing peptide that strongly stimulates GH, with some appetite increase.',
    sequence: 'Hexapeptide GH secretagogue',
    overview:
      'GHRP-2 is a potent GH secretagogue that reliably raises growth hormone, with a modest bump in appetite and, at higher doses, cortisol/prolactin. It is often stacked with a GHRH (like CJC-1295 no DAC) for a stronger, more natural pulse.',
    mechanism: [
      'Activates the ghrelin/GH-secretagogue receptor to release GH.',
      'Mildly stimulates appetite; can nudge cortisol/prolactin at higher doses.',
    ],
    researchedUses: ['Raising endogenous GH/IGF-1', 'Body-composition and recovery research'],
    protocol: { route: 'Subcutaneous injection', dose: '~100–200 mcg', frequency: '1–3× daily', notes: 'Commonly combined with a GHRH.' },
    halfLife: 'Short (~30 minutes).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 100 mcg = 4 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Appetite increase; possible cortisol/prolactin rise at higher doses.', 'Caution with active cancer.'],
    calcPreset: { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'ghrp-6',
    name: 'GHRP-6',
    aka: [],
    category: 'Growth-hormone axis',
    tags: ['ghrp', 'gh pulse', 'appetite'],
    summary: 'A growth-hormone-releasing peptide known for a strong GH pulse and a pronounced hunger increase.',
    sequence: 'Hexapeptide GH secretagogue',
    overview:
      'GHRP-6 is an older GH secretagogue similar to GHRP-2 but with a more noticeable appetite spike, driven by its ghrelin-like activity. That hunger effect is sometimes used deliberately (e.g., to support eating) and sometimes an unwanted side effect.',
    mechanism: [
      'Activates the ghrelin/GH-secretagogue receptor to release GH.',
      'Strongly stimulates appetite (ghrelin-mimetic).',
    ],
    researchedUses: ['Raising endogenous GH/IGF-1', 'Appetite support research'],
    protocol: { route: 'Subcutaneous injection', dose: '~100–200 mcg', frequency: '1–3× daily', notes: 'Often stacked with a GHRH.' },
    halfLife: 'Short (~30 minutes).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 100 mcg = 4 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Marked hunger; possible cortisol/prolactin rise.', 'Caution with active cancer.'],
    calcPreset: { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'hexarelin',
    name: 'Hexarelin',
    aka: ['Examorelin'],
    category: 'Growth-hormone axis',
    tags: ['ghrp', 'gh pulse', 'potent'],
    summary: 'One of the most potent GH-releasing peptides, though tolerance can build with continued use.',
    sequence: 'Hexapeptide GH secretagogue',
    overview:
      'Hexarelin is among the strongest GHRPs for acutely raising growth hormone. Its potency comes with a catch — the GH response can diminish (desensitize) with frequent dosing — so it is often used more conservatively or cycled. It has also been studied for effects on the heart.',
    mechanism: [
      'Potently activates the GH-secretagogue receptor for a large GH pulse.',
      'Receptors can desensitize with frequent, high dosing.',
    ],
    researchedUses: ['Strong GH release research', 'Cardiac and recovery investigation'],
    protocol: { route: 'Subcutaneous injection', dose: '~100 mcg', frequency: '1–2× daily, often cycled', notes: 'Lower frequency helps limit desensitization.' },
    halfLife: 'Short (~30–70 minutes).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 100 mcg = 4 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'GH response can fade with overuse.', 'Possible cortisol/prolactin effects; caution with active cancer.'],
    calcPreset: { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'epithalon',
    name: 'Epithalon',
    aka: ['Epitalon', 'Epithalamin'],
    category: 'Longevity',
    tags: ['longevity', 'telomerase', 'sleep'],
    summary: 'A short Russian-developed peptide studied for telomerase activation, sleep, and healthy aging.',
    sequence: 'Ala-Glu-Asp-Gly (a tetrapeptide)',
    overview:
      'Epithalon is a four-amino-acid peptide derived from a pineal-gland extract, developed in Russia and studied for longevity. Research has looked at its effect on telomerase (the enzyme that maintains chromosome telomeres), circadian/melatonin regulation, and sleep. Most data come from Russian studies and animal models.',
    mechanism: [
      'Reported to increase telomerase activity, supporting telomere maintenance.',
      'Influences the pineal gland and melatonin/circadian rhythm.',
    ],
    researchedUses: ['Healthy-aging and longevity research', 'Sleep and circadian support'],
    protocol: { route: 'Subcutaneous injection', dose: '~5–10 mg per day in short cycles', frequency: 'Daily for a 10–20 day course', notes: 'Typically run as periodic cycles rather than continuously.' },
    halfLife: 'Very short in circulation.',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so a 2.5 mg dose = 0.5 mL (50 units on a U-100 syringe).',
    cautions: ['Not approved; most evidence is older/small studies.', 'Long-term human safety is unestablished.', 'Telomerase claims remain unproven in humans.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2.5, doseUnit: 'mg' },
  },

  {
    slug: 'thymosin-alpha-1',
    name: 'Thymosin Alpha-1',
    aka: ['Tα1', 'Thymalfasin'],
    category: 'Immune support',
    tags: ['immune', 'thymus', 'recovery'],
    summary: 'An immune-modulating peptide (approved in some countries) studied for infection and immune balance.',
    sequence: '28-amino-acid peptide derived from the thymus hormone prothymosin',
    overview:
      'Thymosin alpha-1 is a naturally occurring immune-signaling peptide produced by the thymus. It is approved in several countries as an adjunct for certain infections and as an immune modulator, and it has been studied broadly for balancing immune responses.',
    mechanism: [
      'Enhances T-cell maturation and function.',
      'Modulates innate immunity and inflammatory signaling toward balance.',
    ],
    researchedUses: ['Immune support', 'Adjunct in certain viral infections (approved use in some regions)', 'Recovery research'],
    protocol: { route: 'Subcutaneous injection', dose: '~1.6 mg', frequency: 'A couple times weekly', notes: 'Clinical protocols vary by indication.' },
    halfLife: 'Short (~2 hours).',
    reconstitution: 'Often a 5 mg or 10 mg vial. A 5 mg vial with 2 mL water = 2.5 mg/mL, so 1.6 mg = 64 units on a U-100 syringe.',
    cautions: ['Approved in some countries; research/off-label elsewhere.', 'Injection-site reactions possible.', 'Consult a clinician if immunocompromised or on immune therapy.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 1.6, doseUnit: 'mg' },
  },

  {
    slug: 'semaglutide',
    name: 'Semaglutide',
    aka: ['GLP-1 receptor agonist'],
    category: 'Metabolic & weight',
    tags: ['glp-1', 'weight loss', 'diabetes', 'fda-approved'],
    summary: 'An FDA-approved GLP-1 receptor agonist for type-2 diabetes and weight management.',
    sequence: 'Long-acting GLP-1 analog',
    overview:
      'Semaglutide is a GLP-1 receptor agonist approved for type-2 diabetes and chronic weight management. It mimics the gut hormone GLP-1 to lower blood sugar and reduce appetite, producing significant weight loss in trials. Its long half-life allows once-weekly injection.',
    mechanism: [
      'Activates GLP-1 receptors to boost insulin and lower glucagon in response to meals.',
      'Slows gastric emptying and reduces appetite via brain pathways.',
    ],
    researchedUses: ['Type-2 diabetes (approved)', 'Chronic weight management (approved)', 'Cardiometabolic research'],
    protocol: { route: 'Subcutaneous injection', dose: 'Titrated; ~0.25 mg → up to ~2.4 mg weekly', frequency: 'Once weekly', notes: 'Start low and increase slowly to limit nausea.' },
    halfLife: 'Long (~7 days) — once-weekly dosing.',
    reconstitution: 'Prescription pens come pre-filled. Research vials are commonly 5 mg; 5 mg with 2 mL water = 2.5 mg/mL, so 250 mcg = 10 units on a U-100 syringe.',
    cautions: ['Prescription medication; use under medical supervision.', 'Nausea, GI effects; boxed warning for thyroid C-cell tumors in rodents.', 'Not for personal-history MTC/MEN2 or pregnancy.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'tirzepatide',
    name: 'Tirzepatide',
    aka: ['Dual GIP/GLP-1 agonist'],
    category: 'Metabolic & weight',
    tags: ['glp-1', 'gip', 'weight loss', 'fda-approved'],
    summary: 'An FDA-approved dual GIP/GLP-1 receptor agonist for type-2 diabetes and weight loss.',
    sequence: 'Dual GIP/GLP-1 receptor agonist',
    overview:
      'Tirzepatide is a “twincretin” that activates both GIP and GLP-1 receptors, approved for type-2 diabetes and weight management. Hitting two incretin pathways produced some of the largest weight-loss and glucose-lowering results seen in trials, with once-weekly dosing.',
    mechanism: [
      'Dual agonist of GIP and GLP-1 receptors.',
      'Improves insulin response, lowers appetite, and slows gastric emptying.',
    ],
    researchedUses: ['Type-2 diabetes (approved)', 'Weight management (approved)', 'Cardiometabolic research'],
    protocol: { route: 'Subcutaneous injection', dose: 'Titrated; ~2.5 mg → up to ~15 mg weekly', frequency: 'Once weekly', notes: 'Titrate up slowly to limit GI side effects.' },
    halfLife: 'Long (~5 days) — once-weekly dosing.',
    reconstitution: 'Prescription pens are pre-filled. Research vials are commonly 10 mg; 10 mg with 2 mL water = 5 mg/mL, so 2.5 mg = 50 units on a U-100 syringe.',
    cautions: ['Prescription medication; use under medical supervision.', 'Nausea and GI effects; rodent thyroid C-cell tumor warning.', 'Not for personal-history MTC/MEN2 or pregnancy.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2.5, doseUnit: 'mg' },
  },

  {
    slug: 'retatrutide',
    name: 'Retatrutide',
    aka: ['Triple-G', 'LY3437943'],
    category: 'Metabolic & weight',
    tags: ['glp-1', 'gip', 'glucagon', 'weight loss'],
    summary: 'An investigational triple agonist (GLP-1 / GIP / glucagon) showing large weight-loss effects in trials.',
    sequence: 'Triple GIP/GLP-1/glucagon receptor agonist',
    overview:
      'Retatrutide is an investigational “triple agonist” that adds glucagon-receptor activity on top of GIP and GLP-1. Early-phase trials reported some of the largest weight reductions yet for this drug class. It is not approved — it is still in clinical development.',
    mechanism: [
      'Agonizes GLP-1, GIP, and glucagon receptors together.',
      'Combines appetite suppression and improved glucose handling with increased energy expenditure.',
    ],
    researchedUses: ['Obesity (investigational)', 'Type-2 diabetes and metabolic research'],
    protocol: { route: 'Subcutaneous injection', dose: 'Titrated in trials (up to ~12 mg weekly)', frequency: 'Once weekly', notes: 'Dosing comes from clinical trials; not an approved regimen.' },
    halfLife: 'Long — once-weekly dosing.',
    reconstitution: 'Research vials are commonly 10 mg; 10 mg with 2 mL water = 5 mg/mL, so 2 mg = 40 units on a U-100 syringe.',
    cautions: ['Not approved — still investigational.', 'GI side effects; may raise heart rate.', 'Long-term safety unknown; medical supervision essential.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2, doseUnit: 'mg' },
  },

  {
    slug: 'cjc-1295-dac',
    name: 'CJC-1295 with DAC',
    aka: ['CJC-1295 DAC', 'Drug Affinity Complex GRF'],
    category: 'Growth-hormone axis',
    tags: ['ghrh', 'long-acting', 'gh'],
    summary: 'The long-acting version of CJC-1295 — a Drug Affinity Complex binds it to albumin so one dose lasts about a week.',
    sequence: 'Modified GRF(1-29) with a Drug Affinity Complex (DAC)',
    overview:
      'CJC-1295 with DAC adds a molecule that binds to albumin in the blood, extending its life from minutes to roughly a week. That means a steadier, elevated GH/IGF-1 "bleed" rather than a sharp natural pulse — the opposite trade-off from the DAC-free version.',
    mechanism: [
      'Activates pituitary GHRH receptors like other GRF analogs.',
      'The DAC binds albumin, dramatically extending half-life for sustained GH elevation.',
    ],
    researchedUses: ['Sustained GH/IGF-1 elevation research', 'Body-composition and recovery contexts'],
    protocol: { route: 'Subcutaneous injection', dose: '~1–2 mg', frequency: 'Once or twice weekly', notes: 'Long half-life means far less frequent dosing than DAC-free.' },
    halfLife: 'Long — roughly 6–8 days.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 1 mg = 40 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Steady elevation differs from the natural pulsatile pattern.', 'Caution with active cancer given GH/IGF-1 effects.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 1, doseUnit: 'mg' },
  },

  {
    slug: 'igf-1-lr3',
    name: 'IGF-1 LR3',
    aka: ['Long R3 IGF-1'],
    category: 'Growth & muscle',
    tags: ['igf-1', 'muscle', 'anabolic'],
    summary: 'A long-acting analog of insulin-like growth factor 1, studied for muscle growth and recovery.',
    sequence: 'IGF-1 with an Arg3 substitution and a 13-amino-acid N-terminal extension',
    overview:
      'IGF-1 LR3 is a modified, longer-lasting version of IGF-1 — the downstream hormone through which growth hormone drives much of its muscle-building and repair effects. The modifications reduce its binding to carrier proteins, so more stays active for longer.',
    mechanism: [
      'Activates IGF-1 receptors to promote cell growth, protein synthesis, and repair.',
      'Reduced binding-protein affinity extends its active window vs native IGF-1.',
    ],
    researchedUses: ['Muscle growth and hyperplasia research', 'Recovery and tissue repair'],
    protocol: { route: 'Subcutaneous injection', dose: '~20–50 mcg', frequency: 'Daily in research contexts', notes: 'Often timed around training in anecdotal use.' },
    halfLife: 'Extended vs native IGF-1 (hours).',
    reconstitution: 'Often a 1 mg vial. 1 mg with 1 mL water = 1 mg/mL, so 50 mcg = 5 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Can lower blood sugar (hypoglycemia risk).', 'Pro-growth signaling — significant caution with any cancer risk.'],
    calcPreset: { vialMg: 1, waterMl: 1, dose: 50, doseUnit: 'mcg' },
  },

  {
    slug: 'follistatin-344',
    name: 'Follistatin 344',
    aka: ['FST-344'],
    category: 'Growth & muscle',
    tags: ['muscle', 'myostatin', 'recovery'],
    summary: 'A protein studied for muscle growth via inhibition of myostatin, a natural brake on muscle size.',
    sequence: 'Follistatin isoform (344 amino acids)',
    overview:
      'Follistatin binds and blocks myostatin — the body’s own signal that limits how much muscle you build. By inhibiting that brake, follistatin has drawn strong research interest for muscle growth and repair, mostly in animal and gene-therapy studies.',
    mechanism: [
      'Binds and neutralizes myostatin (GDF-8), releasing the natural cap on muscle growth.',
      'Also modulates related TGF-β family signals involved in tissue repair.',
    ],
    researchedUses: ['Muscle growth and wasting research', 'Recovery and fibrosis studies'],
    protocol: { route: 'Subcutaneous injection', dose: '~100 mcg', frequency: 'Daily in short cycles', notes: 'Typically run as brief cycles in research.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 1 mg vial. 1 mg with 2 mL water = 0.5 mg/mL, so 100 mcg = 20 units on a U-100 syringe.',
    cautions: ['Not approved for human use; minimal human data.', 'Myostatin inhibition has unknown long-term effects on the heart and tendons.', 'Research chemical status.'],
    calcPreset: { vialMg: 1, waterMl: 2, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'kisspeptin-10',
    name: 'Kisspeptin-10',
    aka: ['KP-10', 'Metastin fragment'],
    category: 'Hormonal & reproductive',
    tags: ['hormone', 'reproductive', 'libido'],
    summary: 'A signaling peptide that sits at the top of the reproductive hormone axis, studied for fertility and libido.',
    sequence: '10-amino-acid active fragment of kisspeptin',
    overview:
      'Kisspeptin is a master regulator of reproduction — it triggers the release of GnRH, which cascades down to LH, FSH, and the sex hormones. Kisspeptin-10 is the short active fragment, studied for fertility, hypogonadism, and sexual response.',
    mechanism: [
      'Stimulates GnRH neurons, driving downstream LH/FSH and sex-hormone release.',
      'Sits upstream of the entire hypothalamic-pituitary-gonadal axis.',
    ],
    researchedUses: ['Fertility and reproductive-hormone research', 'Libido and hypogonadism studies'],
    protocol: { route: 'Subcutaneous injection', dose: '~1 mg (varies widely)', frequency: 'Varies', notes: 'Clinical research uses precise, weight-based dosing.' },
    halfLife: 'Short (minutes).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 1 mg = 40 units on a U-100 syringe.',
    cautions: ['Not approved for general use.', 'Affects the whole reproductive-hormone axis — effects can be complex.', 'Consult a clinician, especially with hormone-sensitive conditions.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 1, doseUnit: 'mg' },
  },

  {
    slug: 'gonadorelin',
    name: 'Gonadorelin',
    aka: ['GnRH', 'Factrel'],
    category: 'Hormonal & reproductive',
    tags: ['gnrh', 'fertility', 'trt'],
    summary: 'A synthetic form of GnRH used to stimulate LH and FSH — popular alongside TRT to maintain testicular function.',
    sequence: 'Synthetic gonadotropin-releasing hormone (decapeptide)',
    overview:
      'Gonadorelin is lab-made GnRH, the hormone that tells the pituitary to release LH and FSH. It is used clinically in fertility testing and treatment, and in the TRT community as an alternative to HCG for keeping the testes active during testosterone therapy.',
    mechanism: [
      'Stimulates pituitary release of LH and FSH in a pulsatile fashion.',
      'Maintains testicular signaling that exogenous testosterone would otherwise suppress.',
    ],
    researchedUses: ['Maintaining testicular function on TRT', 'Fertility support and diagnostics'],
    protocol: { route: 'Subcutaneous injection', dose: '~100–200 mcg', frequency: 'Often 2–3× weekly (or as prescribed)', notes: 'Very short half-life; frequent small doses mimic natural pulses.' },
    halfLife: 'Very short (minutes).',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 100 mcg = 4 units on a U-100 syringe.',
    cautions: ['Prescription/compounded where used.', 'Overuse can paradoxically downregulate the axis.', 'Use under medical guidance, especially with TRT.'],
    calcPreset: { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'cagrilintide',
    name: 'Cagrilintide',
    aka: ['AM833'],
    category: 'Metabolic & weight',
    tags: ['weight loss', 'amylin', 'appetite'],
    summary: 'A long-acting amylin analog studied for weight loss, often paired with a GLP-1 agonist.',
    sequence: 'Long-acting amylin receptor agonist',
    overview:
      'Cagrilintide mimics amylin, a hormone that works alongside insulin to control appetite and slow digestion. It is investigational for weight management and has shown strong results combined with a GLP-1 agonist.',
    mechanism: [
      'Activates amylin and calcitonin receptors to increase fullness and slow gastric emptying.',
      'Complements GLP-1 agonists via a separate appetite pathway.',
    ],
    researchedUses: ['Weight management (investigational)', 'Combination therapy with GLP-1 agonists'],
    protocol: { route: 'Subcutaneous injection', dose: 'Titrated; up to ~2.4 mg weekly in trials', frequency: 'Once weekly', notes: 'Trial dosing; not an approved regimen.' },
    halfLife: 'Long — once-weekly dosing.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 300 mcg = 12 units on a U-100 syringe.',
    cautions: ['Not approved — still investigational.', 'GI side effects similar to other weight-loss agents.', 'Medical supervision essential.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 300, doseUnit: 'mcg' },
  },

  {
    slug: 'ss-31',
    name: 'SS-31',
    aka: ['Elamipretide', 'MTP-131'],
    category: 'Mitochondrial & longevity',
    tags: ['mitochondrial', 'longevity', 'recovery'],
    summary: 'A mitochondria-targeting peptide studied for cellular energy, recovery, and age-related decline.',
    sequence: 'Cell-permeable tetrapeptide that concentrates in mitochondria',
    overview:
      'SS-31 (elamipretide) homes in on the inner mitochondrial membrane, where it stabilizes cardiolipin — a lipid essential for efficient energy production. Research has explored it for heart, muscle, kidney, and eye conditions tied to failing mitochondria, as well as general recovery.',
    mechanism: [
      'Binds cardiolipin to stabilize the mitochondrial membrane and electron transport.',
      'Improves ATP production and reduces mitochondrial reactive oxygen species.',
    ],
    researchedUses: ['Mitochondrial dysfunction and fatigue research', 'Cardiac, muscle, and recovery studies'],
    protocol: { route: 'Subcutaneous injection', dose: '~5–10 mg', frequency: 'Daily in research/clinical trials', notes: 'Trial dosing varies by indication.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 50 mg vial. 50 mg with 5 mL water = 10 mg/mL, so 5 mg = 50 units on a U-100 syringe.',
    cautions: ['Investigational; not broadly approved.', 'Injection-site reactions reported in trials.', 'Long-term safety still being studied.'],
    calcPreset: { vialMg: 50, waterMl: 5, dose: 5, doseUnit: 'mg' },
  },

  {
    slug: 'kpv',
    name: 'KPV',
    aka: ['Lysine-Proline-Valine'],
    category: 'Gut & anti-inflammatory',
    tags: ['gut', 'anti-inflammatory', 'healing'],
    summary: 'A tiny tripeptide fragment of α-MSH studied for its anti-inflammatory effects, especially in the gut.',
    sequence: 'Lys-Pro-Val (a fragment of α-MSH)',
    overview:
      'KPV is the tail end of the α-MSH hormone, and it carries much of that hormone’s anti-inflammatory punch without the pigmentation effects. It is studied for inflammatory bowel conditions, wound healing, and skin inflammation, and is often used orally for gut-local effects.',
    mechanism: [
      'Reduces pro-inflammatory signaling (NF-κB pathway) inside cells.',
      'Acts locally in the gut and on immune cells to calm inflammation.',
    ],
    researchedUses: ['Gut inflammation (IBD models)', 'Wound healing and skin inflammation'],
    protocol: { route: 'Oral (gut-local) or subcutaneous injection', dose: '~200–500 mcg', frequency: 'Daily', notes: 'Often taken orally when the target is the gut.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 250 mcg = 10 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Limited human clinical data.', 'Research chemical status.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'll-37',
    name: 'LL-37',
    aka: ['Cathelicidin', 'CAP-18 fragment'],
    category: 'Immune & antimicrobial',
    tags: ['antimicrobial', 'immune', 'healing'],
    summary: 'A natural antimicrobial peptide studied for infection defense, wound healing, and immune modulation.',
    sequence: '37-amino-acid cathelicidin-derived peptide',
    overview:
      'LL-37 is part of the body’s innate immune defense — a naturally occurring peptide that can disrupt bacteria, fungi, and viruses directly while also shaping the immune and healing response. Research spans chronic infections, biofilms, and wound repair.',
    mechanism: [
      'Directly disrupts microbial membranes (broad antimicrobial action).',
      'Modulates immune signaling and supports wound healing and angiogenesis.',
    ],
    researchedUses: ['Antimicrobial and biofilm research', 'Wound healing and immune modulation'],
    protocol: { route: 'Subcutaneous injection', dose: '~100 mcg (varies)', frequency: 'Varies', notes: 'Research dosing; can cause local reactions.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 100 mcg = 4 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Can cause injection-site irritation/inflammation.', 'At high levels it may have pro-inflammatory effects.'],
    calcPreset: { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
  },

  {
    slug: 'oxytocin',
    name: 'Oxytocin',
    aka: ['The "bonding hormone"'],
    category: 'Neuro & hormonal',
    tags: ['mood', 'bonding', 'hormone'],
    summary: 'A natural hormone and neuropeptide involved in bonding, trust, and mood, studied intranasally for social and emotional effects.',
    sequence: 'Nonapeptide (9 amino acids)',
    overview:
      'Oxytocin is best known for its roles in childbirth and breastfeeding, but it also acts in the brain to influence bonding, trust, empathy, and stress. Research (usually intranasal) has explored it for social anxiety, mood, and relationship dynamics.',
    mechanism: [
      'Activates oxytocin receptors in the brain and body.',
      'Modulates social behavior, stress response, and emotional processing.',
    ],
    researchedUses: ['Social bonding, trust, and mood research', 'Anxiety and relationship studies'],
    protocol: { route: 'Intranasal (research); also clinical injectable forms', dose: '~10–40 IU intranasally', frequency: 'As used in studies', notes: 'Intranasal is the common research route.' },
    halfLife: 'Very short (minutes).',
    reconstitution: 'Typically used as a nasal spray or pre-made solution, so syringe-unit dosing usually does not apply.',
    cautions: ['Injectable oxytocin is a prescription drug (obstetrics).', 'Effects on mood/behavior are subtle and context-dependent.', 'Avoid unsupervised use in pregnancy.'],
    calcPreset: null,
  },

  {
    slug: 'adipotide',
    name: 'Adipotide',
    aka: ['FTPP', 'Prohibitin-TP01'],
    category: 'Fat loss',
    tags: ['fat loss', 'experimental'],
    summary: 'An experimental peptide studied for rapid fat loss by cutting the blood supply to fat tissue.',
    sequence: 'Targeting peptide fused to a pro-apoptotic sequence',
    overview:
      'Adipotide works differently from most fat-loss compounds: it targets the blood vessels that feed white fat tissue and triggers them to die, effectively starving fat cells. Primate studies showed significant fat loss, but kidney toxicity concerns have kept it firmly experimental.',
    mechanism: [
      'Homes to receptors on the vasculature of white adipose tissue.',
      'Triggers apoptosis of those blood vessels, causing fat cells to shrink.',
    ],
    researchedUses: ['Obesity research (primate/preclinical)'],
    protocol: { route: 'Subcutaneous injection', dose: 'Research/preclinical only', frequency: 'Short courses in studies', notes: 'No established human protocol.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 5 mg vial. Because of safety concerns it is rarely used; treat figures as reference only.',
    cautions: ['Not approved; flagged for kidney toxicity in animal studies.', 'No established human safety.', 'One of the more experimental compounds listed here.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 500, doseUnit: 'mcg' },
  },

  {
    slug: '5-amino-1mq',
    name: '5-Amino-1MQ',
    type: 'amino',
    aka: ['5-amino-1-methylquinolinium'],
    category: 'Metabolic & longevity',
    tags: ['metabolic', 'fat loss', 'nnmt'],
    summary: 'A small molecule (not a peptide) studied for metabolism and fat loss by blocking the enzyme NNMT.',
    sequence: 'Small-molecule NNMT inhibitor',
    overview:
      '5-Amino-1MQ is a small molecule popular in the peptide/longevity community. It inhibits NNMT, an enzyme that’s overactive in fat tissue and linked to slowed metabolism. Blocking it may raise cellular NAD+ and encourage fat cells to burn more energy. It’s usually taken orally.',
    mechanism: [
      'Inhibits nicotinamide N-methyltransferase (NNMT) in fat and muscle.',
      'May increase NAD+ and cellular energy expenditure.',
    ],
    researchedUses: ['Fat loss and metabolic research', 'Longevity and muscle-function studies'],
    protocol: { route: 'Oral (capsule)', dose: '~50–150 mg', frequency: 'Daily', notes: 'Usually a capsule, not an injection.' },
    halfLife: 'Not well characterized in humans.',
    reconstitution: 'Typically an oral capsule, so reconstitution/syringe math does not apply.',
    cautions: ['Not approved; human data are limited.', 'A research chemical, not a peptide.', 'Long-term safety unknown.'],
    calcPreset: null,
  },

  {
    slug: 'cerebrolysin',
    name: 'Cerebrolysin',
    aka: ['FPF-1070'],
    category: 'Cognitive & neuroprotective',
    tags: ['nootropic', 'neuroprotection', 'recovery'],
    summary: 'A mixture of neuropeptides and amino acids used in some countries for stroke, dementia, and brain injury.',
    sequence: 'Porcine-brain-derived peptide/amino-acid complex',
    overview:
      'Cerebrolysin is a standardized blend of small neuropeptides and amino acids derived from pig brain tissue. It is used clinically in parts of Europe and Asia for stroke recovery, dementia, and traumatic brain injury, and is popular as a nootropic. It comes as a ready-to-use solution.',
    mechanism: [
      'Mimics the action of natural neurotrophic factors that support neuron survival and growth.',
      'May reduce neuroinflammation and support synaptic plasticity.',
    ],
    researchedUses: ['Stroke and traumatic brain injury recovery (approved in some regions)', 'Dementia and cognitive research'],
    protocol: { route: 'Intramuscular or IV injection', dose: '~5–10 mL of solution', frequency: 'Daily courses', notes: 'Supplied pre-mixed in ampoules.' },
    halfLife: 'Varies by component.',
    reconstitution: 'Sold as a ready-to-inject solution, so lyophilized reconstitution and unit math do not apply.',
    cautions: ['Approved in some countries; unapproved in the US.', 'Animal-derived — allergy/contamination considerations.', 'Use under medical guidance.'],
    calcPreset: null,
  },

  {
    slug: 'dihexa',
    name: 'Dihexa',
    aka: ['N-hexanoic-Tyr-Ile-(6) aminohexanoic amide', 'PNB-0408'],
    category: 'Cognitive & neuroprotective',
    tags: ['nootropic', 'cognition', 'synapses'],
    summary: 'A potent nootropic peptide studied for forming new synaptic connections and supporting memory.',
    sequence: 'Modified angiotensin IV analog',
    overview:
      'Dihexa is a small, potent compound derived from angiotensin IV, researched for its striking ability to promote new synapse formation in the brain. Animal work has looked at it for memory and neurodegenerative models; human data are very limited.',
    mechanism: [
      'Enhances hepatocyte growth factor (HGF)/c-Met signaling tied to synapse growth.',
      'Promotes formation of new synaptic connections (synaptogenesis).',
    ],
    researchedUses: ['Memory and cognition research', 'Neurodegeneration models'],
    protocol: { route: 'Oral or transdermal (research)', dose: 'Small (research-scale)', frequency: 'Varies', notes: 'Extremely potent in animal studies; human dosing is not established.' },
    halfLife: 'Not well characterized.',
    reconstitution: 'Usually taken orally/transdermally, so injection unit math generally does not apply.',
    cautions: ['Not approved; minimal human safety data.', 'Its strong pro-growth effects on neurons are not fully understood long-term.', 'Research chemical status.'],
    calcPreset: null,
  },

  {
    slug: 'humanin',
    name: 'Humanin',
    aka: ['HN', 'Mitochondrial-derived peptide'],
    category: 'Longevity & mitochondrial',
    tags: ['longevity', 'mitochondrial', 'neuroprotection'],
    summary: 'A mitochondrial-derived peptide studied for cytoprotection, metabolism, and healthy aging.',
    sequence: '24-amino-acid mitochondrial-derived peptide',
    overview:
      'Humanin is a small peptide encoded in mitochondrial DNA (a cousin of MOTS-c) with broad cytoprotective effects. Research links it to protection of neurons, improved insulin sensitivity, and reduced cell death under stress, making it a longevity candidate.',
    mechanism: [
      'Protects cells from apoptosis (programmed death) under stress.',
      'Improves insulin sensitivity and modulates metabolic and inflammatory pathways.',
    ],
    researchedUses: ['Neuroprotection and healthy-aging research', 'Metabolic and cardiovascular studies'],
    protocol: { route: 'Subcutaneous injection', dose: 'Research-scale (varies)', frequency: 'Varies', notes: 'No established human protocol.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 250 mcg = 10 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Human data are early.', 'Research chemical status.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'pinealon',
    name: 'Pinealon',
    aka: ['Peptide bioregulator'],
    category: 'Longevity & cognitive',
    tags: ['bioregulator', 'longevity', 'cognition'],
    summary: 'A short Russian "bioregulator" tripeptide studied for brain function and healthy aging.',
    sequence: 'Glu-Asp-Arg (a tripeptide)',
    overview:
      'Pinealon is one of the Russian short-peptide "bioregulators" designed to target specific tissues — in this case the brain. Research (largely Russian) has explored it for cognition, neuroprotection, and circadian/anti-aging effects.',
    mechanism: [
      'Proposed to influence gene expression in neural tissue as a peptide bioregulator.',
      'Studied for antioxidant and neuroprotective effects.',
    ],
    researchedUses: ['Cognition and neuroprotection research', 'Healthy-aging / circadian studies'],
    protocol: { route: 'Subcutaneous injection', dose: '~5–10 mg per day in short cycles', frequency: 'Daily for a 10–20 day course', notes: 'Run as periodic cycles like other bioregulators.' },
    halfLife: 'Very short.',
    reconstitution: 'Often a 10 mg vial. 10 mg with 2 mL water = 5 mg/mL, so a 2.5 mg dose = 0.5 mL (50 units on a U-100 syringe).',
    cautions: ['Not approved; most evidence is Russian/older studies.', 'Bioregulator claims remain largely unproven in Western trials.', 'Research chemical status.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2.5, doseUnit: 'mg' },
  },

  {
    slug: 'thymulin',
    name: 'Thymulin',
    aka: ['Zinc-thymulin', 'FTS'],
    category: 'Immune support',
    tags: ['immune', 'thymus', 'anti-inflammatory'],
    summary: 'A zinc-dependent thymic peptide studied for immune balance and anti-inflammatory effects.',
    sequence: 'Nonapeptide that requires zinc for activity',
    overview:
      'Thymulin is a hormone from the thymus that only becomes active when bound to zinc. It helps regulate T-cell development and immune balance, and research has explored it for immune support, inflammation, and even pain signaling.',
    mechanism: [
      'Supports T-cell differentiation and immune regulation (zinc-dependent).',
      'Shows anti-inflammatory and neuroendocrine effects in studies.',
    ],
    researchedUses: ['Immune balance and anti-inflammatory research', 'Age-related immune decline studies'],
    protocol: { route: 'Subcutaneous injection', dose: 'Research-scale (varies)', frequency: 'Varies', notes: 'Adequate zinc status matters for activity.' },
    halfLife: 'Short.',
    reconstitution: 'Often a 5 mg vial. 5 mg with 2 mL water = 2.5 mg/mL, so 250 mcg = 10 units on a U-100 syringe.',
    cautions: ['Not approved for human use.', 'Limited modern human data.', 'Research chemical status.'],
    calcPreset: { vialMg: 5, waterMl: 2, dose: 250, doseUnit: 'mcg' },
  },

  {
    slug: 'survodutide',
    name: 'Survodutide',
    aka: ['BI 456906'],
    category: 'Metabolic & weight',
    tags: ['glp-1', 'glucagon', 'weight loss'],
    summary: 'An investigational dual glucagon/GLP-1 receptor agonist studied for weight loss and liver disease.',
    sequence: 'Dual glucagon/GLP-1 receptor agonist',
    overview:
      'Survodutide activates both the glucagon and GLP-1 receptors — combining appetite suppression with increased energy expenditure and liver-fat reduction. It is in clinical development for obesity and for MASH (fatty liver disease), where early results have been promising.',
    mechanism: [
      'Dual agonist of glucagon and GLP-1 receptors.',
      'Reduces appetite while raising energy expenditure and lowering liver fat.',
    ],
    researchedUses: ['Obesity (investigational)', 'MASH / fatty-liver disease research'],
    protocol: { route: 'Subcutaneous injection', dose: 'Titrated in trials', frequency: 'Once weekly', notes: 'Trial dosing; not an approved regimen.' },
    halfLife: 'Long — once-weekly dosing.',
    reconstitution: 'Research vials commonly 10 mg. 10 mg with 2 mL water = 5 mg/mL, so 2 mg = 40 units on a U-100 syringe.',
    cautions: ['Not approved — still investigational.', 'GI side effects; may raise heart rate.', 'Medical supervision essential.'],
    calcPreset: { vialMg: 10, waterMl: 2, dose: 2, doseUnit: 'mg' },
  },

  {
    slug: 'vip',
    name: 'VIP',
    aka: ['Vasoactive Intestinal Peptide'],
    category: 'Immune & respiratory',
    tags: ['anti-inflammatory', 'cirs', 'immune'],
    summary: 'A signaling peptide studied for inflammation, immune balance, and chronic inflammatory response syndrome (CIRS).',
    sequence: '28-amino-acid neuropeptide',
    overview:
      'VIP is a naturally occurring peptide with wide-ranging roles in blood flow, immune regulation, and inflammation. In the functional-medicine world it is used intranasally as a late step in protocols for CIRS (mold/biotoxin illness). It is also studied for lung and immune conditions.',
    mechanism: [
      'Activates VPAC receptors to reduce inflammation and modulate immune responses.',
      'Regulates blood flow and supports pulmonary and gut function.',
    ],
    researchedUses: ['CIRS / biotoxin-illness protocols', 'Inflammation and pulmonary research'],
    protocol: { route: 'Intranasal (common) or injection', dose: '~50 mcg per spray (protocol-dependent)', frequency: 'Several times daily in CIRS use', notes: 'Usually used only after other CIRS steps are addressed.' },
    halfLife: 'Very short (minutes).',
    reconstitution: 'Usually compounded as a nasal spray, so injection unit math generally does not apply.',
    cautions: ['Prescription/compounded where used.', 'Can lower blood pressure and cause flushing.', 'Best used under a knowledgeable clinician’s guidance.'],
    calcPreset: null,
  },

  {
    slug: 'snap-8',
    name: 'SNAP-8',
    aka: ['Acetyl Octapeptide-3'],
    category: 'Skin & cosmetic',
    tags: ['skin', 'anti-aging', 'topical'],
    summary: 'A cosmetic peptide used topically to soften expression lines — a "needle-free Botox" style ingredient.',
    sequence: 'Acetyl octapeptide-3 (8 amino acids)',
    overview:
      'SNAP-8 is a topical cosmetic peptide marketed as a gentler, needle-free alternative to Botox for expression wrinkles. It’s an extension of Argireline and works at the surface to reduce the muscle contractions that crease skin over time.',
    mechanism: [
      'Interferes with the SNARE complex that muscles use to release acetylcholine.',
      'Reduces the intensity of facial-muscle contractions that form expression lines.',
    ],
    researchedUses: ['Topical anti-wrinkle / expression-line cosmetics'],
    protocol: { route: 'Topical (serum/cream)', dose: 'Low cosmetic concentrations', frequency: 'Applied daily', notes: 'A topical cosmetic ingredient, not an injectable.' },
    halfLife: 'Topical/local; not systemically relevant.',
    reconstitution: 'Formulated into topical serums, so injection unit math does not apply.',
    cautions: ['Cosmetic use; not a medical treatment.', 'Possible local irritation in sensitive skin.', 'Effects are modest vs injectable neuromodulators.'],
    calcPreset: null,
  },
  {
    "slug": "mk-677",
    "name": "MK-677 (Ibutamoren)",
    "aka": [
      "Ibutamoren",
      "Ibutamoren mesylate",
      "MK-0677",
      "L-163,191"
    ],
    "category": "Growth-hormone axis",
    "tags": [
      "oral",
      "gh-secretagogue",
      "ghrelin",
      "appetite"
    ],
    "summary": "An orally active, non-peptide growth-hormone secretagogue studied for its ability to raise growth hormone and IGF-1 levels by mimicking the hormone ghrelin.",
    "sequence": "Non-peptide small molecule (ghrelin-receptor agonist)",
    "overview": "MK-677 is a small-molecule compound that acts as an agonist at the ghrelin receptor (GHSR) rather than a true peptide. Because it is orally bioavailable, it has been investigated in human trials as a way to sustain elevated growth-hormone and IGF-1 output without injection. Studies have looked at its effects on body composition, appetite and, in older adults, muscle and bone parameters. It remains an investigational compound and is not an approved medicine.",
    "mechanism": [
      "Acts as an agonist at the growth-hormone secretagogue receptor (GHSR), mimicking ghrelin.",
      "Appears to stimulate pulsatile release of growth hormone from the pituitary.",
      "Downstream elevation of IGF-1 is thought to mediate many of its studied effects.",
      "Also stimulates appetite through central ghrelin-receptor activity."
    ],
    "researchedUses": [
      "Raising growth-hormone and IGF-1 levels in clinical research",
      "Effects on lean body mass and body composition",
      "Investigated for age-related decline in muscle and bone"
    ],
    "protocol": {
      "route": "Oral (research)",
      "dose": "~10-25 mg per day",
      "frequency": "Once daily, commonly at night",
      "notes": "Oral dosing is frequently cited because the compound is not injected."
    },
    "halfLife": "Approximately 4-6 hours, supporting once-daily oral use in the literature.",
    "reconstitution": "Typically supplied as capsules or an oral solution rather than a lyophilized vial, so insulin-syringe unit math does not apply.",
    "cautions": [
      "A research chemical not approved by the FDA for human use; banned by WADA in sport.",
      "Reported to increase appetite and, in some studies, fasting glucose and insulin resistance.",
      "Can cause water retention and transient edema in the published literature.",
      "Long-term safety in healthy people is not well established."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "ghrp-1",
    "name": "GHRP-1",
    "aka": [
      "Growth Hormone Releasing Peptide-1"
    ],
    "category": "Growth-hormone axis",
    "tags": [
      "gh-secretagogue",
      "ghrelin",
      "pituitary",
      "injectable"
    ],
    "summary": "A synthetic growth-hormone-releasing peptide from the same family as GHRP-2 and GHRP-6, studied for its ability to stimulate growth-hormone secretion.",
    "sequence": "Ala-His-D-2-methyl-Trp-Ala-Trp-D-Phe-Lys-NH2",
    "overview": "GHRP-1 is one of the earlier synthetic growth-hormone-releasing peptides developed to probe the growth-hormone secretagogue pathway. Like related GHRPs, it acts on the ghrelin receptor to prompt the pituitary to release growth hormone. It has been used mainly as a research tool, and human clinical data are more limited than for some of its analogues. It is not an approved therapeutic.",
    "mechanism": [
      "Binds the growth-hormone secretagogue receptor (GHSR), acting as a ghrelin mimetic.",
      "Appears to stimulate growth-hormone release from the anterior pituitary.",
      "May also modestly influence prolactin and cortisol in some studies.",
      "Often described as working synergistically with GHRH analogues on GH output."
    ],
    "researchedUses": [
      "Stimulating endogenous growth-hormone secretion in research",
      "Investigating the GH secretagogue receptor pathway",
      "Comparative studies alongside other GHRPs"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~100 mcg per dose",
      "frequency": "One to three times daily in research contexts",
      "notes": "Doses are commonly cited on a per-injection basis, sometimes timed around GHRH co-administration."
    },
    "halfLife": "Short - on the order of tens of minutes, consistent with other GHRPs.",
    "reconstitution": "Commonly supplied as a 5 mg lyophilized vial. Reconstituting 5 mg with 2 mL of bacteriostatic water gives 2.5 mg/mL, so a 100 mcg dose is about 4 units on a U-100 syringe.",
    "cautions": [
      "A research chemical not approved by the FDA for human use; prohibited in competitive sport.",
      "Human safety and long-term data are limited.",
      "May transiently raise cortisol and prolactin at higher doses.",
      "Purity of research-grade material varies by source."
    ],
    "calcPreset": { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "hgh-fragment-176-191",
    "name": "HGH Fragment 176-191",
    "aka": [
      "HGH Frag 176-191",
      "AOD-9604 (related analogue)",
      "Lipolytic fragment"
    ],
    "category": "Fat loss & metabolism",
    "tags": [
      "fat-loss",
      "lipolysis",
      "gh-fragment",
      "injectable"
    ],
    "summary": "A short fragment corresponding to the C-terminal region of human growth hormone, studied mainly for its reported fat-mobilizing (lipolytic) activity without the broader growth effects of full GH.",
    "sequence": "16-amino-acid C-terminal fragment of human growth hormone (residues 176-191)",
    "overview": "HGH Fragment 176-191 is a synthetic peptide representing the tail end of the growth-hormone molecule. Research interest centers on the observation that this region appears to carry much of GH's fat-metabolizing activity while largely lacking its effects on IGF-1 and blood glucose. Most supporting data come from animal and laboratory studies; robust human evidence is limited. It is an investigational compound, not an approved drug.",
    "mechanism": [
      "Appears to stimulate lipolysis (breakdown of stored fat) in adipose tissue.",
      "Thought to reduce lipogenesis, limiting new fat accumulation.",
      "Reported to act largely independently of the IGF-1 pathway in research models.",
      "Mimics the fat-regulating portion of full-length growth hormone."
    ],
    "researchedUses": [
      "Fat reduction and body-composition research in animal models",
      "Investigating GH-independent lipolytic pathways",
      "Comparative studies with the related analogue AOD-9604"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~250-500 mcg per day",
      "frequency": "Once or split across the day, often on an empty stomach",
      "notes": "Fasted timing is frequently mentioned in research discussions of lipolytic peptides."
    },
    "halfLife": "Short - estimated at roughly 30 minutes; not firmly established in humans.",
    "reconstitution": "Commonly supplied as a 5 mg lyophilized vial. Reconstituting 5 mg with 2 mL of bacteriostatic water gives 2.5 mg/mL, so a 250 mcg dose is about 10 units on a U-100 syringe.",
    "cautions": [
      "A research chemical not approved by the FDA for human use; prohibited in competitive sport.",
      "Human efficacy data for fat loss remain limited and mixed.",
      "Long-term safety has not been established.",
      "Research-grade purity varies widely by source."
    ],
    "calcPreset": {
      "vialMg": 5,
      "waterMl": 2,
      "dose": 250,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "igf-1-des",
    "name": "IGF-1 DES",
    "aka": [
      "IGF-1 DES(1-3)",
      "DES(1-3)IGF-1"
    ],
    "category": "Growth & muscle",
    "tags": [
      "igf",
      "muscle",
      "anabolic",
      "injectable"
    ],
    "summary": "A truncated variant of insulin-like growth factor 1 missing its first three amino acids, studied for its potent, short-acting anabolic activity in tissue.",
    "sequence": "Truncated IGF-1 analogue lacking the N-terminal Gly-Pro-Glu tripeptide",
    "overview": "IGF-1 DES is a naturally occurring shortened form of IGF-1 in which the first three residues are removed. This modification reduces its binding to IGF-binding proteins, which in research settings makes it more locally potent and faster-acting than standard IGF-1. It has been studied largely in laboratory and animal models for effects on cell growth and muscle tissue. It is an investigational compound and is not approved for human use.",
    "mechanism": [
      "Activates the IGF-1 receptor to promote cell growth and protein synthesis.",
      "Reduced affinity for IGF-binding proteins is thought to increase local, short-lived potency.",
      "Appears to support satellite-cell activation and muscle hypertrophy in research models.",
      "Shares downstream signaling with native IGF-1 but with a shorter window of action."
    ],
    "researchedUses": [
      "Local muscle-growth and hypertrophy research",
      "Studies of IGF-1 receptor signaling",
      "Comparative work with full-length IGF-1 analogues"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~50-100 mcg per day",
      "frequency": "Once daily in research contexts",
      "notes": "Doses are typically small owing to the peptide's reported potency."
    },
    "halfLife": "Very short - on the order of minutes, reflecting reduced binding-protein interaction.",
    "reconstitution": "Commonly supplied as a 1 mg lyophilized vial. Reconstituting 1 mg with 1 mL of bacteriostatic water gives 1 mg/mL, so a 50 mcg dose is about 5 units on a U-100 syringe.",
    "cautions": [
      "A research chemical not approved by the FDA for human use; banned in sport.",
      "IGF-signaling compounds carry theoretical concerns around unregulated cell growth.",
      "May affect blood glucose; human safety data are minimal.",
      "Research-grade purity and concentration vary by source."
    ],
    "calcPreset": {
      "vialMg": 1,
      "waterMl": 1,
      "dose": 50,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "mgf",
    "name": "MGF (Mechano Growth Factor)",
    "aka": [
      "Mechano Growth Factor",
      "IGF-1Ec splice variant"
    ],
    "category": "Growth & muscle",
    "tags": [
      "igf",
      "muscle",
      "repair",
      "injectable"
    ],
    "summary": "A splice variant of IGF-1 produced in muscle in response to mechanical stress, studied for its role in muscle repair and satellite-cell activation.",
    "sequence": "IGF-1Ec splice variant (C-terminal E-domain peptide of mechano growth factor)",
    "overview": "MGF is a naturally occurring splice variant of IGF-1 that muscle tissue expresses after mechanical loading or damage. Research suggests it plays an early role in activating muscle stem (satellite) cells and initiating repair before the effects of systemic IGF-1 take over. Synthetic MGF has been used as a research tool to study these processes. Native MGF is short-acting, and it is not an approved therapeutic.",
    "mechanism": [
      "Appears to activate muscle satellite cells, supporting repair after mechanical stress.",
      "Thought to act through a distinct pathway from mature IGF-1 in early repair.",
      "May help delay differentiation so more precursor cells are available for growth.",
      "Signals locally in muscle tissue rather than acting as a systemic hormone."
    ],
    "researchedUses": [
      "Muscle repair and satellite-cell research",
      "Studies of exercise-induced muscle adaptation",
      "Investigating IGF-1 splice-variant signaling"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~100-200 mcg per dose",
      "frequency": "Often cited around training in research discussions",
      "notes": "Native MGF is short-lived, which motivated development of the pegylated PEG-MGF form."
    },
    "halfLife": "Very short - on the order of minutes for the native peptide.",
    "reconstitution": "Commonly supplied as a 2 mg lyophilized vial. Reconstituting 2 mg with 2 mL of bacteriostatic water gives 1 mg/mL, so a 100 mcg dose is about 10 units on a U-100 syringe.",
    "cautions": [
      "A research chemical not approved by the FDA for human use; prohibited in sport.",
      "Human efficacy and safety data are very limited.",
      "IGF-related signaling raises theoretical growth-related concerns.",
      "Research-grade purity varies by source."
    ],
    "calcPreset": {
      "vialMg": 2,
      "waterMl": 2,
      "dose": 100,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "peg-mgf",
    "name": "PEG-MGF",
    "aka": [
      "Pegylated MGF",
      "Pegylated Mechano Growth Factor"
    ],
    "category": "Growth & muscle",
    "tags": [
      "igf",
      "muscle",
      "repair",
      "injectable"
    ],
    "summary": "A pegylated version of mechano growth factor engineered for a longer half-life, studied for muscle repair and satellite-cell activation over a more sustained window.",
    "sequence": "Pegylated form of MGF (IGF-1Ec splice-variant peptide with attached PEG group)",
    "overview": "PEG-MGF is mechano growth factor modified by attaching polyethylene glycol (PEG) to slow its breakdown. Because native MGF is cleared within minutes, this modification aims to extend its presence in circulation so it can act over hours rather than minutes. Research interest parallels that of MGF, focusing on muscle repair and satellite-cell activation. It is an investigational compound and is not approved for human use.",
    "mechanism": [
      "Retains MGF's proposed role in activating muscle satellite cells.",
      "PEGylation is intended to slow enzymatic degradation and prolong action.",
      "Thought to support early muscle repair signaling over an extended window.",
      "Acts through IGF-1 splice-variant pathways rather than as systemic IGF-1."
    ],
    "researchedUses": [
      "Muscle repair and recovery research",
      "Studies extending MGF activity beyond its natural short window",
      "Investigating satellite-cell activation after exercise"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~200-400 mcg per dose",
      "frequency": "Two to three times weekly in research discussions",
      "notes": "Less frequent dosing than native MGF is cited because of the extended half-life."
    },
    "halfLife": "Extended relative to native MGF - estimated on the order of hours to a day.",
    "reconstitution": "Commonly supplied as a 2 mg lyophilized vial. Reconstituting 2 mg with 2 mL of bacteriostatic water gives 1 mg/mL, so a 200 mcg dose is about 20 units on a U-100 syringe.",
    "cautions": [
      "A research chemical not approved by the FDA for human use; prohibited in sport.",
      "Human safety and efficacy data are very limited.",
      "IGF-related signaling raises theoretical growth-related concerns.",
      "Research-grade purity varies by source."
    ],
    "calcPreset": {
      "vialMg": 2,
      "waterMl": 2,
      "dose": 200,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "ace-031",
    "name": "ACE-031",
    "aka": [
      "ACVR2B-Fc",
      "Ramatercept"
    ],
    "category": "Growth & muscle",
    "tags": [
      "myostatin",
      "muscle",
      "activin",
      "injectable"
    ],
    "summary": "A soluble decoy receptor (activin receptor type IIB fused to an antibody fragment) studied for increasing muscle mass by blocking myostatin and related signals.",
    "sequence": "Fusion protein: activin receptor type IIB extracellular domain linked to an IgG Fc region",
    "overview": "ACE-031 is not a small peptide but an engineered fusion protein designed to act as a decoy for the activin receptor type IIB. By capturing myostatin and related ligands before they reach the true receptor, it removes a natural brake on muscle growth. It was investigated in clinical trials, including in Duchenne muscular dystrophy, before development was discontinued over safety signals. It is not an approved medicine.",
    "mechanism": [
      "Acts as a soluble decoy receptor that binds myostatin and related TGF-beta ligands.",
      "Prevents these ligands from activating the true activin type IIB receptor.",
      "Removing myostatin's inhibitory signal is thought to promote muscle growth.",
      "Also binds other ligands such as activin, which contributed to off-target effects."
    ],
    "researchedUses": [
      "Muscle-mass preservation research in muscular dystrophy trials",
      "Studies of myostatin inhibition and muscle growth",
      "Investigating activin-pathway biology"
    ],
    "protocol": {
      "route": "Subcutaneous injection (clinical research)",
      "dose": "~1-3 mg per kg body weight in trials",
      "frequency": "Roughly every few weeks in clinical study protocols",
      "notes": "Dosing was weight-based and given under clinical-trial supervision."
    },
    "halfLife": "Long - on the order of one to two weeks, typical of Fc-fusion proteins.",
    "reconstitution": "Supplied in clinical trials as a lyophilized protein reconstituted per study protocol; weight-based dosing means simple fixed-unit syringe math does not directly apply.",
    "cautions": [
      "Not an approved drug; clinical development was halted over safety concerns.",
      "Trials reported bleeding-related events such as nosebleeds and gum bleeding.",
      "Off-target activin binding produced effects beyond muscle.",
      "Prohibited in competitive sport as a myostatin-pathway agent."
    ],
    "calcPreset": {
      "vialMg": 1,
      "waterMl": 1,
      "dose": 1,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "follistatin-315",
    "name": "Follistatin 315",
    "aka": [
      "FST-315",
      "Follistatin isoform 315"
    ],
    "category": "Growth & muscle",
    "tags": [
      "myostatin",
      "muscle",
      "activin",
      "injectable"
    ],
    "summary": "The circulating 315-amino-acid isoform of the natural protein follistatin, studied for its ability to bind and neutralize myostatin and activin, potentially favoring muscle growth.",
    "sequence": "315-residue circulating isoform of the follistatin protein",
    "overview": "Follistatin 315 is the main blood-borne isoform of follistatin, a naturally occurring protein that binds and inhibits members of the TGF-beta family, including myostatin and activin. Because myostatin limits muscle growth, research has examined follistatin as a way to release that brake. The 315 isoform has lower affinity for cell-surface heparan sulfate than the 288 isoform, giving it a more systemic distribution. It is an investigational protein and is not approved for human use.",
    "mechanism": [
      "Binds and neutralizes myostatin, reducing its brake on muscle growth.",
      "Also binds activin and other TGF-beta family ligands.",
      "The 315 isoform circulates more systemically due to lower heparan-sulfate binding.",
      "Net effect in research models is a shift toward muscle growth signaling."
    ],
    "researchedUses": [
      "Myostatin-inhibition and muscle-growth research",
      "Studies of follistatin isoform distribution and activity",
      "Investigating activin and TGF-beta pathway regulation"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~100 mcg per day",
      "frequency": "Often cited over short multi-day cycles in research discussions",
      "notes": "Dosing figures come from research contexts, not established human protocols."
    },
    "halfLife": "Not firmly established; the 315 isoform is described as more systemically distributed than the 288 form.",
    "reconstitution": "Commonly supplied as a 1 mg lyophilized vial. Reconstituting 1 mg with 1 mL of bacteriostatic water gives 1 mg/mL, so a 100 mcg dose is about 10 units on a U-100 syringe.",
    "cautions": [
      "A research protein not approved by the FDA for human use; prohibited in sport.",
      "Human safety and efficacy data are very limited.",
      "Broad TGF-beta pathway inhibition raises theoretical off-target concerns.",
      "Research-grade purity varies by source."
    ],
    "calcPreset": {
      "vialMg": 1,
      "waterMl": 1,
      "dose": 100,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "mazdutide",
    "name": "Mazdutide",
    "aka": [
      "IBI362",
      "LY3305677"
    ],
    "category": "Metabolic & weight",
    "tags": [
      "glp-1",
      "glucagon",
      "metabolic",
      "investigational",
      "weight"
    ],
    "summary": "An investigational GLP-1/glucagon dual receptor agonist studied in the literature for metabolic and body-weight endpoints.",
    "sequence": "GLP-1/glucagon dual receptor agonist analog (oxyntomodulin-based)",
    "overview": "Mazdutide is an investigational peptide designed to activate both the GLP-1 and glucagon receptors, an approach based on the oxyntomodulin backbone. It has been evaluated in clinical trials but is not an approved medicine, and it appears here only for educational context.",
    "mechanism": [
      "Reported to co-activate GLP-1 and glucagon receptors.",
      "GLP-1 activity is commonly cited as supporting glucose-dependent insulin secretion and appetite reduction.",
      "Glucagon-receptor activity is described in the literature as potentially increasing energy expenditure.",
      "The dual approach is studied for combined effects on glucose and body weight."
    ],
    "researchedUses": [
      "Body-weight reduction in clinical trial populations",
      "Glycemic control endpoints in type 2 diabetes studies",
      "Explored in the literature for effects on hepatic fat"
    ],
    "protocol": {
      "route": "Subcutaneous injection (investigational / research)",
      "dose": "~3-9 mg once weekly has been cited in reported trials; not an established or approved regimen",
      "frequency": "Commonly cited as once weekly in study designs",
      "notes": "Investigational compound; figures reflect reported trial ranges, not clinical guidance."
    },
    "halfLife": "Reported to support once-weekly dosing; precise human values are not firmly established.",
    "reconstitution": "If supplied as a lyophilized research vial, reconstituting 10 mg with 2 mL bacteriostatic water gives 5 mg/mL, so 1 mg would be about 20 units on a U-100 syringe. Figures are illustrative only.",
    "cautions": [
      "Investigational research compound; not FDA-approved for any use.",
      "Human safety and long-term data remain limited.",
      "Research-grade material purity varies by source.",
      "Educational context only and not a recommendation to use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 1,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "aicar",
    "name": "AICAR",
    "aka": [
      "Acadesine",
      "AICA riboside",
      "5-aminoimidazole-4-carboxamide ribonucleotide"
    ],
    "category": "Metabolic & mitochondrial",
    "tags": [
      "ampk",
      "metabolic",
      "mitochondrial",
      "research",
      "endurance"
    ],
    "summary": "A non-peptide small molecule studied in the research literature as an AMPK activator with metabolic effects.",
    "sequence": "Non-peptide small molecule (nucleoside analog)",
    "overview": "AICAR is a nucleoside analog that serves as a precursor in purine synthesis and is widely studied as a direct activator of AMP-activated protein kinase (AMPK). Most data come from cell and animal models, and it is included here strictly for educational context.",
    "mechanism": [
      "Commonly cited as mimicking AMP to activate the AMPK energy-sensing pathway.",
      "Reported to promote glucose uptake in muscle tissue in models.",
      "Associated with increased fatty-acid oxidation in the literature.",
      "Described as influencing mitochondrial biogenesis signaling."
    ],
    "researchedUses": [
      "Metabolic and exercise-mimetic effects in animal models",
      "Cellular energy-metabolism research",
      "Studied historically in cardioprotection experiments"
    ],
    "protocol": {
      "route": "Injection or oral in research settings (not standardized)",
      "dose": "No established human dose; animal studies cite figures on a mg/kg basis that do not translate directly to people",
      "frequency": "Not established",
      "notes": "A research chemical without standardized human dosing; figures are experimental only."
    },
    "halfLife": "Short in published animal data; human values are not firmly established.",
    "reconstitution": "Handled as a research chemical rather than a standardized injectable product, so consumer insulin-syringe unit math does not meaningfully apply.",
    "cautions": [
      "Research chemical; not approved for human use and prohibited by WADA in sport.",
      "Human safety data are minimal.",
      "Research-grade purity varies by source.",
      "Presented for educational context only, not as a recommendation."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "l-carnitine",
    "name": "L-Carnitine",
    "aka": [
      "Levocarnitine",
      "L-3-hydroxy-4-trimethylaminobutyrate"
    ],
    "category": "Energy & metabolism",
    "tags": [
      "amino",
      "energy",
      "metabolism",
      "fatty-acid",
      "supplement"
    ],
    "summary": "An amino-acid-derived compound cited in the literature for its role in fatty-acid transport and energy metabolism.",
    "sequence": "Amino acid derivative (quaternary ammonium compound)",
    "overview": "L-Carnitine is a naturally occurring compound synthesized from lysine and methionine that plays a central role in shuttling long-chain fatty acids into mitochondria. It is available as a dietary supplement and, in a prescription form, for specific deficiency states; this entry is educational context only.",
    "mechanism": [
      "Commonly cited as transporting long-chain fatty acids across the mitochondrial membrane.",
      "Supports beta-oxidation for energy production in the literature.",
      "Reported to influence the acyl-CoA to CoA ratio in cells.",
      "Described as a factor in cellular energy substrate handling."
    ],
    "researchedUses": [
      "Primary and secondary carnitine deficiency (prescription indication for levocarnitine)",
      "Studied for exercise and recovery endpoints",
      "Explored in the literature for cardiovascular and metabolic contexts"
    ],
    "protocol": {
      "route": "Oral supplement most commonly; an injectable prescription form exists for deficiency",
      "dose": "Supplement figures are commonly cited around 500-2000 mg per day; not a recommendation",
      "frequency": "Once or split through the day in cited use",
      "notes": "Most often taken orally, so consumer injection-unit math does not apply."
    },
    "halfLife": "Reported on the order of hours; varies with formulation and route.",
    "reconstitution": "Typically taken orally as a supplement, so vial reconstitution and insulin-syringe unit conversions do not apply.",
    "cautions": [
      "Available as a dietary supplement; the injectable form is prescription-only for deficiency states.",
      "High oral intake has been associated with gastrointestinal effects in the literature.",
      "Supplement quality and labeling accuracy vary by manufacturer.",
      "Educational context only; not medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "alpha-lipoic-acid",
    "name": "Alpha-Lipoic Acid (ALA)",
    "aka": [
      "Thioctic acid",
      "ALA",
      "1,2-dithiolane-3-pentanoic acid"
    ],
    "category": "Metabolic & mitochondrial",
    "tags": [
      "antioxidant",
      "metabolic",
      "mitochondrial",
      "supplement",
      "glucose"
    ],
    "summary": "A naturally occurring compound cited in the literature as an antioxidant and cofactor in mitochondrial energy metabolism.",
    "sequence": "Non-peptide small molecule (organosulfur cofactor)",
    "overview": "Alpha-lipoic acid is a sulfur-containing compound that acts as a cofactor for mitochondrial enzyme complexes and is widely studied for its antioxidant properties. It is sold as a dietary supplement and is included here only for educational context.",
    "mechanism": [
      "Commonly cited as a cofactor for mitochondrial dehydrogenase complexes.",
      "Reported to function as a redox-active antioxidant in both water- and lipid-soluble environments.",
      "Associated with regeneration of other antioxidants such as vitamins C and E in the literature.",
      "Described as influencing insulin-signaling and glucose-uptake pathways in some studies."
    ],
    "researchedUses": [
      "Studied for diabetic peripheral neuropathy symptoms in trial settings",
      "Explored in the literature for oxidative-stress endpoints",
      "Examined for glucose-metabolism-related outcomes"
    ],
    "protocol": {
      "route": "Oral supplement most commonly; an intravenous form has been used in some research and clinical settings",
      "dose": "Oral figures are commonly cited around 300-600 mg per day; not a recommendation",
      "frequency": "Once or split daily in cited use",
      "notes": "Most often taken orally, so consumer injection-unit math does not apply."
    },
    "halfLife": "Short - reported around 30 minutes for oral forms in published data.",
    "reconstitution": "Typically an oral supplement, so vial reconstitution and insulin-syringe unit conversions do not apply.",
    "cautions": [
      "Sold as a dietary supplement; not an approved drug in the United States.",
      "Has been associated with lowered blood glucose, which is noted in the literature.",
      "Supplement quality and dosing accuracy vary by manufacturer.",
      "Presented for educational context only, not as medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "nmn",
    "name": "NMN (Nicotinamide Mononucleotide)",
    "aka": [
      "Nicotinamide mononucleotide",
      "β-NMN"
    ],
    "category": "Longevity & cellular energy",
    "tags": [
      "nad",
      "longevity",
      "cellular-energy",
      "supplement",
      "metabolic"
    ],
    "summary": "A NAD+ precursor cited in the research literature for cellular energy metabolism and aging-related endpoints.",
    "sequence": "Non-peptide small molecule (nucleotide)",
    "overview": "NMN is a nucleotide that serves as a direct precursor in the biosynthesis of NAD+, a coenzyme central to cellular energy and repair processes. Much of the interest comes from animal aging research, and it is included here strictly for educational context.",
    "mechanism": [
      "Commonly cited as a precursor that raises intracellular NAD+ levels.",
      "NAD+ is described as a coenzyme for redox reactions and sirtuin activity.",
      "Reported in animal models to support mitochondrial function.",
      "Associated in the literature with metabolic and DNA-repair pathways."
    ],
    "researchedUses": [
      "Aging and metabolic endpoints in animal models",
      "Early human studies of NAD+ levels and metabolic markers",
      "Explored in the literature for exercise and muscle-function contexts"
    ],
    "protocol": {
      "route": "Oral supplement most commonly (capsule, sublingual)",
      "dose": "Figures commonly cited in early human studies range roughly 250-900 mg per day; not a recommendation",
      "frequency": "Once daily in cited use",
      "notes": "Generally taken orally, so consumer injection-unit math does not apply."
    },
    "halfLife": "Rapidly metabolized in published animal data; firm human values are not established.",
    "reconstitution": "Typically an oral supplement, so vial reconstitution and insulin-syringe unit conversions do not apply.",
    "cautions": [
      "Sold as a dietary supplement; regulatory status has been contested and is evolving.",
      "Long-term human safety and efficacy data remain limited.",
      "Supplement quality and labeling accuracy vary by manufacturer.",
      "Educational context only; not medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "glutathione",
    "name": "Glutathione",
    "aka": [
      "GSH",
      "L-glutathione",
      "gamma-glutamylcysteinylglycine"
    ],
    "category": "Longevity & cellular energy",
    "tags": [
      "antioxidant",
      "tripeptide",
      "longevity",
      "detox",
      "supplement"
    ],
    "summary": "An endogenous tripeptide antioxidant cited in the literature for redox balance and cellular detoxification.",
    "sequence": "Gamma-Glu-Cys-Gly",
    "overview": "Glutathione is a tripeptide made of glutamate, cysteine, and glycine that is a major intracellular antioxidant and a participant in detoxification pathways. It is available as a supplement and is sometimes administered parenterally in clinical settings; this entry is educational context only.",
    "mechanism": [
      "Commonly cited as a primary intracellular antioxidant that neutralizes reactive oxygen species.",
      "Reported to serve as a cofactor for glutathione peroxidase enzymes.",
      "Associated with phase-II detoxification and conjugation reactions in the literature.",
      "Described as helping regenerate other antioxidants such as vitamin C."
    ],
    "researchedUses": [
      "Oxidative-stress endpoints in research settings",
      "Studied in liver and detoxification contexts",
      "Explored in the literature for skin and dermatologic endpoints"
    ],
    "protocol": {
      "route": "Oral or liposomal supplement; sometimes given intravenously or intramuscularly in clinical settings",
      "dose": "Dosing is expressed in milligrams and varies widely by route; parenteral figures are cited in the hundreds of mg per session in some settings",
      "frequency": "Varies by route and setting",
      "notes": "When given IV or IM it is dosed in milligrams by a clinician, so consumer insulin-syringe unit math via micrograms does not apply."
    },
    "halfLife": "Short in circulation; reported on the order of minutes for the reduced form in plasma.",
    "reconstitution": "Oral forms are taken by mouth, and parenteral forms are dosed in milligrams by a clinician, so consumer insulin-syringe unit conversions do not apply.",
    "cautions": [
      "Oral forms are sold as supplements; injectable use is a clinical procedure that should involve a licensed provider.",
      "Compounded injectable products have been the subject of safety and sterility concerns.",
      "Supplement quality and bioavailability vary widely by formulation.",
      "Presented for educational context only, not as medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "n-acetyl-semax",
    "name": "N-Acetyl Semax",
    "aka": [
      "N-Acetyl Semax Amidate",
      "NA-Semax"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "nootropic",
      "intranasal",
      "bdnf",
      "acth",
      "focus"
    ],
    "summary": "An acetylated analog of the Semax peptide, commonly cited in the research literature for context around cognition and neuroprotection.",
    "sequence": "N-acetylated analog of the ACTH(4-7) fragment Pro-Gly-Pro extension (heptapeptide derivative)",
    "overview": "N-Acetyl Semax is a chemically modified version of Semax, itself derived from a fragment of adrenocorticotropic hormone (ACTH). The acetyl modification is described in the literature as intended to improve metabolic stability. Most available data come from Russian preclinical and early clinical work; broad Western clinical trials are lacking. Presented here for educational context only, not as a recommendation to use.",
    "mechanism": [
      "Reported in research to influence brain-derived neurotrophic factor (BDNF) expression.",
      "Appears to modulate the melanocortin and ACTH-related signaling systems.",
      "Studied for possible effects on dopaminergic and serotonergic activity."
    ],
    "researchedUses": [
      "Cognitive performance and attention in exploratory studies",
      "Neuroprotection in animal models of ischemia",
      "Mood and stress regulation in early research"
    ],
    "protocol": {
      "route": "Intranasal (research)",
      "dose": "~200-600 mcg per day (commonly cited)",
      "frequency": "Once daily or split across the day",
      "notes": "Typically applied as nasal drops or spray; the acetyl form is cited as longer-acting than plain Semax."
    },
    "halfLife": "Reported to be longer than unmodified Semax, still on the order of hours.",
    "reconstitution": "Usually supplied as a pre-mixed intranasal solution, so insulin-syringe injection unit math does not apply.",
    "cautions": [
      "Not FDA-approved; sold as a research chemical in most markets.",
      "Human clinical evidence outside of Russian studies is limited.",
      "Purity and concentration vary widely by source.",
      "Not established as safe for general use."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "n-acetyl-selank",
    "name": "N-Acetyl Selank",
    "aka": [
      "N-Acetyl Selank Amidate",
      "NA-Selank"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "anxiolytic",
      "intranasal",
      "nootropic",
      "tuftsin",
      "stress"
    ],
    "summary": "An acetylated analog of Selank, commonly cited in the research literature for context around anxiety and cognition.",
    "sequence": "N-acetylated analog of Selank, a synthetic heptapeptide based on the immunomodulator tuftsin",
    "overview": "N-Acetyl Selank is a modified form of Selank, a synthetic peptide derived from the endogenous tetrapeptide tuftsin. The acetylation is described as intended to increase stability. As with Semax, most data originate from Russian research settings, and large-scale international clinical trials are absent. Included for educational context only.",
    "mechanism": [
      "Reported to modulate GABAergic and serotonergic signaling.",
      "Appears to influence expression of brain-derived neurotrophic factor (BDNF).",
      "Studied for effects on the balance of pro- and anti-inflammatory cytokines."
    ],
    "researchedUses": [
      "Anxiety-like behavior in animal and early human studies",
      "Cognitive and memory support in exploratory research",
      "Stress resilience and mood regulation"
    ],
    "protocol": {
      "route": "Intranasal (research)",
      "dose": "~250-900 mcg per day (commonly cited)",
      "frequency": "Once daily or divided into two or three applications",
      "notes": "Applied as nasal drops or spray; the acetyl form is cited as more stable than plain Selank."
    },
    "halfLife": "Reported longer than unmodified Selank, still relatively short (hours).",
    "reconstitution": "Typically sold as a ready-to-use intranasal solution, so injection unit math does not apply.",
    "cautions": [
      "Not FDA-approved; classified as a research chemical in most markets.",
      "Evidence base is largely limited to Russian studies.",
      "Concentration and purity differ between suppliers.",
      "Long-term safety is not established."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "p21",
    "name": "P21 (P021)",
    "aka": [
      "P021",
      "Peptide 021"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "neurogenesis",
      "bdnf",
      "cntf",
      "neuroprotective",
      "research"
    ],
    "summary": "A small peptidergic compound derived from a CNTF motif, commonly cited in the research literature for context around neurogenesis.",
    "sequence": "Ciliary neurotrophic factor (CNTF)-derived peptidomimetic (short modified peptide)",
    "overview": "P21 (also written P021) is a peptide mimetic based on an active region of ciliary neurotrophic factor (CNTF), with chemical modifications described as improving stability and brain penetration. It has been examined in rodent models of neurodegeneration and memory impairment. There is no established human clinical use, and it is presented here strictly for educational context.",
    "mechanism": [
      "Reported to enhance neurogenesis in the hippocampus.",
      "Appears to increase brain-derived neurotrophic factor (BDNF) signaling.",
      "Studied for inhibition of leukemia inhibitory factor (LIF) signaling and reduction of tau pathology in models."
    ],
    "researchedUses": [
      "Memory and learning in rodent models",
      "Models of Alzheimer-type neurodegeneration",
      "General neurogenesis and neuroprotection research"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~0.5-1 mg per day (figures extrapolated from animal work)",
      "frequency": "Once daily",
      "notes": "Human dosing is not established; cited figures are from preclinical studies scaled loosely."
    },
    "halfLife": "Not well characterized in humans; modifications are described as extending stability.",
    "reconstitution": "For a 5 mg vial reconstituted with 2 mL, the concentration is 2.5 mg/mL, so a 500 mcg dose is about 20 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; strictly a research compound.",
      "No human clinical data exist.",
      "Dosing is speculative and extrapolated from animals.",
      "Purity varies by source."
    ],
    "calcPreset": {
      "vialMg": 5,
      "waterMl": 2,
      "dose": 500,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "fgl",
    "name": "FGL (FG Loop Peptide)",
    "aka": [
      "FG Loop Peptide",
      "FGL(L)",
      "NCAM-derived peptide"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "ncam",
      "neuroplasticity",
      "bdnf",
      "memory",
      "research"
    ],
    "summary": "A peptide derived from the neural cell adhesion molecule, commonly cited in the research literature for context around plasticity and memory.",
    "sequence": "Peptide derived from the second fibronectin type III module of neural cell adhesion molecule (NCAM)",
    "overview": "FGL (FG Loop peptide) is a synthetic peptide corresponding to a region of the neural cell adhesion molecule (NCAM). In animal research it has been examined for effects on synaptic plasticity and memory. Human evidence is minimal and it has no approved use; the information here is for educational context only.",
    "mechanism": [
      "Reported to activate the fibroblast growth factor receptor (FGFR).",
      "Appears to promote synaptic plasticity and neurite outgrowth.",
      "Studied for anti-inflammatory and neuroprotective effects in the brain."
    ],
    "researchedUses": [
      "Learning and memory in rodent models",
      "Neuroprotection in models of injury and aging",
      "Reduction of neuroinflammation in animal studies"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~1-5 mg (figures extrapolated from animal work)",
      "frequency": "Intermittent dosing in studies",
      "notes": "Human dosing has not been established; some early studies used intranasal and injectable routes."
    },
    "halfLife": "Not well established in humans; described as short.",
    "reconstitution": "For a 10 mg vial reconstituted with 2 mL, the concentration is 5 mg/mL, so a 1 mg dose is about 20 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research-stage compound.",
      "Human clinical data are very limited.",
      "Dosing figures are speculative.",
      "Source purity is not guaranteed."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 1,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "pe-22-28",
    "name": "PE-22-28",
    "aka": [
      "Spadin analog",
      "PE 22-28"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "antidepressant",
      "trek-1",
      "neurogenesis",
      "spadin",
      "research"
    ],
    "summary": "A short peptide analog of spadin, commonly cited in the research literature for context around mood and neuroplasticity.",
    "sequence": "Seven-residue analog of spadin, derived from the sortilin propeptide",
    "overview": "PE-22-28 is a truncated analog of spadin, a peptide fragment from the sortilin propeptide. It has been studied in animal models as a blocker of the TREK-1 potassium channel, a target linked to mood regulation. There is no established human use, and it is presented here for educational context only.",
    "mechanism": [
      "Reported to inhibit the TREK-1 (two-pore potassium) channel.",
      "Appears to promote hippocampal neurogenesis in models.",
      "Studied for rapid antidepressant-like effects in rodents."
    ],
    "researchedUses": [
      "Depression-like behavior in animal models",
      "Neurogenesis and synaptic plasticity research",
      "Exploratory work on stroke and neuroprotection"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~200-600 mcg (figures extrapolated from animal work)",
      "frequency": "Once daily in studies",
      "notes": "Human dosing is not established; some research used intranasal delivery."
    },
    "halfLife": "Reported to be short (minutes to a few hours).",
    "reconstitution": "For a 5 mg vial reconstituted with 2 mL, the concentration is 2.5 mg/mL, so a 250 mcg dose is about 10 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical.",
      "No human clinical evidence is available.",
      "Dosing is speculative and extrapolated.",
      "Purity varies by supplier."
    ],
    "calcPreset": {
      "vialMg": 5,
      "waterMl": 2,
      "dose": 250,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "cortexin",
    "name": "Cortexin",
    "aka": [
      "Cortexin polypeptide complex"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "polypeptide",
      "neuroprotective",
      "extract",
      "intramuscular",
      "research"
    ],
    "summary": "A polypeptide extract of animal cerebral cortex, commonly cited in the research literature for context around neuroprotection.",
    "sequence": "Mixture of low-molecular-weight polypeptides extracted from bovine or porcine cerebral cortex",
    "overview": "Cortexin is not a single peptide but a complex of low-molecular-weight polypeptides extracted from the cerebral cortex of cattle or pigs. It is marketed as a medicine in Russia and some neighboring countries for neurological conditions. Rigorous international clinical validation is lacking, and it is included here for educational context only.",
    "mechanism": [
      "Reported to have tissue-specific neurotrophic effects.",
      "Appears to modulate the balance of excitatory and inhibitory neurotransmitters.",
      "Studied for antioxidant and anti-inflammatory activity in the brain."
    ],
    "researchedUses": [
      "Recovery after stroke or brain injury in regional studies",
      "Cognitive impairment and encephalopathy",
      "Epilepsy and neurological development in exploratory work"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~10 mg per day (commonly cited)",
      "frequency": "Once daily for a course of 10 days",
      "notes": "Typically supplied as a lyophilized vial dissolved in saline or procaine before intramuscular injection."
    },
    "halfLife": "Not well characterized, given the mixed polypeptide composition.",
    "reconstitution": "For a 10 mg vial reconstituted with 2 mL, the concentration is 5 mg/mL, so a 10 mg dose is the full 2 mL (about 200 units), usually given intramuscularly rather than with an insulin syringe.",
    "cautions": [
      "Not FDA-approved; approved only in some non-Western markets.",
      "Independent clinical evidence is limited.",
      "Being an animal-derived extract, composition can vary by batch.",
      "Allergic reactions to animal protein are possible."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 10,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "noopept",
    "name": "Noopept",
    "aka": [
      "N-phenylacetyl-L-prolylglycine ethyl ester",
      "GVS-111",
      "Omberacetam"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "nootropic",
      "oral",
      "racetam",
      "memory",
      "research"
    ],
    "summary": "A synthetic dipeptide-derived nootropic, commonly cited in the research literature for context around memory and cognition.",
    "sequence": "Dipeptide-derived nootropic (N-phenylacetyl-L-prolylglycine ethyl ester)",
    "overview": "Noopept is a synthetic compound structurally related to the racetam family but built on a proline-glycine dipeptide backbone. It has been studied primarily in Russian research settings for memory and neuroprotection. It is sold as a dietary supplement in some countries and is unregulated or restricted in others. Presented for educational context only.",
    "mechanism": [
      "Reported to increase expression of nerve growth factor (NGF) and BDNF.",
      "Appears to modulate glutamatergic AMPA and NMDA receptor activity.",
      "Studied for antioxidant effects and reduction of excitotoxicity."
    ],
    "researchedUses": [
      "Memory and learning in animal and early human studies",
      "Cognitive recovery after injury in exploratory research",
      "Anxiety and mild cognitive impairment"
    ],
    "protocol": {
      "route": "Oral (research)",
      "dose": "~10-30 mg per day (commonly cited)",
      "frequency": "Split into two or three doses",
      "notes": "Taken orally as tablets, capsules, or sublingual powder; cited as far more potent by weight than piracetam."
    },
    "halfLife": "Short, reported at roughly 30-60 minutes for the parent compound.",
    "reconstitution": "Taken orally rather than injected, so insulin-syringe unit math does not apply.",
    "cautions": [
      "Not FDA-approved; regulatory status varies from supplement to controlled substance by country.",
      "Human clinical data are limited and mostly regional.",
      "Long-term safety is not well established.",
      "Product purity varies by supplier."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "alpha-gpc",
    "name": "Alpha-GPC",
    "aka": [
      "L-Alpha-glycerylphosphorylcholine",
      "Choline alfoscerate",
      "Alpha-glycerophosphocholine"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "choline",
      "oral",
      "nootropic",
      "acetylcholine",
      "supplement"
    ],
    "summary": "A choline-containing compound, commonly cited in the research literature for context around acetylcholine support and cognition.",
    "sequence": "Non-peptide choline compound (L-alpha-glycerylphosphorylcholine)",
    "overview": "Alpha-GPC is a naturally occurring choline compound that serves as a source of choline for the body. It is sold as a dietary supplement in the United States and as a prescription cognition drug in some other countries. Research has examined it for cognitive support and, separately, for acute physical performance. Presented for educational context only.",
    "mechanism": [
      "Reported to raise choline availability for acetylcholine synthesis.",
      "Appears to support cell membrane phospholipid metabolism.",
      "Studied for effects on growth hormone release after acute dosing."
    ],
    "researchedUses": [
      "Cognitive support in aging and dementia research",
      "Acute physical and power performance studies",
      "Recovery of cognition after stroke in regional trials"
    ],
    "protocol": {
      "route": "Oral (supplement)",
      "dose": "~300-600 mg per day (commonly cited)",
      "frequency": "Once or twice daily",
      "notes": "Taken orally as capsules or powder; some studies used higher acute doses before exercise."
    },
    "halfLife": "Choline from Alpha-GPC is absorbed and metabolized over several hours.",
    "reconstitution": "Taken orally rather than injected, so insulin-syringe unit math does not apply.",
    "cautions": [
      "Sold as a dietary supplement in the US; not an FDA-approved drug here.",
      "Some research links high long-term choline intake to cardiovascular markers, warranting caution.",
      "Quality and dose accuracy vary by brand.",
      "Not intended to diagnose or treat any condition."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "l-theanine",
    "name": "L-Theanine",
    "aka": [
      "N-ethyl-L-glutamine",
      "Theanine"
    ],
    "category": "Sleep & stress",
    "tags": [
      "relaxation",
      "oral",
      "amino acid",
      "focus",
      "supplement"
    ],
    "summary": "An amino acid found in tea, commonly cited in the research literature for context around calm focus and stress.",
    "sequence": "Non-protein amino acid (N-ethyl-L-glutamine)",
    "overview": "L-Theanine is a non-protein amino acid found naturally in tea leaves and some mushrooms. It is widely sold as a dietary supplement and has been studied for effects on relaxation, attention, and sleep quality, often in combination with caffeine. Presented for educational context only.",
    "mechanism": [
      "Reported to increase alpha brain-wave activity associated with relaxed alertness.",
      "Appears to modulate GABA, dopamine, and serotonin levels.",
      "Studied for a dampening effect on the stimulant edge of caffeine."
    ],
    "researchedUses": [
      "Relaxation and stress reduction in human studies",
      "Attention and reaction time when paired with caffeine",
      "Sleep quality support in exploratory research"
    ],
    "protocol": {
      "route": "Oral (supplement)",
      "dose": "~100-400 mg per day (commonly cited)",
      "frequency": "Once daily or as needed",
      "notes": "Taken orally as capsules or powder; frequently combined with caffeine in research."
    },
    "halfLife": "Relatively short, on the order of a few hours.",
    "reconstitution": "Taken orally rather than injected, so insulin-syringe unit math does not apply.",
    "cautions": [
      "Sold as a dietary supplement; not an FDA-approved drug.",
      "Generally well tolerated in studies, but individual responses vary.",
      "May add to the effects of other calming agents.",
      "Not intended to diagnose or treat any condition."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "gaba",
    "name": "GABA",
    "aka": [
      "Gamma-aminobutyric acid",
      "4-aminobutanoic acid"
    ],
    "category": "Sleep & stress",
    "tags": [
      "relaxation",
      "oral",
      "amino acid",
      "sleep",
      "supplement"
    ],
    "summary": "The main inhibitory neurotransmitter, sold as a supplement and commonly cited in the research literature for context around relaxation and sleep.",
    "sequence": "Non-protein amino acid neurotransmitter (gamma-aminobutyric acid)",
    "overview": "GABA (gamma-aminobutyric acid) is the primary inhibitory neurotransmitter in the mammalian central nervous system. It is also sold as an oral dietary supplement, though how much orally taken GABA reaches the brain is debated because of the blood-brain barrier. Presented for educational context only.",
    "mechanism": [
      "Acts at GABA receptors to reduce neuronal excitability within the nervous system.",
      "Oral supplementation is reported to possibly act on the enteric nervous system rather than crossing into the brain directly.",
      "Studied for effects on stress markers and heart-rate variability."
    ],
    "researchedUses": [
      "Relaxation and stress reduction in supplement studies",
      "Sleep onset support in exploratory research",
      "Blood-pressure and stress-marker effects in small trials"
    ],
    "protocol": {
      "route": "Oral (supplement)",
      "dose": "~100-750 mg per day (commonly cited)",
      "frequency": "Once daily, often before bed",
      "notes": "Taken orally as capsules or powder; brain penetration from oral dosing is scientifically debated."
    },
    "halfLife": "Short in circulation, on the order of hours.",
    "reconstitution": "Taken orally rather than injected, so insulin-syringe unit math does not apply.",
    "cautions": [
      "Sold as a dietary supplement; not an FDA-approved drug.",
      "Evidence that oral GABA affects the brain is mixed.",
      "May add to the effects of other sedating agents.",
      "Not intended to diagnose or treat any condition."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "taurine",
    "name": "Taurine",
    "aka": [
      "2-aminoethanesulfonic acid"
    ],
    "category": "Energy & metabolism",
    "tags": [
      "amino acid",
      "oral",
      "metabolism",
      "antioxidant",
      "supplement"
    ],
    "summary": "A sulfur-containing amino acid, commonly cited in the research literature for context around metabolism and cellular function.",
    "sequence": "Sulfur-containing amino acid (2-aminoethanesulfonic acid)",
    "overview": "Taurine is a conditionally essential amino acid found in high concentrations in the heart, muscle, and brain. It is common in the diet and in energy drinks, and is sold as a supplement. Research has examined roles in exercise, metabolism, and cellular protection. Presented for educational context only.",
    "mechanism": [
      "Reported to support cell volume regulation and osmotic balance.",
      "Appears to have antioxidant and calcium-handling roles in muscle and heart cells.",
      "Studied for effects on bile acid conjugation and mitochondrial function."
    ],
    "researchedUses": [
      "Exercise performance and recovery in human studies",
      "Metabolic and cardiovascular markers in research",
      "Antioxidant and cellular protection in models"
    ],
    "protocol": {
      "route": "Oral (supplement)",
      "dose": "~500-3000 mg per day (commonly cited)",
      "frequency": "Once daily or divided",
      "notes": "Taken orally as capsules or powder; higher acute doses appear in some exercise studies."
    },
    "halfLife": "Cleared over roughly one hour in circulation, with tissue stores turning over more slowly.",
    "reconstitution": "Taken orally rather than injected, so insulin-syringe unit math does not apply.",
    "cautions": [
      "Sold as a dietary supplement; not an FDA-approved drug.",
      "Generally well tolerated, but high intakes are not fully studied long-term.",
      "Quality and dose accuracy vary by brand.",
      "Not intended to diagnose or treat any condition."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "thymosin-beta-4",
    "name": "Thymosin Beta-4",
    "aka": [
      "TB-4",
      "Tβ4",
      "Thymosin β4"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "healing",
      "actin",
      "angiogenesis",
      "wound",
      "tissue"
    ],
    "summary": "A naturally occurring actin-binding peptide studied for wound healing, tissue repair, and cell migration.",
    "sequence": "Ac-Ser-Asp-Lys-Pro-Asp-Met-Ala-Glu-Ile-Glu-Lys-Phe-Asp-Lys-Ser-Lys-Leu-Lys-Lys-Thr-Glu-Thr-Gln-Glu-Lys-Asn-Pro-Leu-Pro-Ser-Lys-Glu-Thr-Ile-Glu-Gln-Glu-Lys-Gln-Ala-Gly-Glu-Ser",
    "overview": "Thymosin Beta-4 is a 43-amino-acid peptide found in many tissues and body fluids, where it acts primarily as a regulator of actin, a key building block of the cell's cytoskeleton. It is commonly cited in the research literature for context on cell migration, angiogenesis, and wound repair. The peptide TB-500 is a related fragment often discussed alongside it. Most evidence comes from cell and animal studies; robust human clinical data remain limited.",
    "mechanism": [
      "Binds and sequesters G-actin, influencing cytoskeletal dynamics and cell migration.",
      "Appears to promote angiogenesis and endothelial cell movement.",
      "Studied for modulation of inflammation and reduction of scar formation.",
      "Reported to support recruitment of progenitor cells to sites of injury."
    ],
    "researchedUses": [
      "Soft-tissue and muscle repair in animal models",
      "Corneal and dermal wound healing research",
      "Cardiac tissue repair studies",
      "Reduction of fibrosis and scarring in preclinical work"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~2-5 mg per week",
      "frequency": "Split across two or more doses per week",
      "notes": "Loading and maintenance phases are sometimes described in the literature."
    },
    "halfLife": "Longer than many small peptides; systemic exposure reported over roughly a day.",
    "reconstitution": "Commonly a 10 mg vial; 10 mg in 2 mL gives 5 mg/mL, so a 2 mg dose is about 40 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; sold only as a research chemical, not for human use.",
      "Prohibited in sport by WADA.",
      "Human clinical evidence is limited.",
      "Purity and content vary by source."
    ],
    "calcPreset": { vialMg: 10, waterMl: 2, dose: 2, doseUnit: 'mg' },
    "type": "peptide"
  },
  {
    "slug": "ara-290",
    "name": "ARA-290 (Cibinetide)",
    "aka": [
      "Cibinetide",
      "Cibinetida",
      "pHBSP"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "neuropathy",
      "tissue-protection",
      "inflammation",
      "epo-derived"
    ],
    "summary": "A short synthetic peptide derived from erythropoietin, studied for tissue protection and neuropathic pain without erythropoietin's blood effects.",
    "sequence": "Short synthetic 11-amino-acid peptide modeled on a region of erythropoietin",
    "overview": "ARA-290, also called cibinetide, is an 11-amino-acid peptide designed to mimic a tissue-protective region of the erythropoietin molecule while avoiding effects on red blood cell production. It is commonly cited in the research literature for context on the innate repair receptor and neuroinflammation. It has been examined in early clinical studies, including work on sarcoidosis-associated small-fiber neuropathy, though evidence remains preliminary.",
    "mechanism": [
      "Targets the innate repair receptor (a complex involving the beta-common receptor).",
      "Appears to modulate inflammation and support tissue protection.",
      "Studied for effects on nerve fiber density and neuropathic pain.",
      "Does not meaningfully stimulate erythropoiesis, unlike erythropoietin itself."
    ],
    "researchedUses": [
      "Small-fiber neuropathy in sarcoidosis (clinical research)",
      "Neuropathic pain modeling",
      "Anti-inflammatory tissue-protection studies",
      "Metabolic and diabetic complication research"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "~2-4 mg per day",
      "frequency": "Once daily in reported studies",
      "notes": "Trial protocols have used daily dosing over several weeks."
    },
    "halfLife": "Very short - minutes to a few hours.",
    "reconstitution": "Commonly a 4 mg vial; 4 mg in 2 mL gives 2 mg/mL, so a 4 mg dose is about 100 units on a U-100 syringe.",
    "cautions": [
      "Not an approved drug; investigational and sold as a research chemical.",
      "Human data come from small early-phase studies.",
      "Long-term safety is not established.",
      "Source purity varies."
    ],
    "calcPreset": {
      "vialMg": 4,
      "waterMl": 2,
      "dose": 4000,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "thymalin",
    "name": "Thymalin",
    "aka": [
      "Thymus polypeptide fraction",
      "Thymalinum"
    ],
    "category": "Immune support",
    "tags": [
      "immune",
      "thymus",
      "peptide-fraction",
      "bioregulator"
    ],
    "summary": "A polypeptide fraction extracted from thymus tissue, studied in Russian research for immune regulation.",
    "sequence": "Thymus polypeptide fraction (mixture, not a single defined sequence)",
    "overview": "Thymalin is a preparation of small polypeptides isolated from the thymus gland of cattle, developed within Soviet and Russian research on the thymus and immunity. It is commonly cited in that literature for context on restoring immune balance, particularly T-cell function. The evidence base is largely non-Western and older, and independent large-scale clinical trials are limited.",
    "mechanism": [
      "Reported to influence differentiation and activity of T-lymphocytes.",
      "Appears to help normalize immune cell ratios in some studies.",
      "Studied for modulation of inflammatory and immune signaling.",
      "Described as a thymic peptide bioregulator rather than a single receptor ligand."
    ],
    "researchedUses": [
      "Immune modulation in Russian clinical reports",
      "Support during infection or immune suppression (research)",
      "Age-related immune decline studies",
      "Adjunct research in inflammatory conditions"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~10 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Traditionally supplied as a lyophilized powder for reconstitution."
    },
    "halfLife": "Not well characterized; considered short-acting.",
    "reconstitution": "Commonly a 10 mg vial; 10 mg in 2 mL gives 5 mg/mL, so a 10 mg dose is about 40 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; regulatory status outside its origin countries is that of a research chemical.",
      "Most evidence is non-Western, older, and of limited quality.",
      "Composition is a fraction, so batch consistency can vary.",
      "Human safety data are limited."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 10000,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "thymogen",
    "name": "Thymogen",
    "aka": [
      "Glutamyl-Tryptophan",
      "Glutoxim-related dipeptide",
      "Thymogenum"
    ],
    "category": "Immune support",
    "tags": [
      "immune",
      "dipeptide",
      "thymus",
      "bioregulator"
    ],
    "summary": "A synthetic dipeptide developed in Russian research as an immunomodulator related to thymic peptides.",
    "sequence": "Glu-Trp",
    "overview": "Thymogen is a synthetic dipeptide (glutamyl-tryptophan) developed as a simplified, defined follow-on to thymus extract preparations. It is commonly cited in the Russian research literature for context on immune regulation and is described in intranasal and injectable forms. As with related thymic peptides, most supporting data are non-Western and independent verification is limited.",
    "mechanism": [
      "Reported to influence T-cell maturation and immune cell activity.",
      "Appears to modulate cytokine and inflammatory responses.",
      "Studied as a synthetic analog of thymic immunoregulatory peptides.",
      "Proposed to help normalize immune parameters rather than broadly stimulate them."
    ],
    "researchedUses": [
      "Immune support during infection (research reports)",
      "Recovery from immune suppression in Russian studies",
      "Adjunct research in inflammatory conditions",
      "Mucosal immunity via intranasal formulations"
    ],
    "protocol": {
      "route": "Intramuscular injection or intranasal (research)",
      "dose": "~100 mcg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Also described as a nasal solution in some formulations."
    },
    "halfLife": "Short - on the order of hours.",
    "reconstitution": "Commonly a 5 mg vial; 5 mg in 2 mL gives 2.5 mg/mL, so a 100 mcg dose is about 4 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; treated as a research chemical outside its origin countries.",
      "Evidence is largely non-Western and limited in scope.",
      "Long-term human safety data are lacking.",
      "Source purity varies."
    ],
    "calcPreset": { vialMg: 5, waterMl: 5, dose: 100, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "foxo4-dri",
    "name": "FOXO4-DRI",
    "aka": [
      "FOXO4-D-Retro-Inverso peptide",
      "FOXO4 DRI"
    ],
    "category": "Longevity",
    "tags": [
      "senolytic",
      "aging",
      "p53",
      "apoptosis",
      "research"
    ],
    "summary": "A synthetic retro-inverso peptide studied as a senolytic that targets senescent cells in aging research.",
    "sequence": "Retro-inverso peptide based on a FOXO4 interaction motif",
    "overview": "FOXO4-DRI is a D-retro-inverso peptide designed to disrupt the interaction between the FOXO4 protein and p53 inside senescent (aged, non-dividing) cells. It is commonly cited in the research literature for context on senolytics - agents intended to selectively remove senescent cells. The concept comes largely from a single line of mouse research; human clinical data do not exist, and it remains an experimental tool.",
    "mechanism": [
      "Designed to interfere with FOXO4-p53 binding in senescent cells.",
      "Aims to release p53 to trigger apoptosis selectively in those cells.",
      "The retro-inverso design is intended to improve stability against enzymes.",
      "Proposed to reduce senescent-cell burden associated with aging."
    ],
    "researchedUses": [
      "Senescent-cell clearance in mouse models",
      "Aging and tissue-function research",
      "Chemotherapy-related senescence studies",
      "General senolytic mechanism investigation"
    ],
    "protocol": {
      "route": "Subcutaneous or intraperitoneal injection (animal research)",
      "dose": "Reported in animal studies on a per-kilogram basis; no established human dose",
      "frequency": "Intermittent dosing (e.g., several times over a period) in animal work",
      "notes": "Human protocols are not established; figures are extrapolated from rodent studies."
    },
    "halfLife": "Not well characterized in humans.",
    "reconstitution": "Commonly a 10 mg vial; 10 mg in 2 mL gives 5 mg/mL, so a 1 mg dose is about 4 units on a U-100 syringe. No validated human dose exists.",
    "cautions": [
      "Not approved for any use; a purely experimental research chemical.",
      "No human clinical data exist.",
      "Senolytic mechanisms can affect healthy cells; safety is unknown.",
      "Evidence rests on limited preclinical work."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 1000,
      "doseUnit": "mcg"
    },
    "type": "peptide"
  },
  {
    "slug": "vilon",
    "name": "Vilon",
    "aka": [
      "Lys-Glu dipeptide",
      "Vilon bioregulator"
    ],
    "category": "Longevity",
    "tags": [
      "bioregulator",
      "dipeptide",
      "aging",
      "immune",
      "khavinson"
    ],
    "summary": "A short synthetic dipeptide bioregulator from Khavinson's Russian research, studied for immune and aging-related effects.",
    "sequence": "Lys-Glu",
    "overview": "Vilon is a synthetic dipeptide (lysine-glutamic acid) developed as part of Vladimir Khavinson's work on short peptide bioregulators. It is commonly cited in that literature for context on immune regulation and geroprotection. The evidence base is almost entirely non-Western, older, and based on animal and small human studies; independent replication is limited, so claims should be read cautiously.",
    "mechanism": [
      "Proposed to act as a peptide bioregulator influencing gene expression.",
      "Reported effects on T-cell and immune function in Russian studies.",
      "Suggested to interact with DNA and modulate cell activity.",
      "Described as geroprotective, though mechanisms are not firmly established."
    ],
    "researchedUses": [
      "Immune modulation in Russian research",
      "Age-related decline studies in animals",
      "General geroprotection research",
      "Adjunct investigation in inflammatory conditions"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~5-10 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Supplied as a lyophilized powder in the original formulations."
    },
    "halfLife": "Very short; small peptides of this type clear rapidly.",
    "reconstitution": "Commonly a 10 mg vial; 10 mg in 2 mL gives 5 mg/mL, so a 2 mg dose is about 40 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical outside its origin countries.",
      "Human evidence is limited and largely non-Western.",
      "Long-term safety is not established.",
      "Source purity varies."
    ],
    "calcPreset": { vialMg: 10, waterMl: 2, dose: 2, doseUnit: 'mg' },
    "type": "peptide"
  },
  {
    "slug": "cortagen",
    "name": "Cortagen",
    "aka": [
      "Cortagen bioregulator",
      "Cortical peptide"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "bioregulator",
      "peptide",
      "neuroprotective",
      "brain",
      "khavinson"
    ],
    "summary": "A short peptide bioregulator from Russian research, studied for effects on brain cortex tissue and neural function.",
    "sequence": "Ala-Glu-Asp-Pro",
    "overview": "Cortagen is a short synthetic peptide bioregulator developed within Khavinson's program and associated with the brain cortex. It is commonly cited in the Russian research literature for context on neuroprotection and recovery of nerve function. As with the other bioregulators, human evidence is limited, largely non-Western, and not widely replicated by independent groups.",
    "mechanism": [
      "Proposed to act as a tissue-specific peptide bioregulator for neural tissue.",
      "Reported to influence nerve regeneration in animal studies.",
      "Suggested to modulate gene expression relevant to brain cells.",
      "Described as supporting antioxidant and neurotrophic processes."
    ],
    "researchedUses": [
      "Peripheral nerve recovery in animal research",
      "Neuroprotection studies",
      "Cognitive and neural function research",
      "Recovery after neural injury (preclinical)"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~2-4 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Provided as a lyophilized powder in original formulations."
    },
    "halfLife": "Very short; rapidly cleared.",
    "reconstitution": "Commonly a 5 mg vial; 5 mg in 2 mL gives 2.5 mg/mL, so a 2 mg dose is about 16 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical outside its origin countries.",
      "Human evidence is limited and largely non-Western.",
      "Long-term safety is not established.",
      "Source purity and identity vary."
    ],
    "calcPreset": { vialMg: 5, waterMl: 2, dose: 1000, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "vesugen",
    "name": "Vesugen",
    "aka": [
      "Vesugen bioregulator",
      "Vascular peptide"
    ],
    "category": "Longevity",
    "tags": [
      "bioregulator",
      "tripeptide",
      "vascular",
      "aging",
      "khavinson"
    ],
    "summary": "A short synthetic tripeptide bioregulator from Russian research, studied for vascular tissue support.",
    "sequence": "Lys-Glu-Asp",
    "overview": "Vesugen is a synthetic tripeptide (lysine-glutamic acid-aspartic acid) developed as a vascular-directed bioregulator in Khavinson's peptide program. It is commonly cited in that literature for context on the health of blood vessel walls and age-related vascular changes. The supporting evidence is largely non-Western, limited, and not broadly replicated, so it should be viewed as preliminary.",
    "mechanism": [
      "Proposed to act as a tissue-specific bioregulator for vascular cells.",
      "Reported to influence endothelial cell function in Russian studies.",
      "Suggested to modulate expression of genes relevant to vessel maintenance.",
      "Described as supporting normal vascular tone and structure."
    ],
    "researchedUses": [
      "Vascular aging research",
      "Endothelial function studies",
      "Circulatory support in Russian reports",
      "General geroprotection research"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~2-5 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Supplied as a lyophilized powder in original formulations."
    },
    "halfLife": "Very short; small peptides clear rapidly.",
    "reconstitution": "Commonly a 5 mg vial; 5 mg in 2 mL gives 2.5 mg/mL, so a 2 mg dose is about 16 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical outside its origin countries.",
      "Human evidence is limited and largely non-Western.",
      "Long-term safety is not established.",
      "Source purity varies."
    ],
    "calcPreset": { vialMg: 5, waterMl: 2, dose: 1000, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "cardiogen",
    "name": "Cardiogen",
    "aka": [
      "Cardiogen bioregulator",
      "Cardiac peptide"
    ],
    "category": "Longevity",
    "tags": [
      "bioregulator",
      "peptide",
      "heart",
      "aging",
      "khavinson"
    ],
    "summary": "A short peptide bioregulator from Russian research, studied for effects related to heart muscle tissue.",
    "sequence": "Ala-Glu-Asp-Arg",
    "overview": "Cardiogen is a short synthetic peptide bioregulator associated with heart tissue in Khavinson's peptide research program. It is commonly cited in that literature for context on cardiac cell function and age-related changes in the heart. The evidence base is largely non-Western, limited, and not widely replicated, so it should be treated as preliminary.",
    "mechanism": [
      "Proposed to act as a tissue-specific bioregulator for cardiac cells.",
      "Reported to influence myocardial cell function in animal studies.",
      "Suggested to modulate expression of genes relevant to heart tissue.",
      "Described as supporting normal cardiac cell maintenance."
    ],
    "researchedUses": [
      "Cardiac aging research",
      "Myocardial function studies",
      "Cardiovascular support in Russian reports",
      "General geroprotection research"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~2-5 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Supplied as a lyophilized powder in original formulations."
    },
    "halfLife": "Very short; rapidly cleared.",
    "reconstitution": "Commonly a 5 mg vial; 5 mg in 2 mL gives 2.5 mg/mL, so a 2 mg dose is about 16 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical outside its origin countries.",
      "Human evidence is limited and largely non-Western.",
      "Long-term safety is not established.",
      "Source purity and identity vary."
    ],
    "calcPreset": { vialMg: 5, waterMl: 2, dose: 1000, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "bronchogen",
    "name": "Bronchogen",
    "aka": [
      "Bronchogen bioregulator",
      "Bronchial peptide"
    ],
    "category": "Immune & respiratory",
    "tags": [
      "bioregulator",
      "tetrapeptide",
      "respiratory",
      "lung",
      "khavinson"
    ],
    "summary": "A short synthetic tetrapeptide bioregulator from Russian research, studied for bronchial and respiratory tissue support.",
    "sequence": "Ala-Asp-Glu-Leu",
    "overview": "Bronchogen is a synthetic tetrapeptide bioregulator directed at bronchial tissue within Khavinson's peptide research program. It is commonly cited in that literature for context on respiratory cell function and chronic airway conditions. The supporting evidence is largely non-Western, limited in scale, and not broadly replicated, so it should be viewed as preliminary.",
    "mechanism": [
      "Proposed to act as a tissue-specific bioregulator for bronchial cells.",
      "Reported to influence respiratory epithelial function in Russian studies.",
      "Suggested to modulate expression of genes relevant to airway tissue.",
      "Described as supporting normal bronchial cell maintenance."
    ],
    "researchedUses": [
      "Chronic respiratory condition research",
      "Bronchial tissue function studies",
      "Respiratory recovery in Russian reports",
      "General geroprotection research"
    ],
    "protocol": {
      "route": "Intramuscular injection or intranasal (research)",
      "dose": "~2-5 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Also described in intranasal formulations in some sources."
    },
    "halfLife": "Very short; small peptides clear rapidly.",
    "reconstitution": "Commonly a 5 mg vial; 5 mg in 2 mL gives 2.5 mg/mL, so a 2 mg dose is about 16 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical outside its origin countries.",
      "Human evidence is limited and largely non-Western.",
      "Long-term safety is not established.",
      "Source purity varies."
    ],
    "calcPreset": { vialMg: 5, waterMl: 2, dose: 1000, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "ovagen",
    "name": "Ovagen",
    "aka": [
      "Ovagen bioregulator",
      "Liver-intestine peptide"
    ],
    "category": "Longevity",
    "tags": [
      "bioregulator",
      "tripeptide",
      "liver",
      "intestine",
      "khavinson"
    ],
    "summary": "A short synthetic tripeptide bioregulator from Russian research, studied for liver and intestinal tissue support.",
    "sequence": "Glu-Asp-Leu",
    "overview": "Ovagen is a synthetic tripeptide bioregulator associated with liver and intestinal tissue in Khavinson's peptide research program. It is commonly cited in that literature for context on protein synthesis and function in these organs. As with the other bioregulators, the evidence is largely non-Western, limited, and not widely replicated, so it should be regarded as preliminary.",
    "mechanism": [
      "Proposed to act as a tissue-specific bioregulator for liver and gut cells.",
      "Reported to influence protein synthesis in hepatic and intestinal tissue.",
      "Suggested to modulate expression of genes relevant to these organs.",
      "Described as supporting normal liver and intestinal cell function."
    ],
    "researchedUses": [
      "Liver function research",
      "Intestinal tissue support studies",
      "Digestive and detoxification research in Russian reports",
      "General geroprotection research"
    ],
    "protocol": {
      "route": "Intramuscular injection (research)",
      "dose": "~2-5 mg per day",
      "frequency": "Once daily over short courses in reported use",
      "notes": "Supplied as a lyophilized powder in original formulations."
    },
    "halfLife": "Very short; small peptides clear rapidly.",
    "reconstitution": "Commonly a 5 mg vial; 5 mg in 2 mL gives 2.5 mg/mL, so a 2 mg dose is about 16 units on a U-100 syringe.",
    "cautions": [
      "Not FDA-approved; a research chemical outside its origin countries.",
      "Human evidence is limited and largely non-Western.",
      "Long-term safety is not established.",
      "Source purity and identity vary."
    ],
    "calcPreset": { vialMg: 5, waterMl: 2, dose: 1000, doseUnit: 'mcg' },
    "type": "peptide"
  },
  {
    "slug": "argireline",
    "name": "Argireline (Acetyl Hexapeptide-8)",
    "aka": [
      "Acetyl Hexapeptide-3",
      "Acetyl Hexapeptide-8"
    ],
    "category": "Skin & cosmetic",
    "tags": [
      "cosmetic",
      "topical",
      "wrinkle",
      "skincare",
      "peptide"
    ],
    "summary": "A topical cosmetic peptide studied for the appearance of expression lines.",
    "sequence": "Acetylated hexapeptide (N-terminally acetylated, amidated).",
    "overview": "Argireline is a synthetic hexapeptide used in leave-on cosmetic formulations. It is marketed for the look of fine expression lines around the eyes and forehead. Evidence comes largely from manufacturer and small topical studies; it is a cosmetic ingredient rather than a medical treatment.",
    "mechanism": [
      "Proposed to interfere with components of the neurotransmitter-release complex in skin.",
      "Marketed to reduce the appearance of repeated facial-muscle movement.",
      "Acts at the level of the skin surface as a cosmetic conditioning agent."
    ],
    "researchedUses": [
      "Appearance of forehead and periorbital expression lines",
      "Cosmetic anti-aging formulations",
      "Topical skin-smoothing serums and creams"
    ],
    "protocol": {
      "route": "Topical",
      "dose": "Commonly formulated around 5-10% in leave-on cosmetics",
      "frequency": "Typically applied once or twice daily",
      "notes": "Applied to clean skin as part of a serum or cream; a cosmetic, not an injectable."
    },
    "halfLife": "Not typically characterized for topical cosmetic use.",
    "reconstitution": "Not applicable - this is a topical cosmetic ingredient, so injection unit math does not apply.",
    "cautions": [
      "A cosmetic ingredient, not a medical treatment.",
      "Evidence for wrinkle reduction is limited and largely industry-sourced.",
      "May cause local irritation in sensitive individuals; patch testing is common."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "matrixyl",
    "name": "Matrixyl (Palmitoyl Pentapeptide-4)",
    "aka": [
      "Palmitoyl Pentapeptide-4",
      "Pal-KTTKS"
    ],
    "category": "Skin & cosmetic",
    "tags": [
      "cosmetic",
      "topical",
      "collagen",
      "skincare",
      "peptide"
    ],
    "summary": "A topical cosmetic peptide studied for collagen-related skin appearance.",
    "sequence": "Palmitoylated pentapeptide (palmitoyl-Lys-Thr-Thr-Lys-Ser).",
    "overview": "Matrixyl is a lipidated pentapeptide used in leave-on skincare. It is marketed to support the appearance of firmer, smoother skin. The palmitoyl chain is intended to aid skin penetration. It is a cosmetic ingredient, not a medical therapy.",
    "mechanism": [
      "Proposed to signal fibroblasts to support collagen-related activity.",
      "The palmitoyl group is intended to improve delivery through the skin barrier.",
      "Acts as a cosmetic conditioning agent at the skin surface."
    ],
    "researchedUses": [
      "Appearance of fine lines and skin firmness",
      "Cosmetic anti-aging serums and moisturizers",
      "Formulations targeting skin texture"
    ],
    "protocol": {
      "route": "Topical",
      "dose": "Often formulated around 3-8% of a matrix concentrate in leave-on products",
      "frequency": "Typically applied once or twice daily",
      "notes": "Blended into serums or creams; not used by injection."
    },
    "halfLife": "Not typically characterized for topical cosmetic use.",
    "reconstitution": "Not applicable - this is a topical cosmetic ingredient, so injection unit math does not apply.",
    "cautions": [
      "A cosmetic ingredient, not a medical treatment.",
      "Clinical evidence is limited and often manufacturer-sponsored.",
      "May cause irritation in sensitive skin; patch testing is common."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "palmitoyl-tripeptide-1",
    "name": "Palmitoyl Tripeptide-1",
    "aka": [
      "Pal-GHK",
      "Palmitoyl Oligopeptide"
    ],
    "category": "Skin & cosmetic",
    "tags": [
      "cosmetic",
      "topical",
      "collagen",
      "skincare",
      "peptide"
    ],
    "summary": "A topical cosmetic peptide studied for skin firmness and tone appearance.",
    "sequence": "Palmitoylated tripeptide (palmitoyl-Gly-His-Lys).",
    "overview": "Palmitoyl Tripeptide-1 is a lipidated tripeptide often paired with Palmitoyl Tetrapeptide-7 in cosmetic blends. It is marketed to support the look of firmer, more even-toned skin. It is a cosmetic ingredient rather than a medical treatment.",
    "mechanism": [
      "Proposed to signal skin cells toward collagen-related support.",
      "The palmitoyl chain is intended to enhance skin penetration.",
      "Functions as a cosmetic conditioning agent at the surface of the skin."
    ],
    "researchedUses": [
      "Appearance of skin firmness and elasticity",
      "Cosmetic anti-aging and tone-evening formulations",
      "Serums and creams targeting fine lines"
    ],
    "protocol": {
      "route": "Topical",
      "dose": "Typically formulated at low percentages (often well under 1% active) in leave-on products",
      "frequency": "Typically applied once or twice daily",
      "notes": "Commonly combined with other peptides in cosmetic serums; not injected."
    },
    "halfLife": "Not typically characterized for topical cosmetic use.",
    "reconstitution": "Not applicable - this is a topical cosmetic ingredient, so injection unit math does not apply.",
    "cautions": [
      "A cosmetic ingredient, not a medical treatment.",
      "Supporting evidence is limited and often industry-sourced.",
      "May cause local irritation in sensitive individuals; patch testing is common."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "glycine",
    "name": "Glycine",
    "aka": [
      "Aminoacetic acid"
    ],
    "category": "Sleep & stress",
    "tags": [
      "amino acid",
      "sleep",
      "oral",
      "supplement",
      "glycine"
    ],
    "summary": "A simple amino acid taken orally and studied in relation to sleep quality.",
    "sequence": "Amino acid.",
    "overview": "Glycine is the smallest amino acid and a normal component of dietary protein and body tissues. As a supplement it has been studied for subjective sleep quality when taken before bed. It also serves as a building block for collagen and other molecules.",
    "mechanism": [
      "Acts as an inhibitory neurotransmitter in parts of the nervous system.",
      "May influence body temperature regulation around sleep onset.",
      "Serves as a substrate for collagen and glutathione synthesis."
    ],
    "researchedUses": [
      "Subjective sleep quality when taken before bed",
      "General amino-acid and collagen support",
      "Studied as a component of glutathione production"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~3 g before bedtime as a supplement",
      "frequency": "Once daily, often in the evening",
      "notes": "Taken as a powder or capsule; individual needs vary."
    },
    "halfLife": "Short; cleared over a few hours.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "High doses may cause mild digestive upset in some people.",
      "Those with kidney or liver conditions should be cautious and seek guidance."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "l-glutamine",
    "name": "L-Glutamine",
    "aka": [
      "Glutamine"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "amino acid",
      "recovery",
      "gut",
      "oral",
      "supplement"
    ],
    "summary": "A conditionally essential amino acid taken orally and studied for gut and recovery support.",
    "sequence": "Amino acid.",
    "overview": "L-Glutamine is the most abundant free amino acid in the body and a fuel source for intestinal and immune cells. As a supplement it is studied in the context of gut lining support and recovery during periods of stress, though results in healthy people are mixed.",
    "mechanism": [
      "Serves as a primary fuel for intestinal and immune cells.",
      "Acts as a nitrogen donor in various metabolic pathways.",
      "May support maintenance of the intestinal barrier under stress."
    ],
    "researchedUses": [
      "Gut lining support during illness or stress",
      "Recovery support in athletic and clinical settings",
      "Nitrogen source in metabolically demanding states"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~5 g per serving as a supplement",
      "frequency": "Once or several times daily depending on goals",
      "notes": "Taken as a powder or capsule; body normally produces its own."
    },
    "halfLife": "Short; turned over rapidly in the body.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "High doses may cause digestive upset in some people.",
      "Those with liver or kidney conditions should seek guidance before use."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "l-arginine",
    "name": "L-Arginine",
    "aka": [
      "Arginine"
    ],
    "category": "Energy & metabolism",
    "tags": [
      "amino acid",
      "nitric oxide",
      "circulation",
      "oral",
      "supplement"
    ],
    "summary": "An amino acid taken orally and studied as a nitric-oxide precursor for circulation.",
    "sequence": "Amino acid.",
    "overview": "L-Arginine is a conditionally essential amino acid and a precursor to nitric oxide, a signaling molecule involved in blood-vessel dilation. It is studied for circulation and exercise contexts, though absorption can be variable when taken orally.",
    "mechanism": [
      "Serves as a substrate for nitric-oxide synthesis.",
      "Nitric oxide contributes to blood-vessel dilation.",
      "Participates in the urea cycle and protein synthesis."
    ],
    "researchedUses": [
      "Circulation and blood-flow support",
      "Exercise performance contexts",
      "General nitric-oxide precursor supplementation"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~3-6 g per day as a supplement",
      "frequency": "Once daily or divided doses",
      "notes": "Oral absorption is variable; some prefer citrulline for this reason."
    },
    "halfLife": "Short; cleared over a few hours.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "High doses may cause digestive upset or lower blood pressure.",
      "Those on blood-pressure medication or with heart conditions should seek guidance."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "l-citrulline",
    "name": "L-Citrulline",
    "aka": [
      "Citrulline",
      "Citrulline Malate"
    ],
    "category": "Energy & metabolism",
    "tags": [
      "amino acid",
      "nitric oxide",
      "endurance",
      "oral",
      "supplement"
    ],
    "summary": "An amino acid taken orally and studied for nitric oxide and exercise endurance.",
    "sequence": "Amino acid.",
    "overview": "L-Citrulline is a non-protein amino acid that the body converts to arginine, raising nitric-oxide availability. It is often studied for exercise endurance and blood flow, and is thought to raise arginine levels more effectively than oral arginine itself.",
    "mechanism": [
      "Converted to arginine in the kidneys, raising arginine levels.",
      "Indirectly supports nitric-oxide production and vessel dilation.",
      "May aid clearance of ammonia via the urea cycle."
    ],
    "researchedUses": [
      "Exercise endurance and reduced fatigue",
      "Blood-flow and circulation support",
      "Nitric-oxide precursor supplementation"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~6-8 g of citrulline malate before exercise",
      "frequency": "Once daily, often pre-workout",
      "notes": "Sold as pure citrulline or as citrulline malate; taken as powder or capsule."
    },
    "halfLife": "Short; cleared over a few hours.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "High doses may cause mild digestive upset in some people.",
      "Those on blood-pressure medication should seek guidance before use."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "nac",
    "name": "NAC (N-Acetyl Cysteine)",
    "aka": [
      "N-Acetylcysteine",
      "N-Acetyl-L-Cysteine"
    ],
    "category": "Longevity & cellular energy",
    "tags": [
      "amino acid",
      "antioxidant",
      "glutathione",
      "oral",
      "supplement"
    ],
    "summary": "A modified amino acid taken orally and studied as a glutathione and antioxidant precursor.",
    "sequence": "Acetylated amino acid (N-acetyl derivative of cysteine).",
    "overview": "NAC is an acetylated form of the amino acid cysteine and a precursor to glutathione, a major cellular antioxidant. It has an established medical role in certain clinical settings and is also studied as a supplement for antioxidant and respiratory support.",
    "mechanism": [
      "Provides cysteine as a rate-limiting substrate for glutathione synthesis.",
      "Acts directly as a free-radical scavenger.",
      "May help thin mucus by breaking disulfide bonds."
    ],
    "researchedUses": [
      "Glutathione and antioxidant support",
      "Respiratory and mucus-clearance contexts",
      "General cellular-stress research"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~600-1200 mg per day as a supplement",
      "frequency": "Once or twice daily",
      "notes": "Taken as a capsule; has an established clinical use under medical supervision."
    },
    "halfLife": "Short; cleared over a few hours.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment in over-the-counter form.",
      "High doses may cause nausea or digestive upset.",
      "Regulatory status has varied; those with conditions should seek guidance."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "beta-alanine",
    "name": "Beta-Alanine",
    "aka": [
      "β-Alanine"
    ],
    "category": "Energy & metabolism",
    "tags": [
      "amino acid",
      "endurance",
      "carnosine",
      "oral",
      "supplement"
    ],
    "summary": "A non-essential amino acid taken orally and studied for muscular endurance.",
    "sequence": "Amino acid.",
    "overview": "Beta-Alanine is a non-essential amino acid that combines with histidine to form carnosine in muscle. As a supplement it is studied for buffering muscle acidity during high-intensity exercise. A harmless tingling sensation is a common side effect.",
    "mechanism": [
      "Serves as the rate-limiting precursor for muscle carnosine.",
      "Carnosine helps buffer hydrogen ions during intense effort.",
      "May delay muscular fatigue in short, high-intensity exercise."
    ],
    "researchedUses": [
      "Muscular endurance in high-intensity exercise",
      "Raising muscle carnosine content",
      "Exercise-performance research"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~3-6 g per day as a supplement",
      "frequency": "Divided into smaller doses to limit tingling",
      "notes": "Effects build over weeks of consistent use; taken as powder or capsule."
    },
    "halfLife": "Short; cleared over a few hours, though muscle carnosine accumulates.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "Commonly causes harmless skin tingling (paresthesia) at higher single doses.",
      "High doses may cause digestive discomfort in some people."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "creatine",
    "name": "Creatine",
    "aka": [
      "Creatine Monohydrate"
    ],
    "category": "Growth & muscle",
    "tags": [
      "amino acid",
      "strength",
      "muscle",
      "oral",
      "supplement"
    ],
    "summary": "An amino-acid-derived compound taken orally and widely studied for strength and muscle.",
    "sequence": "Amino acid derivative (synthesized from glycine, arginine, and methionine).",
    "overview": "Creatine is a compound made from amino acids and stored in muscle as phosphocreatine, an energy reserve for short bursts of effort. It is among the most studied sports supplements, with consistent evidence for strength and muscle-performance support.",
    "mechanism": [
      "Stored as phosphocreatine to rapidly regenerate ATP.",
      "Supports high-intensity, short-duration energy output.",
      "May draw water into muscle cells, aiding cell volume."
    ],
    "researchedUses": [
      "Strength and power output",
      "Muscle mass support alongside training",
      "Studied in cognitive and general-health contexts"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~3-5 g per day as a maintenance dose",
      "frequency": "Once daily; some use a short higher-dose loading phase",
      "notes": "Monohydrate is the most studied form; taken as powder or capsule."
    },
    "halfLife": "Short in blood; muscle stores build and deplete over weeks.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "May cause minor water-weight gain or digestive upset in some people.",
      "Those with kidney conditions should seek guidance before use."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "l-lysine",
    "name": "L-Lysine",
    "aka": [
      "Lysine"
    ],
    "category": "Immune support",
    "tags": [
      "amino acid",
      "immune",
      "essential",
      "oral",
      "supplement"
    ],
    "summary": "An essential amino acid taken orally and studied in immune and cold-sore contexts.",
    "sequence": "Amino acid.",
    "overview": "L-Lysine is an essential amino acid obtained from diet and used for protein and collagen formation. As a supplement it is most studied in the context of recurrent cold sores, and it also supports calcium handling and general amino-acid balance.",
    "mechanism": [
      "Serves as a building block for protein and collagen synthesis.",
      "May counter arginine, which certain viruses use to replicate.",
      "Involved in calcium absorption and carnitine formation."
    ],
    "researchedUses": [
      "Frequency and severity of recurrent cold sores",
      "General protein and collagen support",
      "Amino-acid balance in restricted diets"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~1-3 g per day as a supplement",
      "frequency": "Once daily or divided doses",
      "notes": "Taken as a capsule or tablet; dietary intake usually covers basic needs."
    },
    "halfLife": "Short; cleared over a few hours.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "High doses may cause digestive upset in some people.",
      "Those with kidney conditions should seek guidance before use."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "carnosine",
    "name": "Carnosine",
    "aka": [
      "L-Carnosine",
      "Beta-alanyl-L-histidine"
    ],
    "category": "Longevity & cellular energy",
    "tags": [
      "dipeptide",
      "antioxidant",
      "glycation",
      "oral",
      "supplement"
    ],
    "summary": "A dipeptide taken orally and studied for antioxidant and anti-glycation properties.",
    "sequence": "Dipeptide (beta-alanyl-L-histidine).",
    "overview": "Carnosine is a dipeptide of beta-alanine and histidine found naturally in muscle and brain tissue. As a supplement it is studied for antioxidant, pH-buffering, and anti-glycation activity, though oral carnosine is partly broken down during digestion.",
    "mechanism": [
      "Acts as a buffer against acidity in muscle tissue.",
      "Scavenges reactive oxygen species as an antioxidant.",
      "May bind reactive carbonyls, limiting glycation of proteins."
    ],
    "researchedUses": [
      "Antioxidant and anti-glycation research",
      "Muscle pH buffering",
      "General cellular-aging contexts"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly ~500-1000 mg per day as a supplement",
      "frequency": "Once or twice daily",
      "notes": "Partly degraded during digestion; some prefer beta-alanine to raise muscle carnosine."
    },
    "halfLife": "Short; oral carnosine is rapidly broken down by carnosinase.",
    "reconstitution": "Not applicable - this is an oral supplement, so injection unit math does not apply.",
    "cautions": [
      "A dietary supplement, not a medical treatment.",
      "High doses may cause mild digestive upset in some people.",
      "Human evidence for many claimed benefits remains limited."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "glow",
    "name": "GLOW",
    "aka": [
      "GLOW blend",
      "GHK-Cu / BPC-157 / TB-500"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "blend",
      "healing",
      "skin",
      "recovery"
    ],
    "summary": "A popular recovery-and-skin blend combining GHK-Cu, BPC-157 and TB-500 in a single vial.",
    "sequence": "Blend — not a single peptide (GHK-Cu + BPC-157 + TB-500).",
    "overview": "GLOW is a marketing name for a fixed combination of three widely discussed peptides: the copper peptide GHK-Cu plus the repair peptides BPC-157 and TB-500. It is sold pre-mixed so one injection delivers all three, and is talked about mainly for skin quality and general recovery. Exact ratios vary by supplier, and no combination product like this is an approved medicine.",
    "mechanism": [
      "GHK-Cu is studied for collagen synthesis, skin remodeling and antioxidant signaling.",
      "BPC-157 is studied for soft-tissue and gut repair, partly via angiogenesis.",
      "TB-500 (a thymosin beta-4 fragment) is studied for cell migration and systemic recovery."
    ],
    "researchedUses": [
      "Skin appearance and elasticity (GHK-Cu component)",
      "General soft-tissue recovery",
      "Wound-healing support in animal models"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "Dosed by the vial's per-component mg content",
      "frequency": "Often daily in discussed protocols",
      "notes": "A fixed blend cannot be adjusted per component — separate vials allow independent dosing."
    },
    "halfLife": "Varies by component; generally short (hours) for each peptide.",
    "reconstitution": "Sold as a multi-peptide vial. Use our blend calculator to work out the units for each component after adding bacteriostatic water.",
    "cautions": [
      "None of these peptides are FDA-approved; a fixed blend removes the ability to adjust one component.",
      "Human data are limited for each component and essentially absent for the combination.",
      "Actual content and purity of blended research vials vary widely by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "klow",
    "name": "KLOW",
    "aka": [
      "KLOW blend",
      "GHK-Cu / BPC-157 / TB-500 / KPV"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "blend",
      "healing",
      "anti-inflammatory",
      "skin"
    ],
    "summary": "The GLOW recovery trio (GHK-Cu, BPC-157, TB-500) with KPV added for its anti-inflammatory profile.",
    "sequence": "Blend — not a single peptide (GHK-Cu + BPC-157 + TB-500 + KPV).",
    "overview": "KLOW extends the GLOW blend by adding KPV, a short tripeptide fragment of alpha-MSH studied for calming inflammation. The idea marketed behind it is to pair tissue repair (BPC-157, TB-500), skin and collagen support (GHK-Cu), and an anti-inflammatory component (KPV) in one vial. As with any fixed blend, ratios vary by supplier and the combination is not an approved medicine.",
    "mechanism": [
      "GHK-Cu supports collagen and skin remodeling in research settings.",
      "BPC-157 and TB-500 are studied for complementary local and systemic tissue repair.",
      "KPV is studied for down-regulating inflammatory signaling, including in the gut."
    ],
    "researchedUses": [
      "Recovery paired with inflammatory-symptom support",
      "Skin and soft-tissue quality",
      "Gut-related inflammation (KPV component) in models"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "Dosed by the vial's per-component mg content",
      "frequency": "Often daily in discussed protocols",
      "notes": "Four peptides share one vial — the ratio is fixed."
    },
    "halfLife": "Varies by component; generally short for each.",
    "reconstitution": "Multi-peptide vial. Use the blend calculator to find units per component after reconstitution.",
    "cautions": [
      "No component is FDA-approved.",
      "Combination has essentially no human safety data.",
      "Blended-vial content and purity vary by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "klow-cu",
    "name": "KLOW-Cu",
    "aka": [
      "KLOW copper",
      "Copper KLOW"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "blend",
      "healing",
      "copper",
      "skin"
    ],
    "summary": "A copper-forward version of the KLOW blend, emphasizing the GHK-Cu (copper peptide) component.",
    "sequence": "Blend — not a single peptide (copper-emphasized KLOW).",
    "overview": "KLOW-Cu is a variant of the KLOW blend that leans more heavily on GHK-Cu, the copper-bound tripeptide associated with collagen and skin remodeling, alongside the usual BPC-157, TB-500 and KPV. It is marketed toward skin and cosmetic recovery. Like all blends here, composition is supplier-dependent and the product is not an approved medicine.",
    "mechanism": [
      "Higher GHK-Cu content for collagen synthesis and skin antioxidant signaling.",
      "BPC-157 / TB-500 for soft-tissue repair.",
      "KPV for anti-inflammatory signaling."
    ],
    "researchedUses": [
      "Skin quality and cosmetic recovery",
      "Soft-tissue repair",
      "Inflammation support in models"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "By the vial's per-component mg content",
      "frequency": "Often daily in discussed protocols",
      "notes": "Copper peptides can sting on injection; some users dilute further."
    },
    "halfLife": "Varies by component; short.",
    "reconstitution": "Multi-peptide vial — use the blend calculator per component.",
    "cautions": [
      "Not FDA-approved.",
      "Excess copper exposure is a theoretical concern with copper-heavy blends.",
      "Content and purity vary by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "zeus",
    "name": "ZEUS",
    "aka": [
      "Zeus blend"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "blend",
      "healing",
      "recovery"
    ],
    "summary": "A branded multi-peptide recovery blend, most often built around GHK-Cu, BPC-157 and TB-500.",
    "sequence": "Blend — not a single peptide; exact composition varies by supplier.",
    "overview": "ZEUS is a supplier brand name for a multi-peptide recovery blend. The most commonly cited make-up is the GHK-Cu / BPC-157 / TB-500 recovery trio, though some vendors vary the components or ratios. Because the exact formula is not standardized, always check the specific product label. It is not an approved medicine.",
    "mechanism": [
      "Combines collagen/skin support (GHK-Cu) with tissue-repair peptides (BPC-157, TB-500).",
      "Marketed for a broad, systemic recovery effect from one vial.",
      "Any effect depends on the actual components and their amounts."
    ],
    "researchedUses": [
      "General recovery and tissue repair",
      "Skin and connective-tissue support",
      "Convenience of a single combined vial"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "By the vial's stated per-component content",
      "frequency": "Often daily in discussed protocols",
      "notes": "Confirm the exact composition on the product before use."
    },
    "halfLife": "Varies by component; short.",
    "reconstitution": "Multi-peptide vial — use the blend calculator per component.",
    "cautions": [
      "Composition is not standardized across suppliers.",
      "No component is FDA-approved.",
      "Human data on the combination are absent."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "cagrisema",
    "name": "CagriSema",
    "aka": [
      "Cagrilintide + Semaglutide",
      "Cagri/Sema"
    ],
    "category": "Metabolic & weight",
    "tags": [
      "blend",
      "glp-1",
      "amylin",
      "weight"
    ],
    "summary": "An investigational fixed combination of the amylin analog cagrilintide with the GLP-1 agonist semaglutide.",
    "sequence": "Blend — not a single peptide (cagrilintide + semaglutide).",
    "overview": "CagriSema pairs cagrilintide (a long-acting amylin analog) with semaglutide (a GLP-1 receptor agonist), two appetite-regulating pathways in one weekly injection. It has been studied in clinical trials for weight management, but the combination is investigational and not an approved product; research-market versions are unregulated. Nothing here is a recommendation to use it.",
    "mechanism": [
      "Semaglutide activates GLP-1 receptors to increase satiety and slow gastric emptying.",
      "Cagrilintide mimics amylin, which also reduces appetite and food intake.",
      "The two are combined to act on complementary appetite pathways."
    ],
    "researchedUses": [
      "Investigational weight management in trials",
      "Appetite and blood-sugar regulation research"
    ],
    "protocol": {
      "route": "Subcutaneous injection, typically once weekly (trials)",
      "dose": "Trial dosing titrates upward; specific figures are protocol-dependent",
      "frequency": "Once weekly in studied regimens",
      "notes": "Investigational combination; not an established or approved regimen."
    },
    "halfLife": "Both components are long-acting (on the order of days).",
    "reconstitution": "Research versions ship as a combined lyophilized vial. Use the blend calculator to find units per component after reconstitution.",
    "cautions": [
      "Investigational combination; not FDA-approved.",
      "GLP-1/amylin agonists commonly cause nausea and GI effects.",
      "Unregulated research versions vary in content and purity."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "bpc-157-tb-500",
    "name": "BPC-157 / TB-500 Blend",
    "aka": [
      "BPC/TB",
      "BPC-157 + TB-500"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "blend",
      "healing",
      "tendon",
      "recovery"
    ],
    "summary": "The classic recovery pairing — BPC-157 and TB-500 combined in one vial for local plus systemic repair.",
    "sequence": "Blend — not a single peptide (BPC-157 + TB-500).",
    "overview": "This is the most commonly discussed peptide recovery stack sold pre-mixed: BPC-157, studied mainly for localized soft-tissue and gut repair, together with TB-500, studied for systemic cell migration and recovery. They act through different mechanisms, so they are framed as a \"local + systemic\" combination. Sold as a fixed blend, the ratio (often 1:1) cannot be adjusted.",
    "mechanism": [
      "BPC-157 is studied for local tissue protection and angiogenesis.",
      "TB-500 is studied for cell migration and systemic recovery via actin regulation.",
      "The pairing targets repair through complementary pathways."
    ],
    "researchedUses": [
      "Tendon, ligament and muscle recovery (animal models)",
      "General soft-tissue healing",
      "Post-injury recovery discussion"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "By the vial's per-component mg (e.g. 5 mg + 5 mg or 10 mg + 10 mg)",
      "frequency": "Often daily or several times weekly in discussed protocols",
      "notes": "A fixed blend locks the ratio; separate vials give more control."
    },
    "halfLife": "Short for BPC-157; TB-500 is somewhat longer.",
    "reconstitution": "Combined vial — reconstitute and use the blend calculator to find units for each peptide.",
    "cautions": [
      "Neither peptide is FDA-approved; both are banned by WADA.",
      "Human data are limited.",
      "Blend content and purity vary by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "cjc-1295-ipamorelin",
    "name": "CJC-1295 / Ipamorelin Blend",
    "aka": [
      "CJC/Ipa",
      "CJC-1295 + Ipamorelin"
    ],
    "category": "Growth-hormone axis",
    "tags": [
      "blend",
      "growth hormone",
      "ghrh",
      "ghrp"
    ],
    "summary": "A GHRH analog (CJC-1295) paired with a selective GHRP (Ipamorelin) — the most common GH-support blend.",
    "sequence": "Blend — not a single peptide (CJC-1295 without DAC + Ipamorelin).",
    "overview": "This blend combines CJC-1295 (usually the no-DAC / Mod-GRF form), which mimics growth-hormone-releasing hormone, with Ipamorelin, a selective growth-hormone secretagogue. The two are paired because they nudge growth-hormone release through different receptors at once, which is why they are discussed as synergistic. It is sold pre-mixed and is not an approved medicine.",
    "mechanism": [
      "CJC-1295 acts as a GHRH analog to raise baseline GH release.",
      "Ipamorelin triggers a clean GH pulse with minimal effect on cortisol or appetite.",
      "Together they are thought to produce a larger, more natural-shaped GH pulse."
    ],
    "researchedUses": [
      "Growth-hormone support research",
      "Recovery and body-composition discussion",
      "Sleep-quality anecdotes"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "Commonly cited around 100 mcg of each per dose",
      "frequency": "Often once daily, away from meals / before bed",
      "notes": "Timing away from food is commonly discussed for GH secretagogues."
    },
    "halfLife": "Ipamorelin is short (~2 h); no-DAC CJC-1295 is also short.",
    "reconstitution": "Combined vial — reconstitute and use the blend calculator to find units per component.",
    "cautions": [
      "Not FDA-approved; influences your own hormone release.",
      "Not a substitute for medically supervised GH therapy.",
      "Blend content and purity vary by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "tesamorelin-ipamorelin",
    "name": "Tesamorelin / Ipamorelin Blend",
    "aka": [
      "Tesa/Ipa",
      "Tesamorelin + Ipamorelin"
    ],
    "category": "Growth-hormone axis",
    "tags": [
      "blend",
      "growth hormone",
      "ghrh",
      "fat loss"
    ],
    "summary": "A GHRH analog studied for visceral fat (tesamorelin) paired with the GH secretagogue ipamorelin.",
    "sequence": "Blend — not a single peptide (Tesamorelin + Ipamorelin).",
    "overview": "This blend pairs tesamorelin — a stabilized GHRH analog that is the one FDA-approved GHRH-type peptide, used clinically for HIV-associated visceral fat — with ipamorelin, a selective GH secretagogue. Sold pre-mixed (often at a ratio like 12 mg : 3 mg), it is marketed for growth-hormone support and body composition. The blended research product itself is not an approved medicine even though tesamorelin as a standalone drug is.",
    "mechanism": [
      "Tesamorelin acts as a GHRH analog and has clinical data for reducing visceral adipose tissue.",
      "Ipamorelin adds a selective GH pulse through the ghrelin/GHS receptor.",
      "The pairing aims to combine steady GHRH drive with a pulsatile secretagogue."
    ],
    "researchedUses": [
      "Visceral fat reduction (tesamorelin component, clinical)",
      "Growth-hormone support",
      "Body-composition research"
    ],
    "protocol": {
      "route": "Subcutaneous injection",
      "dose": "By the vial's per-component mg (e.g. 12 mg tesamorelin / 3 mg ipamorelin)",
      "frequency": "Often once daily",
      "notes": "Tesamorelin as a standalone is prescription-only; the blend is a research product."
    },
    "halfLife": "Both components are relatively short-acting.",
    "reconstitution": "Combined vial — reconstitute and use the blend calculator to find units per component.",
    "cautions": [
      "The blended research product is not FDA-approved.",
      "GH-axis peptides affect endocrine function.",
      "Content and purity of research blends vary by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "lipo-extreme",
    "name": "Lipo Extreme",
    "aka": [
      "Lipotropic blend",
      "MIC injection"
    ],
    "category": "Fat loss & metabolism",
    "tags": [
      "blend",
      "lipotropic",
      "fat loss",
      "b12"
    ],
    "summary": "A lipotropic (\"fat-metabolizing\") injection blend — typically MIC (methionine, inositol, choline) with B12 and L-carnitine.",
    "sequence": "Blend — not a peptide; amino acids, vitamins and cofactors.",
    "overview": "Lipo Extreme is a marketing name for a concentrated lipotropic injection. These blends usually combine methionine, inositol and choline (together called \"MIC\") with vitamin B12 and often L-carnitine — compounds involved in fat metabolism and energy. They are popular in weight-management clinics, but the injectable blends are not FDA-approved drugs and evidence for fat-loss benefit is weak. Exact composition varies by supplier.",
    "mechanism": [
      "Methionine, inositol and choline are involved in lipid processing and liver fat handling.",
      "L-carnitine helps shuttle fatty acids into mitochondria for energy.",
      "Vitamin B12 supports energy metabolism and is added partly for the \"energy\" claim."
    ],
    "researchedUses": [
      "Marketed as an adjunct to diet for fat loss",
      "Energy support",
      "Liver fat metabolism (mechanistic rationale)"
    ],
    "protocol": {
      "route": "Intramuscular or subcutaneous injection",
      "dose": "Sold by concentration (e.g. 500 mg/mL); dosing is clinic-dependent",
      "frequency": "Often weekly in clinic settings",
      "notes": "Dosed in mg of the total blend, not insulin units."
    },
    "halfLife": "Component-dependent; water-soluble components clear over hours to days.",
    "reconstitution": "Usually supplied as a ready-to-use solution, so insulin-syringe unit math does not apply.",
    "cautions": [
      "Not an FDA-approved drug; fat-loss evidence is weak.",
      "Injection-site reactions are common.",
      "Composition and concentration vary by supplier."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "5-amino-1mq-nadh",
    "name": "5-Amino-1MQ + NADH",
    "aka": [
      "5-Amino-1MQ / NADH blend"
    ],
    "category": "Metabolic & mitochondrial",
    "tags": [
      "blend",
      "nnmt",
      "nad",
      "metabolic"
    ],
    "summary": "A metabolic blend pairing the NNMT inhibitor 5-Amino-1MQ with NADH to support cellular energy pathways.",
    "sequence": "Blend — not a peptide (5-Amino-1MQ small molecule + NADH coenzyme).",
    "overview": "5-Amino-1MQ is a small-molecule inhibitor of the enzyme NNMT, studied in animals for fat metabolism and for raising cellular NAD+. Pairing it with NADH — the reduced form of the NAD coenzyme — is marketed to support the same energy pathway from two angles. Both are research/supplement-grade compounds, not approved medicines, and human data are limited.",
    "mechanism": [
      "5-Amino-1MQ inhibits NNMT, which in animal studies preserves NAD+ and affects fat-cell metabolism.",
      "NADH is a coenzyme central to mitochondrial energy production.",
      "The blend targets cellular energy and metabolism from complementary directions."
    ],
    "researchedUses": [
      "Fat-cell metabolism research (5-Amino-1MQ)",
      "Cellular energy and NAD+ support",
      "Metabolic-health interest"
    ],
    "protocol": {
      "route": "Oral capsule is common for 5-Amino-1MQ; some blends are injectable",
      "dose": "Commonly cited around 50-150 mg/day for 5-Amino-1MQ",
      "frequency": "Once daily in discussed use",
      "notes": "Route and content vary by product."
    },
    "halfLife": "Not well established in humans.",
    "reconstitution": "If supplied as an oral product, injection unit math does not apply; injectable versions use the blend calculator.",
    "cautions": [
      "Research/supplement compounds; not FDA-approved as medicines.",
      "Human safety and efficacy data are limited.",
      "Content and purity vary by source."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "slu-pp-332",
    "name": "SLU-PP-332",
    "aka": [
      "SLU-PP-332 ERR agonist"
    ],
    "category": "Fat loss & metabolism",
    "tags": [
      "exercise mimetic",
      "metabolic",
      "fat loss",
      "research"
    ],
    "summary": "A research \"exercise-mimetic\" small molecule that activates estrogen-related receptors (ERRs) to boost fat metabolism.",
    "sequence": "Non-peptide small molecule (ERR-alpha/beta/gamma agonist).",
    "overview": "SLU-PP-332 is an experimental compound that activates the estrogen-related receptors (ERRs), master regulators of mitochondrial energy metabolism. In rodents it increased fat oxidation and endurance without exercise, earning the \"exercise mimetic\" label. It is purely a research chemical — there is no human data, no approved use, and no established human dose. Nothing here is a recommendation to use it.",
    "mechanism": [
      "Activates ERR-alpha, -beta and -gamma, up-regulating mitochondrial and oxidative-metabolism genes.",
      "In rodents, increased fatty-acid oxidation and running endurance.",
      "Effects on humans are unknown."
    ],
    "researchedUses": [
      "Fat-oxidation and endurance research (rodents)",
      "Metabolic-disease mechanism studies",
      "Interest as an exercise-mimetic concept"
    ],
    "protocol": {
      "route": "Injection in rodent research; human route not established",
      "dose": "No validated human dose exists; rodent studies use mg/kg figures",
      "frequency": "Not established",
      "notes": "Entirely experimental — no human protocol."
    },
    "halfLife": "Reported short in rodent studies; unknown in humans.",
    "reconstitution": "Handled as a research chemical; no standard human reconstitution.",
    "cautions": [
      "Purely experimental — no human safety data whatsoever.",
      "Not approved for human use anywhere.",
      "Long-term ERR activation effects are unknown."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "acetic-acid",
    "name": "Acetic Acid (reconstitution)",
    "aka": [
      "Dilute acetic acid",
      "AA solvent"
    ],
    "category": "Solvents & reconstitution",
    "tags": [
      "solvent",
      "reconstitution",
      "diluent"
    ],
    "summary": "A dilute acetic-acid solution used as a solvent to dissolve peptides that do not fully reconstitute in bacteriostatic water.",
    "sequence": "Not a peptide — a diluent/solvent (dilute acetic acid in water).",
    "overview": "Some peptides are poorly soluble in plain bacteriostatic water and dissolve better in a mildly acidic solution. Dilute acetic acid (often around 0.6%) is used as a first solvent to fully dissolve the powder, after which bacteriostatic water is typically added to reach the working volume. It is a preparation aid, not a therapeutic compound, and is included here for reference.",
    "mechanism": [
      "Lowers pH slightly, improving solubility of certain hydrophobic or aggregation-prone peptides.",
      "Used in a small amount to dissolve the powder before diluting to final volume.",
      "Does not have a biological \"dose\" of its own."
    ],
    "researchedUses": [
      "Dissolving hard-to-reconstitute peptides",
      "Improving clarity of a reconstituted solution",
      "Standard laboratory peptide handling"
    ],
    "protocol": {
      "route": "Not injected on its own — a solvent used during reconstitution",
      "dose": "A small volume to dissolve the powder, then diluted with bacteriostatic water",
      "frequency": "N/A",
      "notes": "Follow the specific peptide’s handling notes; only very dilute solutions are used."
    },
    "halfLife": "Not applicable.",
    "reconstitution": "Acetic acid IS a reconstitution solvent — used to dissolve the peptide before adding bacteriostatic water; unit math applies to the final peptide solution, not the acid.",
    "cautions": [
      "A preparation aid, not a treatment — never a \"dose\" on its own.",
      "Only very dilute, appropriate-grade solutions are suitable.",
      "Excess acid can degrade sensitive peptides or sting on injection."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "somatropin",
    "name": "Somatropin (HGH)",
    "aka": [
      "Recombinant human growth hormone",
      "rhGH",
      "Human growth hormone"
    ],
    "category": "Growth-hormone axis",
    "tags": [
      "growth-hormone",
      "recombinant",
      "igf-1",
      "metabolism"
    ],
    "summary": "A recombinant form of human growth hormone commonly cited in the research literature for context on the somatotropic axis.",
    "sequence": "Recombinant 191-residue protein identical to endogenous human growth hormone.",
    "overview": "Somatropin is a laboratory-produced version of the 22 kDa human growth hormone. It is discussed in the literature in the context of growth-hormone deficiency and body-composition research. In many jurisdictions it is a prescription medicine, and non-medical use is a frequent topic of caution.",
    "mechanism": [
      "Binds the growth-hormone receptor to trigger intracellular signaling.",
      "Stimulates hepatic production of IGF-1, which mediates many downstream effects.",
      "Influences protein, lipid, and carbohydrate metabolism.",
      "Acts on growth plates and multiple tissues during development."
    ],
    "researchedUses": [
      "Growth-hormone deficiency in clinical settings",
      "Body-composition and lean-mass research",
      "Recovery from certain catabolic states",
      "Metabolic studies of lipolysis and glucose handling"
    ],
    "protocol": {
      "route": "Subcutaneous injection (clinical)",
      "dose": "Commonly cited around 1-3 IU per day in research contexts",
      "frequency": "Typically once daily, often evening",
      "notes": "Dosed in international units (IU); clinical dosing is individualized and supervised."
    },
    "halfLife": "Short in circulation - roughly a few hours - though biological effects persist longer via IGF-1.",
    "reconstitution": "Supplied as a lyophilized powder reconstituted with the accompanying diluent or bacteriostatic water. Because it is dosed in IU rather than a fixed mg target, standard peptide unit math does not map cleanly; follow the product's IU-per-vial labeling.",
    "cautions": [
      "Prescription medicine in many countries; non-medical use is generally restricted or prohibited.",
      "Associated in the literature with fluid retention, joint discomfort, and effects on glucose tolerance.",
      "Purity and potency vary widely in non-pharmaceutical sources.",
      "Not intended as medical advice; use only under qualified supervision."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "ghk-basic",
    "name": "GHK (Copper-Free)",
    "aka": [
      "Glycyl-L-histidyl-L-lysine",
      "GHK tripeptide"
    ],
    "category": "Skin, hair & regeneration",
    "tags": [
      "skin",
      "tripeptide",
      "regeneration",
      "collagen"
    ],
    "summary": "The GHK tripeptide without its copper ion, commonly cited in the research literature for context on skin and tissue signaling.",
    "sequence": "Gly-His-Lys",
    "overview": "GHK is a naturally occurring tripeptide found in human plasma that declines with age. The copper-free form is the base peptide without the bound copper(II) ion of its better-known complex. It is studied for gene-expression modulation and tissue remodeling, with much of the data from cell and animal models.",
    "mechanism": [
      "Appears to modulate expression of genes involved in tissue remodeling.",
      "May influence collagen and extracellular-matrix synthesis.",
      "Reported antioxidant and anti-inflammatory signaling in vitro.",
      "Copper-free form lacks the copper delivery attributed to the GHK-Cu complex."
    ],
    "researchedUses": [
      "Skin-appearance and firmness research",
      "Wound-healing and tissue-repair models",
      "Hair-follicle and scalp studies",
      "General regenerative signaling investigations"
    ],
    "protocol": {
      "route": "Subcutaneous injection or topical (research)",
      "dose": "~1-2 mg per day in cited research contexts",
      "frequency": "Once daily or as studied",
      "notes": "Often compared against the copper-bound GHK-Cu form."
    },
    "halfLife": "Short - on the order of minutes to a few hours in circulation.",
    "reconstitution": "A common presentation is a 50 mg vial; 50 mg in 5 mL gives 10 mg/mL, so a 2 mg amount is about 20 units on a U-100 syringe when injected.",
    "cautions": [
      "Not FDA-approved for therapeutic use.",
      "Human clinical data are limited relative to cosmetic claims.",
      "Source purity varies.",
      "Educational information only, not a recommendation to use."
    ],
    "calcPreset": {
      "vialMg": 50,
      "waterMl": 5,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "tesofensine",
    "name": "Tesofensine",
    "aka": [
      "NS2330"
    ],
    "category": "Metabolic & weight",
    "tags": [
      "weight",
      "appetite",
      "monoamine",
      "investigational"
    ],
    "summary": "An oral, non-peptide triple monoamine reuptake inhibitor commonly cited in the research literature for context on appetite and weight studies.",
    "sequence": "Non-peptide small molecule.",
    "overview": "Tesofensine is an investigational small-molecule compound that inhibits reuptake of noradrenaline, dopamine, and serotonin. It was originally explored for neurodegenerative conditions and later studied for effects on appetite and body weight. It is not an approved therapy in major markets.",
    "mechanism": [
      "Inhibits reuptake of noradrenaline, dopamine, and serotonin.",
      "Raises synaptic monoamine levels, associated with reduced appetite.",
      "Central effects thought to influence energy balance and satiety.",
      "Acts as an oral small molecule rather than an injectable peptide."
    ],
    "researchedUses": [
      "Weight and body-composition research",
      "Appetite-regulation studies",
      "Earlier neurological investigations (historical)",
      "Metabolic-endpoint clinical trials"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly cited around 0.25-0.5 mg per day in trials",
      "frequency": "Once daily",
      "notes": "Taken by mouth; no injection involved."
    },
    "halfLife": "Long - reported on the order of several days.",
    "reconstitution": "Taken orally as a small molecule, so injection reconstitution and syringe unit math do not apply.",
    "cautions": [
      "Investigational; not FDA-approved.",
      "Reported effects on heart rate, blood pressure, and mood in trials.",
      "Monoamine activity carries interaction and stimulation concerns.",
      "Provided for education only, not as medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "larazotide",
    "name": "Larazotide",
    "aka": [
      "Larazotide acetate",
      "AT-1001"
    ],
    "category": "Gut & anti-inflammatory",
    "tags": [
      "gut",
      "barrier",
      "celiac",
      "tight-junction"
    ],
    "summary": "An orally administered, locally acting gut-barrier peptide commonly cited in the research literature for context on intestinal permeability.",
    "sequence": "Gly-Gly-Val-Leu-Val-Gln-Pro-Gly",
    "overview": "Larazotide is a synthetic octapeptide studied as a tight-junction regulator that acts within the gut lumen and is not meaningfully absorbed systemically. Much of its attention comes from celiac-disease research aimed at reducing intestinal permeability.",
    "mechanism": [
      "Appears to promote assembly of intestinal tight junctions.",
      "May reduce paracellular permeability of the gut lining.",
      "Acts locally in the intestinal lumen with minimal systemic absorption.",
      "Thought to blunt certain gluten-triggered barrier disruption."
    ],
    "researchedUses": [
      "Celiac-disease adjunct research",
      "Intestinal-permeability (leaky gut) studies",
      "Gut-barrier and inflammation models",
      "Mucosal-integrity investigations"
    ],
    "protocol": {
      "route": "Oral",
      "dose": "Commonly cited around 0.5 mg (500 mcg) before meals in trials",
      "frequency": "Typically three times daily with meals",
      "notes": "Designed to act in the gut; taken by mouth."
    },
    "halfLife": "Not applicable in the usual systemic sense - it acts locally and is minimally absorbed.",
    "reconstitution": "Administered orally and acts within the gut lumen, so injection reconstitution and syringe unit math do not apply.",
    "cautions": [
      "Investigational; not FDA-approved.",
      "Efficacy data remain mixed in later trials.",
      "Intended to act locally, not as a systemic agent.",
      "Educational context only, not a treatment recommendation."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "pentosan-polysulfate",
    "name": "Pentosan Polysulfate (PPS)",
    "aka": [
      "Pentosan polysulfate sodium",
      "PPS",
      "Elmiron"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "joints",
      "cartilage",
      "bladder",
      "polysaccharide"
    ],
    "summary": "A semi-synthetic sulfated polysaccharide commonly cited in the research literature for context on joint and bladder-lining research.",
    "sequence": "Semi-synthetic sulfated polysaccharide derived from beech-wood xylan.",
    "overview": "Pentosan polysulfate is a heparin-like semi-synthetic polysaccharide. It is approved in some regions for interstitial cystitis and is studied for osteoarthritis and cartilage-related endpoints. It is not a peptide despite frequent grouping with tissue-repair compounds.",
    "mechanism": [
      "Heparin-like molecule with anti-inflammatory and anticoagulant properties.",
      "May help restore the protective glycosaminoglycan layer of the bladder.",
      "Studied for effects on cartilage matrix and joint tissue.",
      "Reported influence on certain enzymes involved in tissue breakdown."
    ],
    "researchedUses": [
      "Interstitial cystitis / bladder-pain research",
      "Osteoarthritis and joint-repair studies",
      "Cartilage-matrix investigations",
      "Anti-inflammatory and microcirculation models"
    ],
    "protocol": {
      "route": "Oral (bladder indication) or injection in some veterinary/research contexts",
      "dose": "Commonly cited around 100 mg three times daily orally in clinical use",
      "frequency": "Two to three times daily depending on route",
      "notes": "Route and dose depend heavily on the indication studied."
    },
    "halfLife": "Variable; oral bioavailability is low and elimination is prolonged in tissues.",
    "reconstitution": "Most often taken orally as a capsule, so injection reconstitution and syringe unit math generally do not apply. Injectable research forms follow their own labeling.",
    "cautions": [
      "Prescription status varies by region; not a general supplement.",
      "Associated in the literature with bleeding risk and reports of retinal changes with long-term oral use.",
      "A polysaccharide, not a peptide.",
      "Educational information only, not medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "palmitoyl-tetrapeptide-7",
    "name": "Palmitoyl Tetrapeptide-7",
    "aka": [
      "Rigin",
      "Pal-GQPR"
    ],
    "category": "Skin & cosmetic",
    "tags": [
      "cosmetic",
      "topical",
      "anti-inflammatory",
      "skin"
    ],
    "summary": "A palmitoylated cosmetic peptide commonly cited in the skincare literature for context on soothing and anti-aging formulations.",
    "sequence": "Palmitoyl-Gly-Gln-Pro-Arg",
    "overview": "Palmitoyl Tetrapeptide-7 pairs the GQPR peptide with a palmitic-acid tail to aid skin penetration in topical products. It is used in cosmetics and is often marketed for reducing the appearance of inflammation-related aging, based largely on in vitro and formulation data.",
    "mechanism": [
      "The palmitoyl tail improves lipid-layer penetration of the peptide.",
      "Reported to modulate interleukin signaling associated with skin inflammation.",
      "May support extracellular-matrix maintenance in skin models.",
      "Frequently combined with Palmitoyl Tripeptide-1 in blends."
    ],
    "researchedUses": [
      "Cosmetic anti-aging formulations",
      "Soothing / anti-redness skincare",
      "Skin-firmness appearance studies",
      "Combination cosmetic peptide products"
    ],
    "protocol": {
      "route": "Topical",
      "dose": "Typically formulated at very low percentages (parts-per-million range) in creams and serums",
      "frequency": "Applied once or twice daily as a cosmetic",
      "notes": "Delivered as a leave-on topical, not injected."
    },
    "halfLife": "Not typically characterized; used topically rather than systemically.",
    "reconstitution": "Supplied within finished topical products or as a cosmetic raw material, so injection reconstitution and syringe unit math do not apply.",
    "cautions": [
      "A cosmetic ingredient, not an approved drug.",
      "Evidence is largely in vitro and formulation-based.",
      "Not intended for injection.",
      "Educational context only, not a usage recommendation."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "matrixyl-3000",
    "name": "Matrixyl 3000",
    "aka": [
      "Palmitoyl Tripeptide-1 + Palmitoyl Tetrapeptide-7 blend"
    ],
    "category": "Skin & cosmetic",
    "tags": [
      "cosmetic",
      "topical",
      "matrikine",
      "anti-aging"
    ],
    "summary": "A topical cosmetic peptide blend commonly cited in the skincare literature for context on wrinkle-appearance formulations.",
    "sequence": "Cosmetic blend of Palmitoyl Tripeptide-1 (Pal-GHK) and Palmitoyl Tetrapeptide-7 (Pal-GQPR).",
    "overview": "Matrixyl 3000 is a trademarked cosmetic complex combining two matrikine-mimetic palmitoylated peptides. It is marketed for the appearance of fine lines and skin firmness, with supporting data drawn mainly from manufacturer studies and skin-model work.",
    "mechanism": [
      "Combines two matrikine-mimetic peptides that signal skin-repair pathways.",
      "Reported to encourage extracellular-matrix components such as collagen in skin models.",
      "Palmitoyl tails aid topical penetration.",
      "The two peptides are described as acting synergistically in the blend."
    ],
    "researchedUses": [
      "Cosmetic wrinkle-appearance products",
      "Skin-firmness and elasticity formulations",
      "Anti-aging serums and creams",
      "Combination peptide skincare research"
    ],
    "protocol": {
      "route": "Topical",
      "dose": "Commonly used around 3-10% of the finished cosmetic formula (the raw blend is heavily diluted)",
      "frequency": "Applied once or twice daily as a cosmetic",
      "notes": "A leave-on topical complex, not an injectable."
    },
    "halfLife": "Not typically characterized; applied topically rather than dosed systemically.",
    "reconstitution": "Sold as a cosmetic raw material or within finished skincare, so injection reconstitution and syringe unit math do not apply.",
    "cautions": [
      "A cosmetic complex, not an approved drug.",
      "Much of the supporting evidence is manufacturer-generated.",
      "Not intended for injection.",
      "Provided for education only, not as a recommendation."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "bacteriostatic-water",
    "name": "Bacteriostatic Water",
    "aka": [
      "Bacteriostatic water for injection",
      "BAC water"
    ],
    "category": "Solvents & reconstitution",
    "tags": [
      "diluent",
      "solvent",
      "benzyl-alcohol",
      "reconstitution"
    ],
    "summary": "A preserved sterile water used as a diluent to reconstitute lyophilized peptides; it is a solvent, not a therapeutic.",
    "sequence": "Sterile water containing 0.9% benzyl alcohol as a preservative.",
    "overview": "Bacteriostatic water is sterile water for injection with 0.9% benzyl alcohol added as a bacteriostatic preservative. The preservative lets a vial be entered multiple times over a limited period without supporting bacterial growth, which is why it is commonly chosen for reconstituting multi-use peptide vials.",
    "mechanism": [
      "Serves as the liquid vehicle that dissolves a lyophilized powder.",
      "The 0.9% benzyl alcohol inhibits bacterial growth in an entered vial.",
      "Allows repeated withdrawals from a multi-dose vial within its in-use window.",
      "Has no pharmacological action of its own on the target condition."
    ],
    "researchedUses": [
      "Reconstituting lyophilized peptides and other powders",
      "Preparing multi-use injectable vials",
      "General diluent for compatible medications",
      "Laboratory and research dilution"
    ],
    "protocol": {
      "route": "Diluent (mixed into a powder vial)",
      "dose": "No therapeutic dose - volume chosen to reach a desired concentration",
      "frequency": "Not applicable",
      "notes": "Often avoided in neonates and in large volumes due to benzyl alcohol content."
    },
    "halfLife": "Not applicable - it is a solvent, not an active compound.",
    "reconstitution": "This item is itself the diluent. The volume added to a powder sets the final concentration (for example, 2 mL into a 5 mg vial yields 2.5 mg/mL). Its benzyl alcohol preservative makes it suited to multi-use vials.",
    "cautions": [
      "A medical solvent, not a therapeutic agent.",
      "Benzyl alcohol can be unsuitable for neonates and for very large cumulative volumes.",
      "Maintain aseptic technique and observe in-use dating.",
      "Educational information only, not medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "sterile-water",
    "name": "Sterile Water for Injection",
    "aka": [
      "Sterile water",
      "Water for injection",
      "WFI"
    ],
    "category": "Solvents & reconstitution",
    "tags": [
      "diluent",
      "solvent",
      "preservative-free",
      "single-use"
    ],
    "summary": "A preservative-free sterile water used as a single-use diluent; it is a solvent, not a therapeutic.",
    "sequence": "Sterile water for injection with no added preservative.",
    "overview": "Sterile water for injection is purified, sterilized water containing no antimicrobial preservative. Because it has nothing to inhibit bacterial growth, it is intended for single use and is often chosen where benzyl alcohol must be avoided.",
    "mechanism": [
      "Serves as the liquid vehicle that dissolves a lyophilized powder.",
      "Contains no preservative, so an opened vial is meant for immediate single use.",
      "Used where preservatives such as benzyl alcohol are contraindicated.",
      "Has no pharmacological action of its own on the target condition."
    ],
    "researchedUses": [
      "Single-use reconstitution of injectable powders",
      "Preparations where preservative-free diluent is required",
      "Neonatal and preservative-sensitive contexts",
      "General laboratory dilution"
    ],
    "protocol": {
      "route": "Diluent (mixed into a powder vial)",
      "dose": "No therapeutic dose - volume chosen to reach a desired concentration",
      "frequency": "Not applicable",
      "notes": "Being preservative-free, discard leftover after a single use."
    },
    "halfLife": "Not applicable - it is a solvent, not an active compound.",
    "reconstitution": "This item is itself the diluent. The added volume sets the final concentration of the dissolved powder. Because it lacks a preservative, it is intended for single use rather than repeated multi-dose entry.",
    "cautions": [
      "A medical solvent, not a therapeutic agent.",
      "No preservative, so any remainder should be discarded after single use.",
      "Not for direct injection undiluted in ways that are hypotonic; used to reconstitute or dilute.",
      "Educational information only, not medical advice."
    ],
    "calcPreset": null,
    "type": "amino"
  },
  {
    "slug": "somatostatin",
    "name": "Somatostatin",
    "aka": [
      "Growth-hormone-inhibiting hormone",
      "GHIH",
      "SST"
    ],
    "category": "Growth-hormone axis",
    "tags": [
      "hormone",
      "inhibitory",
      "infusion",
      "endocrine"
    ],
    "summary": "A natural inhibitory hormone commonly cited in the research literature for context on the regulation of growth hormone and other secretions.",
    "sequence": "Ala-Gly-Cys-Lys-Asn-Phe-Phe-Trp-Lys-Thr-Phe-Thr-Ser-Cys (SST-14)",
    "overview": "Somatostatin is an endogenous regulatory hormone that broadly inhibits secretion, including growth hormone from the pituitary and various gastrointestinal and pancreatic hormones. Its native form has a very short half-life, so clinical use is typically by continuous infusion, and longer-acting analogs are used for practical dosing.",
    "mechanism": [
      "Binds somatostatin receptors to suppress hormone secretion.",
      "Inhibits pituitary release of growth hormone (and TSH).",
      "Reduces secretion of several gut and pancreatic hormones, including insulin and glucagon.",
      "Acts as the counter-regulatory brake to growth-hormone-releasing signals."
    ],
    "researchedUses": [
      "Management research on acute variceal and GI bleeding",
      "Studies of hormone-secreting tumors and fistulas",
      "Endocrine regulation of the growth-hormone axis",
      "Gastrointestinal secretion research"
    ],
    "protocol": {
      "route": "Continuous intravenous infusion (clinical)",
      "dose": "Commonly cited around a 250 mcg bolus followed by ~250 mcg/hour infusion in clinical settings",
      "frequency": "Continuous, due to its very short half-life",
      "notes": "Native peptide is rarely self-administered; analogs are used for longer action."
    },
    "halfLife": "Very short - on the order of a few minutes.",
    "reconstitution": "Used clinically as a continuous intravenous infusion prepared and titrated by professionals, so simple subcutaneous syringe unit math does not apply.",
    "cautions": [
      "A clinical infusion agent, not a general research self-administration compound.",
      "Broad inhibitory effects can disturb glucose and other hormone levels.",
      "Very short half-life makes single injections impractical.",
      "Educational context only, not medical advice."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "endoluten",
    "name": "Endoluten",
    "aka": [
      "Pineal peptide bioregulator",
      "Endoluten AA"
    ],
    "category": "Longevity",
    "tags": [
      "bioregulator",
      "pineal",
      "longevity",
      "peptide-extract"
    ],
    "summary": "A peptide extract derived from pineal tissue, studied in Russian bioregulator research for aging-related and neuroendocrine markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Endoluten is presented in the Khavinson bioregulator literature as a peptide preparation obtained from the pineal gland. It is the most cited of the pineal-derived bioregulators and is discussed largely in the context of aging, neuroendocrine rhythm, and general longevity interest. Nearly all of the supporting work originates from a small set of Russian and Eastern-European laboratories, is older, and has not been broadly reproduced in mainstream Western literature, so any claims about it should be read with caution.",
    "mechanism": [
      "Proposed to act as a tissue-specific 'peptide bioregulator' influencing gene expression in pineal and related cells.",
      "Discussed in relation to melatonin-linked circadian and neuroendocrine signaling.",
      "The proposed mechanisms are not well established outside the originating research group and lack independent confirmation."
    ],
    "researchedUses": [
      "General 'anti-aging' and longevity research interest",
      "Neuroendocrine and circadian-rhythm markers in aging models",
      "Cited in discussions of pineal function and age-related decline"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Typically described as repeated in cycles rather than continuously in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; considered a research compound, not an approved therapy.",
      "The evidence base is limited, older, and largely non-Western, and has not been widely replicated.",
      "Purity and true content of research-grade or extract-based material vary considerably by source.",
      "Not a substitute for evaluation or care from a qualified clinician."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "cerluten",
    "name": "Cerluten",
    "aka": [
      "Brain peptide bioregulator",
      "Cerluten AA"
    ],
    "category": "Cognitive & neuroprotective",
    "tags": [
      "bioregulator",
      "brain",
      "neuroprotective",
      "cns"
    ],
    "summary": "A brain-derived peptide bioregulator studied in Russian research for CNS and cognitive-related markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Cerluten is described in the bioregulator literature as a peptide complex obtained from brain (central nervous system) tissue. It is discussed mainly in the context of neuroprotection, recovery of nervous-tissue function, and cognitive markers in aging. As with the other bioregulators, the supporting studies come almost entirely from the originating Russian research program, are typically small and dated, and have not been broadly validated in independent Western trials.",
    "mechanism": [
      "Proposed to act as a CNS-specific peptide bioregulator influencing gene expression in neural tissue.",
      "Discussed in relation to neuronal maintenance and repair processes.",
      "Mechanistic claims rest largely on in-house animal and cell work and are not well established elsewhere."
    ],
    "researchedUses": [
      "Neuroprotection interest in aging models",
      "Cognitive and nervous-system recovery discussions",
      "Cited alongside other 'brain' peptides in longevity contexts"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Often described as used in repeated cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound rather than an approved neurological treatment.",
      "Human evidence is limited, older, and largely non-Western, and has not been widely replicated.",
      "Research-grade or extract material varies in purity and actual peptide content by source.",
      "Neurological symptoms warrant evaluation by a qualified clinician, not self-directed peptide use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "vladonix",
    "name": "Vladonix",
    "aka": [
      "Thymus peptide bioregulator",
      "Vladonix AA"
    ],
    "category": "Immune support",
    "tags": [
      "bioregulator",
      "thymus",
      "immune",
      "longevity"
    ],
    "summary": "A thymus-derived peptide bioregulator studied in Russian research for immune-related markers in aging.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Vladonix is presented as a peptide preparation derived from thymus tissue, positioned within the bioregulator framework as an immune-oriented compound. It is discussed for its proposed effects on immune-cell activity and immune resilience during aging, a role broadly parallel to earlier thymic peptide work. The evidence remains concentrated in the originating research group, is largely older and non-Western, and has not been widely reproduced in mainstream immunology literature.",
    "mechanism": [
      "Proposed to act as a thymus-specific peptide bioregulator influencing gene expression in immune tissue.",
      "Studied for effects on T-cell and broader immune-cell activity.",
      "Mechanisms overlap with claims made for other thymic peptides but are not independently well established."
    ],
    "researchedUses": [
      "Immune support in aging models",
      "General immune-resilience research interest",
      "Cited in longevity-peptide and thymus-function discussions"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Typically described in cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved immune therapy.",
      "The evidence base is limited, older, and largely non-Western, and has not been widely replicated.",
      "Purity of research-grade or tissue-extract material varies by source.",
      "Immune conditions should be managed with a qualified clinician rather than self-directed peptide use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "testagen",
    "name": "Testagen",
    "aka": [
      "Testis peptide bioregulator",
      "Testes bioregulator"
    ],
    "category": "Hormonal & reproductive",
    "tags": [
      "bioregulator",
      "testis",
      "reproductive",
      "hormonal"
    ],
    "summary": "A testis-derived peptide bioregulator studied in Russian research for reproductive-tissue markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Testagen is described in the bioregulator literature as a peptide complex obtained from testicular tissue, and is discussed in the context of male reproductive-tissue function and age-related decline. Some sources also refer to a short synthetic analog associated with the program. As with the wider bioregulator catalogue, the supporting data are largely in-house, older, and non-Western, and independent replication in mainstream endocrinology is lacking, so claims warrant skepticism.",
    "mechanism": [
      "Proposed to act as a testis-specific peptide bioregulator influencing gene expression in reproductive tissue.",
      "Discussed in relation to maintenance of testicular cell function with age.",
      "Endocrine effects are asserted mainly within the originating research and are not well established externally."
    ],
    "researchedUses": [
      "Male reproductive-tissue research interest",
      "Age-related reproductive decline discussions",
      "Cited alongside other organ-specific bioregulators"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Described as used in repeated cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved hormonal or fertility treatment.",
      "Human evidence is limited, older, and largely non-Western, and has not been widely replicated.",
      "Research-grade material varies in purity and true content by source.",
      "Hormonal and reproductive concerns should be assessed by a qualified clinician."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "libidon",
    "name": "Libidon",
    "aka": [
      "Prostamax",
      "Prostate peptide bioregulator"
    ],
    "category": "Hormonal & reproductive",
    "tags": [
      "bioregulator",
      "prostate",
      "reproductive",
      "urological"
    ],
    "summary": "A prostate-derived peptide bioregulator (also marketed as Prostamax) studied in Russian research for prostate-tissue markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Libidon, also marketed under the name Prostamax, is described in the bioregulator literature as a peptide preparation obtained from prostate tissue. It is discussed in relation to prostate function and age-related urological changes in men. Its lineage overlaps conceptually with earlier prostate peptide work, but the specific supporting data remain concentrated in the originating Russian program, are older and non-Western, and have not been broadly replicated in mainstream urology.",
    "mechanism": [
      "Proposed to act as a prostate-specific peptide bioregulator influencing gene expression in prostatic tissue.",
      "Discussed in relation to maintenance of normal prostate cell function with age.",
      "Claimed effects rest largely on in-house studies and are not independently well established."
    ],
    "researchedUses": [
      "Prostate-tissue and urological research interest",
      "Age-related prostate-function discussions",
      "Cited alongside other organ-specific bioregulators"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Typically described in cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved treatment for prostate conditions.",
      "The evidence base is limited, older, and largely non-Western, and has not been widely replicated.",
      "Purity of research-grade or extract material varies by source.",
      "Prostate and urinary symptoms should be evaluated by a qualified clinician rather than self-managed with peptides."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "chonluten",
    "name": "Chonluten",
    "aka": [
      "Bronchial peptide bioregulator",
      "Lung bioregulator"
    ],
    "category": "Immune & respiratory",
    "tags": [
      "bioregulator",
      "bronchial",
      "respiratory",
      "immune"
    ],
    "summary": "A bronchial/lung-derived peptide bioregulator studied in Russian research for respiratory-tissue markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Chonluten is presented in the bioregulator literature as a peptide preparation derived from bronchial and lung tissue. It is discussed in the context of respiratory-tissue function and resilience, sometimes framed alongside immune and stress-response themes. A short synthetic tripeptide (Glu-Asp-Gly, sometimes labeled bronchogen) is associated with this line in some sources. The supporting evidence is largely in-house, older, and non-Western, and has not been widely reproduced in mainstream respiratory literature.",
    "mechanism": [
      "Proposed to act as a bronchial/lung-specific peptide bioregulator influencing gene expression in respiratory tissue.",
      "Discussed in relation to maintenance and repair of bronchial epithelium.",
      "Mechanistic claims derive mainly from the originating research group and are not well established elsewhere."
    ],
    "researchedUses": [
      "Respiratory-tissue research interest",
      "General resilience and recovery discussions in aging models",
      "Cited alongside other organ-specific bioregulators"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Described as used in repeated cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved respiratory therapy.",
      "Human evidence is limited, older, and largely non-Western, and has not been widely replicated.",
      "Research-grade material varies in purity and actual content by source.",
      "Respiratory symptoms warrant evaluation by a qualified clinician, not self-directed peptide use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "visoluten",
    "name": "Visoluten",
    "aka": [
      "Retina peptide bioregulator",
      "Eye bioregulator"
    ],
    "category": "Longevity",
    "tags": [
      "bioregulator",
      "retina",
      "ophthalmic",
      "longevity"
    ],
    "summary": "A retina/eye-derived peptide bioregulator studied in Russian research for ocular-tissue markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Visoluten is described in the bioregulator literature as a peptide preparation obtained from retinal and eye tissue. It is discussed in relation to age-related changes in ocular tissue and is often grouped with the pineal- and vision-adjacent bioregulators in longevity contexts. A short synthetic retina-associated peptide is referenced by some sources. The supporting data are largely from the originating Russian program, are older and non-Western, and remain outside mainstream ophthalmology's replicated evidence.",
    "mechanism": [
      "Proposed to act as a retina-specific peptide bioregulator influencing gene expression in ocular tissue.",
      "Discussed in relation to maintenance of retinal cell function with age.",
      "Claimed effects rest largely on in-house work and are not independently well established."
    ],
    "researchedUses": [
      "Ocular-tissue and age-related vision research interest",
      "General longevity and tissue-maintenance discussions",
      "Cited alongside other organ-specific bioregulators"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Typically described in cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved treatment for eye conditions.",
      "The evidence base is limited, older, and largely non-Western, and has not been widely replicated.",
      "Purity of research-grade or extract material varies by source.",
      "Vision changes should be assessed by a qualified eye-care professional rather than self-managed with peptides."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "sigumir",
    "name": "Sigumir",
    "aka": [
      "Cartilage peptide bioregulator",
      "Bone-joint bioregulator"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "bioregulator",
      "cartilage",
      "joint",
      "recovery"
    ],
    "summary": "A cartilage/bone-derived peptide bioregulator studied in Russian research for joint- and bone-tissue markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Sigumir is presented in the bioregulator literature as a peptide preparation derived from cartilage and bone tissue, discussed in the context of joint maintenance and age-related musculoskeletal changes. It is often grouped with recovery-oriented bioregulators. As with the rest of the catalogue, the supporting research is concentrated in the originating Russian group, is older and non-Western, and has not been broadly validated in independent orthopedic or rheumatology literature.",
    "mechanism": [
      "Proposed to act as a cartilage/bone-specific peptide bioregulator influencing gene expression in connective tissue.",
      "Discussed in relation to maintenance of chondrocyte and bone-cell function with age.",
      "Mechanistic claims derive mainly from in-house studies and are not well established externally."
    ],
    "researchedUses": [
      "Joint- and cartilage-tissue research interest",
      "Age-related musculoskeletal maintenance discussions",
      "Cited alongside other organ-specific bioregulators"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Described as used in repeated cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved treatment for joint or bone conditions.",
      "Human evidence is limited, older, and largely non-Western, and has not been widely replicated.",
      "Research-grade material varies in purity and true content by source.",
      "Joint and bone problems should be evaluated by a qualified clinician rather than self-directed peptide use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "crystagen",
    "name": "Crystagen",
    "aka": [
      "Immune peptide bioregulator",
      "Immune-system bioregulator"
    ],
    "category": "Immune support",
    "tags": [
      "bioregulator",
      "immune",
      "peptide",
      "longevity"
    ],
    "summary": "An immune-system peptide bioregulator studied in Russian research for immune-related markers in aging.",
    "sequence": "Short synthetic peptide bioregulator",
    "overview": "Crystagen is described in the bioregulator literature as a short synthetic immune-oriented peptide, positioned as a general immune-system regulator within the Khavinson framework. It is discussed for proposed effects on immune-cell activity and immune balance during aging, overlapping conceptually with the thymic bioregulators. The supporting evidence is largely in-house, older, and non-Western, and independent replication in mainstream immunology is lacking.",
    "mechanism": [
      "Proposed to act as an immune-oriented peptide bioregulator influencing gene expression in immune cells.",
      "Studied for effects on immune-cell activity and cytokine-related markers.",
      "Mechanistic claims rest largely on the originating group's work and are not independently well established."
    ],
    "researchedUses": [
      "Immune support in aging models",
      "General immune-balance research interest",
      "Cited in longevity-peptide and immune-function discussions"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Typically described in cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved immune therapy.",
      "The evidence base is limited, older, and largely non-Western, and has not been widely replicated.",
      "Purity of research-grade material varies by source.",
      "Immune conditions should be managed with a qualified clinician rather than self-directed peptide use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "suprefort",
    "name": "Suprefort",
    "aka": [
      "Pancreas peptide bioregulator",
      "Pancreatic bioregulator"
    ],
    "category": "Metabolic & weight",
    "tags": [
      "bioregulator",
      "pancreas",
      "metabolic",
      "longevity"
    ],
    "summary": "A pancreas-derived peptide bioregulator studied in Russian research for metabolic and pancreatic-tissue markers.",
    "sequence": "Tissue-specific peptide fraction",
    "overview": "Suprefort is presented in the bioregulator literature as a peptide preparation obtained from pancreatic tissue, discussed in the context of carbohydrate metabolism and age-related metabolic changes. It is often grouped with the metabolic-oriented bioregulators. As with the wider catalogue, its supporting data come chiefly from the originating Russian program, are older and non-Western, and have not been broadly reproduced in mainstream endocrinology or metabolic research.",
    "mechanism": [
      "Proposed to act as a pancreas-specific peptide bioregulator influencing gene expression in pancreatic tissue.",
      "Discussed in relation to maintenance of pancreatic cell function and glucose-related markers.",
      "Metabolic claims derive mainly from in-house studies and are not well established externally."
    ],
    "researchedUses": [
      "Metabolic and pancreatic-tissue research interest",
      "Age-related metabolic-maintenance discussions",
      "Cited alongside other organ-specific bioregulators"
    ],
    "protocol": {
      "route": "Subcutaneous or intramuscular injection (research); oral capsule forms also marketed",
      "dose": "Commonly cited around 1-3 mg per short course",
      "frequency": "Short cyclical courses in the discussed protocols",
      "notes": "Described as used in repeated cycles in the source literature."
    },
    "halfLife": "Not well established.",
    "reconstitution": "Commonly a 5-10 mg vial; reconstitute with bacteriostatic water and use the calculator for units.",
    "cautions": [
      "Not FDA-approved; a research compound, not an approved treatment for metabolic or blood-sugar conditions.",
      "Human evidence is limited, older, and largely non-Western, and has not been widely replicated.",
      "Research-grade or extract material varies in purity and true content by source.",
      "Metabolic and blood-sugar concerns should be managed with a qualified clinician rather than self-directed peptide use."
    ],
    "calcPreset": {
      "vialMg": 10,
      "waterMl": 2,
      "dose": 2,
      "doseUnit": "mg"
    },
    "type": "peptide"
  },
  {
    "slug": "midnight-blend",
    "noindex": true,
    "name": "Midnight Blend",
    "aka": [
      "Midnight",
      "Nighttime GH blend"
    ],
    "category": "Sleep & stress",
    "tags": [
      "blend",
      "growth hormone",
      "sleep",
      "recovery"
    ],
    "summary": "A nighttime recovery blend, typically pairing a GH-secretagogue combo (CJC-1295 + Ipamorelin) with DSIP for sleep.",
    "sequence": "Blend — not a single peptide; composition varies by supplier.",
    "overview": "Midnight Blend is a marketing name for a before-bed recovery injection. The most common make-up pairs the growth-hormone secretagogues CJC-1295 (no-DAC) and Ipamorelin — which drive a GH pulse that naturally peaks during deep sleep — with DSIP (delta sleep-inducing peptide) for its sleep associations. Exact components and ratios vary by supplier, and the blend is not an approved medicine.",
    "mechanism": [
      "CJC-1295 and Ipamorelin together prompt a growth-hormone pulse through two different receptors.",
      "Growth-hormone release naturally peaks in early deep sleep, the rationale for nighttime dosing.",
      "DSIP is loosely studied for sleep regulation, though human evidence is weak."
    ],
    "researchedUses": [
      "Nighttime growth-hormone support",
      "Sleep and recovery interest",
      "Convenience of a single pre-bed injection"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research), typically before bed",
      "dose": "By the vial's per-component content",
      "frequency": "Once nightly in discussed protocols",
      "notes": "Taken away from food; a fixed blend locks the ratio of each component."
    },
    "halfLife": "Short for each component.",
    "reconstitution": "Multi-peptide vial — reconstitute and use the blend calculator to find units per component.",
    "cautions": [
      "No component is FDA-approved.",
      "DSIP sleep evidence in humans is limited.",
      "Blend content and purity vary by source."
    ],
    "calcPreset": null,
    "type": "peptide"
  },
  {
    "slug": "hercules",
    "noindex": true,
    "name": "Hercules",
    "aka": [
      "Hercules blend"
    ],
    "category": "Recovery & tissue repair",
    "tags": [
      "blend",
      "healing",
      "performance",
      "recovery"
    ],
    "summary": "A performance-and-recovery blend, most often combining BPC-157, TB-500 and GHK-Cu for tissue repair.",
    "sequence": "Blend — not a single peptide; composition varies by supplier.",
    "overview": "Hercules is a supplier brand name for a recovery and performance blend. It is most commonly built on the BPC-157 / TB-500 repair pair plus GHK-Cu for connective-tissue and skin support, though some vendors add a growth-hormone secretagogue. Because the formula is not standardized, always check the specific product label. It is not an approved medicine.",
    "mechanism": [
      "BPC-157 and TB-500 are studied for complementary local and systemic tissue repair.",
      "GHK-Cu supports collagen and connective-tissue remodeling.",
      "Any real effect depends on the actual components and their amounts."
    ],
    "researchedUses": [
      "General recovery and tissue repair",
      "Connective-tissue and skin support",
      "Convenience of a combined vial"
    ],
    "protocol": {
      "route": "Subcutaneous injection (research)",
      "dose": "By the vial's per-component content",
      "frequency": "Often daily in discussed protocols",
      "notes": "Confirm the exact composition on the specific product."
    },
    "halfLife": "Varies by component; generally short.",
    "reconstitution": "Multi-peptide vial — reconstitute and use the blend calculator to find units per component.",
    "cautions": [
      "Composition is not standardized across suppliers.",
      "No component is FDA-approved.",
      "Human data on the combination are absent."
    ],
    "calcPreset": null,
    "type": "peptide"
  }
]

// Default every profile to type 'peptide' unless it says otherwise (e.g. B12).
export const PEPTIDES = RAW.map((p) => ({ type: 'peptide', ...p }))

export const getPeptide = (slug) => PEPTIDES.find((p) => p.slug === slug)
